/* Fab product gallery
   Lightweight, zero-dependency replacement for the GG Product Gallery / pmslider app.

   Behaviour
   ---------
   - Carousel: swipe / drag / arrows / dots / thumbs / arrow keys to navigate.
   - In-place zoom on images (Ruggable-style):
       Desktop: click image to toggle zoom 1x <-> 2.5x, anchored to click point.
                When zoomed, cursor becomes 'zoom-out' and dragging pans.
                Click again (or press Esc) to exit zoom.
       Mobile:  tap image to toggle zoom 1x <-> 2.5x, anchored to tap point.
                When zoomed, dragging pans, pinch refines (1x..4x).
                Tap again to exit zoom.
   - Hosted videos: autoplay muted on the active slide, pause when you swipe
     away, custom tap-to-toggle play/pause overlay (no native controls bar so
     swipes work cleanly anywhere on the video surface).
   - External videos (YouTube/Vimeo): poster + play button; click injects an
     iframe; navigating away resets the iframe back to the poster.
   - Variant -> media linkage: bubbled `variant:change` events map variant.id
     to media.id via the JSON island shipped with the markup.

   Public element: <fab-gallery>
     - id="MediaGallery-{section_id}" so existing product-info.js
       setActiveMedia(mediaId) lookups still work.

   Public methods:
     - setActiveMedia(mediaId)  -> slides to the matching media. Exits zoom.
     - goTo(index)              -> slides to a specific index. Exits zoom.
     - upsertGeneratedSlides(spec) -> insert/update/remove draft-preview slides.
*/

(function () {
  if (window.customElements && customElements.get('fab-gallery')) return;

  const SWIPE_LOCK_THRESHOLD = 8;       // px before we decide horizontal vs vertical intent
  const SWIPE_DISTANCE_RATIO = 0.15;    // % of viewport width to commit to a slide change
  const SWIPE_DISTANCE_MAX = 80;        // px cap for the commit threshold
  const TAP_MAX_MOVE_MOUSE = 14;        // Chrome mouse-click drift tolerance
  const ZOOM_SCALE = 2.5;               // Ruggable-ish target zoom level
  const MAX_PINCH_SCALE = 4;            // upper bound for two-finger pinch
  const PINCH_RESET_BELOW = 1.05;       // pinch ends below this -> snap back to 1x
  const PREVIEW_WAITING_ID = 'fab-preview-waiting';
  const PREVIEW_ROOM_ID = 'fab-preview-room';
  const PREVIEW_CLOSE_ID = 'fab-preview-close';
  const PREVIEW_IDS = [PREVIEW_WAITING_ID, PREVIEW_ROOM_ID, PREVIEW_CLOSE_ID];
  const IMAGE_WIDTHS = [360, 540, 720, 900, 1080, 1296, 1500];
  const ZOOM_WIDTHS = [1200, 1800, 2400];

  function imageUrlAtWidth(baseUrl, width) {
    if (!baseUrl) return '';
    try {
      const protocolRelative = baseUrl.indexOf('//') === 0;
      const url = new URL(baseUrl, window.location.origin);
      url.searchParams.set('width', String(width));
      return protocolRelative ? url.href.replace(/^https?:/, '') : url.href;
    } catch (_) {
      const separator = baseUrl.indexOf('?') === -1 ? '?' : '&';
      return baseUrl + separator + 'width=' + width;
    }
  }

  function imageSrcset(baseUrl, widths) {
    return widths.map((width) => imageUrlAtWidth(baseUrl, width) + ' ' + width + 'w').join(', ');
  }

  class FabGallery extends HTMLElement {
    constructor() {
      super();
      this.activeIndex = 0;
      this.variantMediaMap = {};

      // Pointer / gesture state
      this.pointers = new Map();        // pointerId -> { x, y, startX, startY, downTarget, pointerType }
      this.mode = null;                 // 'swipe' | 'pan' | 'pinch' | null
      this.swipeStart = null;
      this.panStart = null;
      this.pinchStart = null;
      this.realSwipeCommitted = false;  // suppresses the trailing click after a swipe
      this.panMoved = false;            // suppresses the trailing click after a real pan
      this.tapHandledByPointerup = false; // pointerup already toggled zoom — click should bail

      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onPointerCancel = this.onPointerUp.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onWindowKeyDown = this.onWindowKeyDown.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
      this.onWindowResize = this.onWindowResize.bind(this);
      this._syncPreviewMotion = this._syncPreviewMotion.bind(this);
      this._reduceMotionMq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    }

    cacheEls() {
      this.viewport = this.querySelector('[data-fab-gallery-viewport]');
      this.track = this.querySelector('[data-fab-gallery-track]');
      this.slides = Array.from(this.querySelectorAll('[data-fab-gallery-slide]'));
      this.thumbsContainer = this.querySelector('[data-fab-gallery-thumbs]');
      this.thumbs = Array.from(this.querySelectorAll('[data-fab-gallery-thumb]'));
      this.dotsContainer = this.querySelector('[data-fab-gallery-dots]');
      this.dots = Array.from(this.querySelectorAll('[data-fab-gallery-dot]'));
      this.prevBtn = this.querySelector('[data-fab-gallery-prev]');
      this.nextBtn = this.querySelector('[data-fab-gallery-next]');

      const initialActive = this.slides.findIndex((s) =>
        s.classList.contains('fab-gallery__slide--active')
      );
      this.activeIndex = initialActive >= 0 ? initialActive : 0;

      this.variantMediaMap = {};
      try {
        const mapEl = this.querySelector('[data-fab-gallery-variant-map]');
        if (mapEl && mapEl.textContent.trim()) {
          this.variantMediaMap = JSON.parse(mapEl.textContent.trim());
        }
      } catch (_) {
        this.variantMediaMap = {};
      }
    }

    connectedCallback() {
      this.cacheEls();
      this.bindEvents();
      this.update(true, 1);

      const widen = () => this.hydrateNeighbourImages(2);
      const active = this.getActiveImage();
      if (active && !(active.complete && active.naturalWidth > 0)) {
        active.addEventListener('load', widen, { once: true });
        active.addEventListener('error', widen, { once: true });
      } else if ('requestIdleCallback' in window) {
        requestIdleCallback(widen, { timeout: 2000 });
      } else {
        setTimeout(widen, 500);
      }

      // If the initial active slide is a hosted video, kick off autoplay.
      this.playActiveVideoIfAny();
    }

    disconnectedCallback() {
      this._clearTrackWillChangeNow();
      if (this._previewRevealTimer) {
        clearTimeout(this._previewRevealTimer);
        this._previewRevealTimer = 0;
      }
      this.unbindEvents();
    }

    hydrateNeighbourImages(reach = 2) {
      for (let d = -reach; d <= reach; d++) {
        const slide = this.slides[this.activeIndex + d];
        if (!slide) continue;

        slide.querySelectorAll('img[data-image-base-src]:not([src]), img[data-src]').forEach((img) => {
          img.loading = 'eager';
          if (d !== 0) img.setAttribute('fetchpriority', 'low');
          const baseUrl = img.dataset.imageBaseSrc || img.getAttribute('src');
          if (baseUrl) {
            img.srcset = imageSrcset(baseUrl, IMAGE_WIDTHS);
            img.src = imageUrlAtWidth(baseUrl, 720);
          } else {
            if (img.dataset.srcset) img.srcset = img.dataset.srcset;
            if (img.dataset.src) img.src = img.dataset.src;
          }
          img.removeAttribute('data-src');
          img.removeAttribute('data-srcset');
        });
      }
    }

    /* ---------------- will-change lifecycle ---------------- */

    _clearTrackWillChangeNow() {
      clearTimeout(this._trackWcTimer);
      this._trackWcTimer = undefined;
      if (!this.track) return;
      if (this._trackWcListener) {
        this.track.removeEventListener('transitionend', this._trackWcListener);
        this._trackWcListener = null;
      }
      this.track.style.willChange = '';
    }

    _scheduleTrackWillChangeRelease() {
      if (!this.track || this.slides.length <= 1) return;
      clearTimeout(this._trackWcTimer);
      if (this._trackWcListener) {
        this.track.removeEventListener('transitionend', this._trackWcListener);
        this._trackWcListener = null;
      }
      const track = this.track;
      const listener = (ev) => {
        if (ev.target !== track || ev.propertyName !== 'transform') return;
        this._clearTrackWillChangeNow();
      };
      track.addEventListener('transitionend', listener);
      this._trackWcListener = listener;
      this._trackWcTimer = setTimeout(() => this._clearTrackWillChangeNow(), 520);
    }

    /* ---------------- bindings ---------------- */

    bindEvents() {
      this.prevBtn?.addEventListener('click', () => this.go(-1, 'swipe'));
      this.nextBtn?.addEventListener('click', () => this.go(+1, 'swipe'));

      this.addEventListener('click', (e) => {
        const thumb = e.target.closest && e.target.closest('[data-fab-gallery-thumb]');
        if (thumb && this.contains(thumb)) {
          const idx = parseInt(thumb.dataset.mediaIndex, 10);
          if (!Number.isNaN(idx)) this.goTo(idx, 'thumb');
          return;
        }
        const dot = e.target.closest && e.target.closest('[data-fab-gallery-dot]');
        if (dot && this.contains(dot)) {
          const idx = parseInt(dot.dataset.mediaIndex, 10);
          if (!Number.isNaN(idx)) this.goTo(idx, 'thumb');
        }
      });

      // Use viewport as the gesture surface.
      this.viewport?.addEventListener('pointerdown', this.onPointerDown);
      this.viewport?.addEventListener('click', this.onClick);

      this.addEventListener('keydown', this.onKeyDown);
      window.addEventListener('keydown', this.onWindowKeyDown);
      window.addEventListener('resize', this.onWindowResize);

      // Hosted video play/pause overlays. Clicks on the overlay only fire
      // when the user actually tapped — drags are intercepted by the gesture
      // system and won't reach this.
      this.querySelectorAll('[data-fab-gallery-video-toggle]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          const slide = btn.closest('[data-fab-gallery-slide]');
          const video = slide?.querySelector('video');
          if (!video) return;
          if (video.paused) {
            const p = video.play();
            if (p && typeof p.catch === 'function') p.catch(() => {});
          } else {
            video.pause();
          }
        });
      });

      // Reflect playing state on the slide for the play-button overlay's CSS.
      this.querySelectorAll('video').forEach((video) => {
        const slide = video.closest('[data-fab-gallery-slide]');
        if (!slide) return;
        const setState = () => {
          slide.dataset.videoPlaying = video.paused ? 'false' : 'true';
        };
        video.addEventListener('play', setState);
        video.addEventListener('pause', setState);
        video.addEventListener('ended', setState);
        setState();
      });

      this.querySelectorAll('[data-fab-gallery-external-video]').forEach((wrap) => {
        wrap.addEventListener('click', (e) => {
          // Only act on a stationary click. If a swipe gesture committed, the
          // realSwipeCommitted flag will be true and we ignore the click.
          if (this.realSwipeCommitted) return;
          e.stopPropagation();
          this.playExternalVideo(wrap);
        });
      });

      this.productInfoEl = this.closest('product-info') || document;
      this.productInfoEl.addEventListener('variant:change', this.onVariantChange);
      document.addEventListener('visibilitychange', this._syncPreviewMotion);
      if (this._reduceMotionMq) {
        if (this._reduceMotionMq.addEventListener) this._reduceMotionMq.addEventListener('change', this._syncPreviewMotion);
        else if (this._reduceMotionMq.addListener) this._reduceMotionMq.addListener(this._syncPreviewMotion);
      }
    }

    unbindEvents() {
      this.viewport?.removeEventListener('pointerdown', this.onPointerDown);
      this.viewport?.removeEventListener('click', this.onClick);
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerCancel);
      window.removeEventListener('keydown', this.onWindowKeyDown);
      window.removeEventListener('resize', this.onWindowResize);
      this.productInfoEl?.removeEventListener('variant:change', this.onVariantChange);
      document.removeEventListener('visibilitychange', this._syncPreviewMotion);
      if (this._reduceMotionMq) {
        if (this._reduceMotionMq.removeEventListener) this._reduceMotionMq.removeEventListener('change', this._syncPreviewMotion);
        else if (this._reduceMotionMq.removeListener) this._reduceMotionMq.removeListener(this._syncPreviewMotion);
      }
    }

    /* ---------------- variant handling ---------------- */

    onVariantChange(event) {
      if (this.hasGeneratedPreview()) return;
      const variant = event?.detail;
      if (!variant) return;
      const mediaId = this.variantMediaMap[String(variant.id)];
      if (!mediaId) return;
      this.setActiveMedia(mediaId);
    }

    // Public, also called by product-info.js
    setActiveMedia(mediaId) {
      if (this.hasGeneratedPreview() && String(mediaId).indexOf('fab-preview-') !== 0) return;
      const idx = this.slides.findIndex((s) => String(s.dataset.mediaId) === String(mediaId));
      if (idx < 0) return;
      this.goTo(idx);
    }

    hasGeneratedPreview() {
      return PREVIEW_IDS.some((id) => this.querySelector('[data-media-id="' + id + '"]'));
    }

    previewSlideActive() {
      const id = this.slides[this.activeIndex] && this.slides[this.activeIndex].dataset.mediaId || '';
      return PREVIEW_IDS.indexOf(id) >= 0;
    }

    _resyncSlides() {
      this.slides = Array.from(this.querySelectorAll('[data-fab-gallery-slide]'));
      this.thumbsContainer = this.querySelector('[data-fab-gallery-thumbs]');
      this.thumbs = Array.from(this.querySelectorAll('[data-fab-gallery-thumb]'));
      this.dotsContainer = this.querySelector('[data-fab-gallery-dots]');
      this.dots = Array.from(this.querySelectorAll('[data-fab-gallery-dot]'));
      this.prevBtn = this.querySelector('[data-fab-gallery-prev]');
      this.nextBtn = this.querySelector('[data-fab-gallery-next]');
      this.slides.forEach((slide, i) => { slide.dataset.mediaIndex = String(i); });
      this.thumbs.forEach((thumb, i) => { thumb.dataset.mediaIndex = String(i); });
      this.dots.forEach((dot, i) => { dot.dataset.mediaIndex = String(i); });
      if (this.activeIndex >= this.slides.length) this.activeIndex = Math.max(0, this.slides.length - 1);
    }

    _ensurePreviewChrome() {
      const layout = this.querySelector('.fab-gallery__layout');
      const stage = this.querySelector('.fab-gallery__stage');
      if (layout && stage && !this.querySelector('[data-fab-gallery-thumbs]')) {
        const thumbs = document.createElement('div');
        thumbs.className = 'fab-gallery__thumbs';
        thumbs.setAttribute('data-fab-gallery-thumbs', '');
        thumbs.setAttribute('role', 'tablist');
        thumbs.setAttribute('aria-label', 'Product image thumbnails');
        layout.insertBefore(thumbs, stage);
      }
      if (stage && !this.querySelector('[data-fab-gallery-dots]')) {
        const dots = document.createElement('div');
        dots.className = 'fab-gallery__dots';
        dots.setAttribute('data-fab-gallery-dots', '');
        dots.setAttribute('role', 'tablist');
        dots.setAttribute('aria-label', 'Image pagination');
        stage.appendChild(dots);
      }
      if (stage && !this.querySelector('[data-fab-gallery-prev]')) {
        const prev = document.createElement('button');
        prev.type = 'button';
        prev.className = 'fab-gallery__arrow fab-gallery__arrow--prev';
        prev.setAttribute('data-fab-gallery-prev', '');
        prev.setAttribute('aria-label', 'Previous slide');
        prev.innerHTML = '<svg viewBox="0 0 512 512" aria-hidden="true"><path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="48" d="M328 112L184 256l144 144"/></svg>';
        prev.addEventListener('click', () => this.go(-1, 'swipe'));
        stage.appendChild(prev);
        const next = document.createElement('button');
        next.type = 'button';
        next.className = 'fab-gallery__arrow fab-gallery__arrow--next';
        next.setAttribute('data-fab-gallery-next', '');
        next.setAttribute('aria-label', 'Next slide');
        next.innerHTML = '<svg viewBox="0 0 512 512" aria-hidden="true"><path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="48" d="M184 112l144 144-144 144"/></svg>';
        next.addEventListener('click', () => this.go(+1, 'swipe'));
        stage.appendChild(next);
      }
      this._resyncSlides();
    }

    _removePreviewNode(id) {
      this.querySelectorAll('[data-media-id="' + id + '"]').forEach((el) => el.remove());
    }

    prefersReducedMotion() {
      return !!(this._reduceMotionMq && this._reduceMotionMq.matches);
    }

    _syncPreviewMotion() {
      const waiting = this.querySelector('[data-fab-gallery-slide][data-media-id="' + PREVIEW_WAITING_ID + '"]');
      const motion = waiting && waiting.querySelector('.preview-motion');
      if (!motion) return;
      const onSlide = this.slides[this.activeIndex] === waiting;
      const hidden = !!document.hidden;
      const reduce = this.prefersReducedMotion();
      const error = !!(waiting.querySelector('.fab-print-preview-stage.is-error'));
      const shouldPlay = motion.getAttribute('data-motion') === 'on' && onSlide && !hidden && !reduce && !error;
      motion.classList.toggle('is-paused', !shouldPlay);
      if (reduce) motion.classList.add('is-reduced');
      else motion.classList.remove('is-reduced');
    }

    upsertGeneratedSlides(spec) {
      spec = spec || {};
      if (!this.track) return;
      const currentId = (this.slides[this.activeIndex] && this.slides[this.activeIndex].dataset.mediaId) || '';
      const wasOnPreview = PREVIEW_IDS.indexOf(currentId) >= 0;
      const reduceMotion = this.prefersReducedMotion();

      if (!spec.mode || spec.mode === 'none') {
        if (this._previewRevealTimer) {
          clearTimeout(this._previewRevealTimer);
          this._previewRevealTimer = 0;
        }
        PREVIEW_IDS.forEach((id) => this._removePreviewNode(id));
        this._resyncSlides();
        this.update(true);
        return;
      }

      this._ensurePreviewChrome();
      if (spec.mode === 'waiting' || spec.mode === 'error') {
        this._removePreviewNode(PREVIEW_ROOM_ID);
        this._removePreviewNode(PREVIEW_CLOSE_ID);
        this._upsertWaitingSlide(spec);
        this._resyncSlides();
        if (spec.activate) this.activeIndex = 0;
        else if (wasOnPreview) {
          const idx = this.slides.findIndex((s) => s.dataset.mediaId === PREVIEW_WAITING_ID);
          if (idx >= 0) this.activeIndex = idx;
        } else {
          const stay = this.slides.findIndex((s) => s.dataset.mediaId === currentId);
          if (stay >= 0) this.activeIndex = stay;
        }
        this.update(true);
        return;
      }

      if (spec.mode === 'ready') {
        const immediate = !!(spec.immediate || reduceMotion);
        const waiting = this.querySelector('[data-fab-gallery-slide][data-media-id="' + PREVIEW_WAITING_ID + '"]');
        if (waiting && spec.reveal && !immediate && spec.room && spec.room.src) {
          this._beginRoomReveal(waiting, spec, currentId);
          return;
        }
        this._commitReadySlides(spec, currentId, wasOnPreview, immediate);
      }
    }

    _upsertWaitingSlide(spec) {
      let waiting = this.querySelector('[data-fab-gallery-slide][data-media-id="' + PREVIEW_WAITING_ID + '"]');
      if (!waiting) {
        waiting = document.createElement('div');
        waiting.className = 'fab-gallery__slide';
        waiting.setAttribute('data-fab-gallery-slide', '');
        waiting.setAttribute('data-media-id', PREVIEW_WAITING_ID);
        waiting.setAttribute('data-media-type', 'preview');
        waiting.setAttribute('role', 'group');
        waiting.setAttribute('aria-roledescription', 'slide');
        waiting.innerHTML =
          '<div class="fab-print-preview-stage">' +
            '<div class="preview-motion" aria-hidden="true" data-motion="off">' +
              '<div class="preview-motion__wash"></div>' +
              '<div class="preview-motion__wash preview-motion__wash--second"></div>' +
              '<div class="preview-motion__grain"></div>' +
              '<div class="preview-motion__sweep"></div>' +
            '</div>' +
            '<div class="fab-print-preview-stage__copy">' +
              '<p class="fab-print-preview-stage__kicker"></p>' +
              '<p class="fab-print-preview-stage__status"></p>' +
              '<p class="fab-print-preview-stage__support"></p>' +
              '<button type="button" class="fab-print-preview-stage__skip" data-fab-print-preview-skip hidden></button>' +
              '<button type="button" class="fab-print-preview-stage__retry" data-fab-print-preview-retry-room hidden></button>' +
            '</div>' +
          '</div>';
        this.track.insertBefore(waiting, this.track.firstChild);
      }
      const stage = waiting.querySelector('.fab-print-preview-stage');
      const motion = waiting.querySelector('.preview-motion');
      if (stage) stage.classList.toggle('is-error', spec.mode === 'error');
      if (motion) {
        const colour = spec.colour || spec.wash || '#DCC9BF';
        const accent = spec.accent || '#F2E7D8';
        motion.style.setProperty('--preview-colour', colour);
        motion.style.setProperty('--preview-accent', accent);
        const nextMotion = spec.motion === 'on' && spec.mode !== 'error' ? 'on' : 'off';
        if (motion.getAttribute('data-motion') !== nextMotion) motion.setAttribute('data-motion', nextMotion);
        motion.classList.remove('is-exiting');
        this._syncWaitingPhotos(motion, spec, nextMotion === 'on');
      }
      const kicker = waiting.querySelector('.fab-print-preview-stage__kicker');
      const status = waiting.querySelector('.fab-print-preview-stage__status');
      const support = waiting.querySelector('.fab-print-preview-stage__support');
      const skip = waiting.querySelector('[data-fab-print-preview-skip]');
      const retry = waiting.querySelector('[data-fab-print-preview-retry-room]');
      if (kicker) kicker.textContent = spec.kicker || '';
      if (status) status.textContent = spec.status || '';
      if (support) support.textContent = spec.support || '';
      if (skip) {
        skip.textContent = spec.skipLabel || '';
        skip.hidden = !spec.showSkip;
      }
      if (retry) {
        retry.textContent = spec.retryLabel || '';
        retry.hidden = !spec.showRetry;
      }
      this._upsertPreviewThumb(PREVIEW_WAITING_ID, spec.kicker, spec.photoUrl || (spec.photoUrls && spec.photoUrls[0]) || '');
      this._upsertPreviewDot(PREVIEW_WAITING_ID, spec.kicker);
    }

    _syncWaitingPhotos(motion, spec, motionOn) {
      const urls = (spec.photoUrls && spec.photoUrls.length)
        ? spec.photoUrls.filter(Boolean)
        : (spec.photoUrl ? [spec.photoUrl] : []);
      Array.from(motion.querySelectorAll(':scope > .preview-motion__photo')).forEach((el) => el.remove());
      let wrap = motion.querySelector('.preview-motion__photos');
      if (!urls.length) {
        if (wrap) wrap.remove();
        return;
      }
      if (!wrap) {
        wrap = document.createElement('div');
        wrap.className = 'preview-motion__photos';
        motion.insertBefore(wrap, motion.firstChild);
      }
      wrap.dataset.count = String(urls.length);
      const existing = Array.from(wrap.querySelectorAll('.preview-motion__photo'));
      const reduce = this.prefersReducedMotion();
      urls.forEach((url, i) => {
        let img = existing[i];
        if (!img) {
          img = document.createElement('img');
          img.className = 'preview-motion__photo';
          img.alt = '';
          img.decoding = 'async';
          wrap.appendChild(img);
        }
        if (img.getAttribute('src') !== url) {
          img.classList.remove('is-soft');
          img.src = url;
        }
        if (motionOn && spec.mode !== 'error' && !reduce) {
          if (!img.classList.contains('is-soft')) {
            requestAnimationFrame(function () { img.classList.add('is-soft'); });
          }
        } else {
          img.classList.toggle('is-soft', motionOn && reduce);
        }
      });
      existing.slice(urls.length).forEach((el) => el.remove());
    }

    _beginRoomReveal(waiting, spec, currentId) {
      const self = this;
      const motion = waiting.querySelector('.preview-motion');
      const stage = waiting.querySelector('.fab-print-preview-stage');
      if (motion) motion.classList.add('is-exiting');
      let overlay = waiting.querySelector('img.fab-gallery__image--preview-reveal');
      if (!overlay && spec.room && spec.room.src) {
        overlay = document.createElement('img');
        overlay.className = 'fab-gallery__image fab-gallery__image--preview-reveal';
        overlay.decoding = 'async';
        overlay.alt = spec.room.alt || '';
        this._applyPreviewMediaBox(overlay, spec.room);
        overlay.src = spec.room.src;
        if (stage) stage.appendChild(overlay);
        requestAnimationFrame(function () { overlay.classList.add('is-sharp'); });
      }
      if (this._previewRevealTimer) clearTimeout(this._previewRevealTimer);
      this._previewRevealTimer = setTimeout(function () {
        self._previewRevealTimer = 0;
        const stayId = (self.slides[self.activeIndex] && self.slides[self.activeIndex].dataset.mediaId) || currentId;
        self._commitReadySlides(spec, stayId, PREVIEW_IDS.indexOf(stayId) >= 0, true);
      }, 800);
    }

    _commitReadySlides(spec, currentId, wasOnPreview, immediate) {
      this._removePreviewNode(PREVIEW_WAITING_ID);
      const firstProduct = Array.from(this.track.children).find((el) => PREVIEW_IDS.indexOf(el.dataset.mediaId) < 0) || null;
      const room = this._upsertPreviewImage(PREVIEW_ROOM_ID, spec.room || {}, immediate);
      const close = spec.close && spec.close.src
        ? this._upsertPreviewImage(PREVIEW_CLOSE_ID, spec.close, immediate)
        : this._upsertClosePlaceholder(spec.close || {});
      if (room.parentNode !== this.track) this.track.insertBefore(room, firstProduct);
      if (close.parentNode !== this.track) this.track.insertBefore(close, room.nextSibling);
      this._upsertPreviewThumb(PREVIEW_ROOM_ID, spec.room && spec.room.label, spec.room && spec.room.src);
      this._upsertPreviewThumb(PREVIEW_CLOSE_ID, spec.close && spec.close.label, spec.close && spec.close.src);
      this._upsertPreviewDot(PREVIEW_ROOM_ID, spec.room && spec.room.label);
      this._upsertPreviewDot(PREVIEW_CLOSE_ID, spec.close && spec.close.label);
      this._resyncSlides();
      const waitingGone = currentId === PREVIEW_WAITING_ID;
      if (spec.activate || waitingGone) {
        const stayClose = currentId === PREVIEW_CLOSE_ID;
        const target = stayClose ? PREVIEW_CLOSE_ID : PREVIEW_ROOM_ID;
        const idx = this.slides.findIndex((s) => s.dataset.mediaId === target);
        if (idx >= 0) this.activeIndex = idx;
      } else {
        const stay = this.slides.findIndex((s) => s.dataset.mediaId === currentId);
        if (stay >= 0) this.activeIndex = stay;
        else if (wasOnPreview) {
          const idx = this.slides.findIndex((s) => s.dataset.mediaId === PREVIEW_ROOM_ID);
          if (idx >= 0) this.activeIndex = idx;
        }
      }
      this.update(true);
    }

    _upsertClosePlaceholder(spec) {
      let slide = this.querySelector('[data-fab-gallery-slide][data-media-id="' + PREVIEW_CLOSE_ID + '"]');
      if (slide && slide.querySelector('img.fab-gallery__image')) return slide;
      if (!slide) {
        slide = document.createElement('div');
        slide.className = 'fab-gallery__slide';
        slide.setAttribute('data-fab-gallery-slide', '');
        slide.setAttribute('data-media-id', PREVIEW_CLOSE_ID);
        slide.setAttribute('data-media-type', 'preview');
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-roledescription', 'slide');
      }
      slide.innerHTML =
        '<div class="fab-print-preview-stage fab-print-preview-stage--bare">' +
          '<div class="preview-motion" aria-hidden="true" data-motion="off"></div>' +
          '<div class="fab-print-preview-stage__copy">' +
            '<p class="fab-print-preview-stage__kicker"></p>' +
            '<p class="fab-print-preview-stage__status"></p>' +
            '<p class="fab-print-preview-stage__support"></p>' +
            '<button type="button" class="fab-print-preview-stage__retry" data-fab-print-preview-retry-close hidden></button>' +
          '</div>' +
        '</div>';
      const kicker = slide.querySelector('.fab-print-preview-stage__kicker');
      const status = slide.querySelector('.fab-print-preview-stage__status');
      const retry = slide.querySelector('[data-fab-print-preview-retry-close]');
      if (kicker) kicker.textContent = spec.label || '';
      if (status) status.textContent = spec.status || '';
      if (retry) {
        retry.textContent = spec.retryLabel || '';
        retry.hidden = !spec.showRetry;
      }
      return slide;
    }

    _upsertPreviewThumb(id, label, src) {
      let thumb = this.querySelector('[data-fab-gallery-thumb][data-media-id="' + id + '"]');
      const host = this.querySelector('[data-fab-gallery-thumbs]');
      if (!host) return;
      if (!thumb) {
        thumb = document.createElement('button');
        thumb.type = 'button';
        thumb.className = 'fab-gallery__thumb fab-gallery__thumb--preview';
        thumb.setAttribute('data-fab-gallery-thumb', '');
        thumb.setAttribute('data-media-id', id);
        thumb.setAttribute('role', 'tab');
        const firstProduct = host.querySelector('[data-fab-gallery-thumb]:not(.fab-gallery__thumb--preview)');
        host.insertBefore(thumb, firstProduct);
      }
      thumb.setAttribute('aria-label', label || 'Your preview');
      if (src) {
        let img = thumb.querySelector('img');
        if (!img) {
          img = document.createElement('img');
          img.alt = '';
          img.decoding = 'async';
          thumb.appendChild(img);
        }
        if (img.getAttribute('src') !== src) img.src = src;
      }
    }

    _upsertPreviewDot(id, label) {
      let dot = this.querySelector('[data-fab-gallery-dot][data-media-id="' + id + '"]');
      const host = this.querySelector('[data-fab-gallery-dots]');
      if (!host) return;
      if (!dot) {
        dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'fab-gallery__dot';
        dot.setAttribute('data-fab-gallery-dot', '');
        dot.setAttribute('data-media-id', id);
        dot.setAttribute('role', 'tab');
        const firstProduct = host.querySelector('[data-fab-gallery-dot]:not([data-media-id^="fab-preview-"])');
        host.insertBefore(dot, firstProduct);
      }
      dot.setAttribute('aria-label', label || 'Your preview');
    }

    _upsertPreviewImage(id, spec, immediate) {
      let slide = this.querySelector('[data-fab-gallery-slide][data-media-id="' + id + '"]');
      if (!slide) {
        slide = document.createElement('div');
        slide.className = 'fab-gallery__slide';
        slide.setAttribute('data-fab-gallery-slide', '');
        slide.setAttribute('data-media-id', id);
        slide.setAttribute('data-media-type', 'image');
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-roledescription', 'slide');
      }
      if (spec.src) {
        slide.setAttribute('data-media-type', 'image');
        const leftover = slide.querySelector('.fab-print-preview-stage');
        if (leftover) leftover.remove();
        let img = slide.querySelector('img.fab-gallery__image');
        if (!img) {
          img = document.createElement('img');
          img.className = 'fab-gallery__image';
          img.decoding = 'async';
          slide.insertBefore(img, slide.firstChild);
        }
        img.alt = spec.alt || '';
        this._applyPreviewMediaBox(img, spec);
        if (spec.orientation) slide.setAttribute('data-preview-orientation', spec.orientation);
        else slide.removeAttribute('data-preview-orientation');
        if (img.getAttribute('src') !== spec.src) {
          if (immediate) {
            img.classList.remove('fab-gallery__image--preview-reveal', 'is-sharp');
            img.src = spec.src;
          } else {
            img.classList.add('fab-gallery__image--preview-reveal');
            img.classList.remove('is-sharp');
            img.src = spec.src;
            const sharpen = () => {
              img.classList.add('is-sharp');
              const done = (ev) => {
                if (ev && ev.propertyName && ev.propertyName !== 'opacity' && ev.propertyName !== 'filter') return;
                img.classList.remove('fab-gallery__image--preview-reveal', 'is-sharp');
                img.removeEventListener('transitionend', done);
              };
              img.addEventListener('transitionend', done);
              setTimeout(done, 900);
            };
            if (typeof img.decode === 'function') img.decode().then(sharpen).catch(sharpen);
            else requestAnimationFrame(sharpen);
          }
        }
      }
      if (spec.caption) {
        let cap = slide.querySelector('.fab-gallery__caption--preview');
        if (!cap) {
          cap = document.createElement('p');
          cap.className = 'fab-gallery__caption--preview';
          slide.appendChild(cap);
        }
        cap.textContent = spec.caption;
      }
      return slide;
    }

    _applyPreviewMediaBox(img, spec) {
      if (!img || !spec) return;
      const w = Number(spec.width);
      const h = Number(spec.height);
      if (w > 0 && h > 0) {
        img.setAttribute('width', String(w));
        img.setAttribute('height', String(h));
        img.style.aspectRatio = w + ' / ' + h;
      }
      img.style.objectFit = 'contain';
      img.style.objectPosition = 'center';
    }

    /* ---------------- carousel navigation ---------------- */

    go(dir, interaction) {
      this.goTo(this.activeIndex + dir, interaction || null);
    }

    goTo(idx, interaction) {
      const max = this.slides.length - 1;
      idx = Math.max(0, Math.min(idx, max));
      // Always exit zoom on slide change to keep state predictable.
      this.exitZoom(this.getActiveImage());
      if (idx === this.activeIndex) {
        this.update();
        return;
      }
      this.activeIndex = idx;
      this.update();
      // User-driven only (thumb / swipe / arrows). Skip programmatic setActiveMedia.
      if (interaction && typeof window.__fabTrackGalleryInteraction === 'function') {
        window.__fabTrackGalleryInteraction(interaction, idx, this);
      }
    }

    update(skipTransition, reach) {
      if (!this.track) return;

      this.hydrateNeighbourImages(reach);

      if (skipTransition) {
        this._clearTrackWillChangeNow();
        this.track.classList.add('fab-gallery__track--no-transition');
      } else if (this.slides.length > 1) {
        this.track.style.willChange = 'transform';
        this._scheduleTrackWillChangeRelease();
      }

      this.track.style.transform = `translateX(${-this.activeIndex * 100}%)`;

      if (skipTransition) {
        // Force a layout flush, then re-enable transitions.
        // eslint-disable-next-line no-unused-expressions
        this.track.offsetHeight;
        requestAnimationFrame(() => {
          this.track.classList.remove('fab-gallery__track--no-transition');
        });
      }

      this.slides.forEach((slide, i) => {
        const active = i === this.activeIndex;
        slide.classList.toggle('fab-gallery__slide--active', active);

        // Hosted video: pause when the slide goes inactive; the active slide's
        // video gets played by playActiveVideoIfAny() below.
        const video = slide.querySelector('video');
        if (video && !active) {
          try { video.pause(); } catch (_) {}
        }

        // External video (YouTube/Vimeo): destroy the iframe when navigating
        // away so it stops counting view time / playing audio in the bg.
        if (!active) {
          const externalWrap = slide.querySelector('[data-fab-gallery-external-video]');
          const iframe = externalWrap?.querySelector('iframe');
          if (iframe && externalWrap.dataset.originalHtml) {
            externalWrap.innerHTML = externalWrap.dataset.originalHtml;
          }
        }
      });

      this.thumbs.forEach((thumb, i) => {
        const active = i === this.activeIndex;
        thumb.classList.toggle('fab-gallery__thumb--active', active);
        thumb.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      this.dots.forEach((dot, i) => {
        const active = i === this.activeIndex;
        dot.classList.toggle('fab-gallery__dot--active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      this.scrollActiveThumbIntoView();

      if (this.prevBtn) {
        const hidePrev = this.activeIndex === 0;
        this.prevBtn.toggleAttribute('hidden', hidePrev);
        this.prevBtn.setAttribute('aria-hidden', hidePrev ? 'true' : 'false');
      }
      if (this.nextBtn) {
        const hideNext = this.activeIndex === this.slides.length - 1;
        this.nextBtn.toggleAttribute('hidden', hideNext);
        this.nextBtn.setAttribute('aria-hidden', hideNext ? 'true' : 'false');
      }

      // Autoplay the now-active video, if any.
      this.playActiveVideoIfAny();
      this._syncPreviewMotion();

      this.dispatchEvent(
        new CustomEvent('fab-gallery:change', {
          detail: {
            index: this.activeIndex,
            mediaId: this.slides[this.activeIndex]?.dataset.mediaId,
          },
          bubbles: true,
        })
      );
    }

    /** Try to play the hosted video on the active slide. Browsers will block
        autoplay if the video isn't muted; our markup sets muted+playsinline so
        this should succeed on all modern browsers. The catch handles edge
        cases (Safari Low Power Mode, etc.) silently. */
    playActiveVideoIfAny() {
      const slide = this.slides[this.activeIndex];
      if (!slide || slide.dataset.mediaType !== 'video') return;
      const video = slide.querySelector('video');
      if (!video) return;
      // Defensive — these are also set in the markup but resetting here means
      // a programmatically-paused video can still autoplay on next visit.
      video.muted = true;
      video.playsInline = true;
      const p = video.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    }

    scrollActiveThumbIntoView() {
      const container = this.thumbsContainer;
      const thumb = this.thumbs[this.activeIndex];
      if (!container || !thumb) return;
      if (container.offsetParent === null) return;

      if (container.scrollHeight > container.clientHeight + 1) {
        const top = thumb.offsetTop - (container.clientHeight - thumb.offsetHeight) / 2;
        container.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      } else if (container.scrollWidth > container.clientWidth + 1) {
        const left = thumb.offsetLeft - (container.clientWidth - thumb.offsetWidth) / 2;
        container.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
      }
    }

    onWindowResize() {
      // Pan bounds depend on viewport size, so re-clamp the active zoomed image.
      const img = this.getActiveImage();
      const s = this.zoomStateOf(img);
      if (!img || s.scale <= 1.001) return;
      const vpRect = this.viewport.getBoundingClientRect();
      const fit = this.getFitSize(img, vpRect);
      const { tx, ty } = this.clampPan(s.tx, s.ty, fit, vpRect, s.scale);
      this.applyZoomTransform(img, s.scale, tx, ty, false);
    }

    /* ---------------- zoom helpers ---------------- */

    isEditPhotoGallery() {
      return this.hasAttribute('data-fab-cp-gallery') || this.classList.contains('fab-cp-gallery--edit-photo');
    }

    openEditPhoto() {
      this.dispatchEvent(new CustomEvent('fab-cp-gallery:edit', { bubbles: true }));
    }

    getActiveImage() {
      const slide = this.slides[this.activeIndex];
      if (!slide || slide.dataset.mediaType !== 'image') return null;
      return slide.querySelector('img.fab-gallery__image') || null;
    }

    zoomStateOf(img) {
      return img && img._fabZoom ? img._fabZoom : { scale: 1, tx: 0, ty: 0 };
    }

    isZoomed() {
      return this.zoomStateOf(this.getActiveImage()).scale > 1.001;
    }

    applyZoomTransform(img, scale, tx, ty, smooth) {
      if (!img) return;
      img.style.transition = smooth
        ? 'transform 280ms cubic-bezier(0.32, 0.72, 0, 1)'
        : 'none';
      img.style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`;
      img._fabZoom = { scale, tx, ty };
      const zoomed = scale > 1.001;
      img.dataset.zoomed = zoomed ? 'true' : 'false';
      this.classList.toggle('fab-gallery--zoomed', zoomed);
      // Cursor affordance — the slide handles the hit area.
      const slide = img.closest('[data-fab-gallery-slide]');
      if (slide) slide.style.cursor = zoomed ? 'zoom-out' : 'zoom-in';
    }

    /* Object-fit:contain rendered size for an image inside a viewport rect. */
    getFitSize(img, vpRect) {
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      if (!w || !h) return { w: vpRect.width, h: vpRect.height };
      const r = img.getBoundingClientRect();
      const s = this.zoomStateOf(img).scale || 1;
      return { w: r.width / s, h: r.height / s };
    }

    clampPan(tx, ty, fit, vpRect, scale) {
      const maxX = Math.max(0, (fit.w * scale - vpRect.width) / 2);
      const maxY = Math.max(0, (fit.h * scale - vpRect.height) / 2);
      return {
        tx: Math.max(-maxX, Math.min(maxX, tx)),
        ty: Math.max(-maxY, Math.min(maxY, ty)),
      };
    }

    /* Swap to a higher-resolution source on first zoom to maximise sharpness.
       Restores the responsive src on exit so subsequent navigation stays cheap. */
    upgradeImageForZoom(img) {
      if (!img || img.dataset.zoomUpgraded === 'true') return;
      const baseUrl = img.dataset.imageBaseSrc || img.getAttribute('src');
      const zoomSrc = img.dataset.zoomSrc || imageUrlAtWidth(baseUrl, 2400);
      if (!zoomSrc) return;
      // Stash originals
      img.dataset.origSrc = img.getAttribute('src') || '';
      img.dataset.origSrcset = img.getAttribute('srcset') || '';
      img.dataset.origSizes = img.getAttribute('sizes') || '';
      // Apply zoom-quality
      img.src = zoomSrc;
      if (baseUrl) img.srcset = imageSrcset(baseUrl, ZOOM_WIDTHS);
      else if (img.dataset.zoomSrcset) img.srcset = img.dataset.zoomSrcset;
      else img.removeAttribute('srcset');
      img.sizes = '100vw';
      img.dataset.zoomUpgraded = 'true';
    }

    restoreImageAfterZoom(img) {
      if (!img || img.dataset.zoomUpgraded !== 'true') return;
      if (img.dataset.origSrc) img.src = img.dataset.origSrc;
      if (img.dataset.origSrcset) img.srcset = img.dataset.origSrcset;
      else img.removeAttribute('srcset');
      if (img.dataset.origSizes) img.sizes = img.dataset.origSizes;
      else img.removeAttribute('sizes');
      img.dataset.zoomUpgraded = 'false';
    }

    enterZoomAt(clientX, clientY, scale) {
      const img = this.getActiveImage();
      if (!img) return;
      this.upgradeImageForZoom(img);

      const vpRect = this.viewport.getBoundingClientRect();
      const vc = { x: vpRect.left + vpRect.width / 2, y: vpRect.top + vpRect.height / 2 };
      const mRelX = clientX - vc.x;
      const mRelY = clientY - vc.y;

      // Anchored zoom from the current state (always 1x when entering, but
      // the math is general so it works for re-anchoring at any scale).
      const state = this.zoomStateOf(img);
      const k = scale / state.scale;
      let tx = mRelX - (mRelX - state.tx) * k;
      let ty = mRelY - (mRelY - state.ty) * k;

      const fit = this.getFitSize(img, vpRect);
      ({ tx, ty } = this.clampPan(tx, ty, fit, vpRect, scale));
      this.applyZoomTransform(img, scale, tx, ty, true);
    }

    exitZoom(img) {
      if (!img) return;
      const state = this.zoomStateOf(img);
      if (state.scale <= 1.001 && state.tx === 0 && state.ty === 0) return;
      this.applyZoomTransform(img, 1, 0, 0, true);
      // Restore the responsive image source after the transition has had a
      // chance to start; leaves the visual snap clean.
      setTimeout(() => {
        // Only restore if still at scale 1 (user might have re-zoomed)
        if (this.zoomStateOf(img).scale <= 1.001) {
          this.restoreImageAfterZoom(img);
        }
      }, 320);
    }

    toggleZoomAt(clientX, clientY) {
      if (this.isEditPhotoGallery()) {
        this.openEditPhoto();
        return;
      }
      const img = this.getActiveImage();
      if (!img) return;
      if (this.isZoomed()) {
        this.exitZoom(img);
      } else {
        this.enterZoomAt(clientX, clientY, ZOOM_SCALE);
        if (typeof window.__fabTrackGalleryInteraction === 'function') {
          window.__fabTrackGalleryInteraction('zoom', this.activeIndex, this);
        }
      }
    }

    /* ---------------- pointer / gesture system ---------------- */

    onPointerDown(e) {
      // Ignore secondary mouse buttons
      if (e.button !== undefined && e.button !== 0) return;
      const target = e.target;

      // Always ignore navigation buttons (prev/next/dots) so they don't
      // accidentally start a gesture.
      if (target.closest('button[data-fab-gallery-prev], button[data-fab-gallery-next], button[data-fab-gallery-dot]')) return;
      // External video iframes own their gestures completely.
if (target.closest('iframe')) return;

// Native video controls live in the bottom ~40px strip. Let the
// browser own that area entirely so play/pause/scrubber/fullscreen
// all work natively. Above the strip, our gesture system handles
// swipes; pure taps still fall through to the <video> element's
// own click behaviour (toggling play/pause on click is browser
// default for click-to-play videos but Chrome/Safari only do this
// when controls are present).
if (target.tagName === 'VIDEO') {
  const vRect = target.getBoundingClientRect();
  const yFromBottom = vRect.bottom - e.clientY;
  if (yFromBottom <= 50) return; // hands off — controls strip
}

this.hydrateNeighbourImages(2);

if (!target.closest('button[data-fab-gallery-video-toggle]')) {
  try { this.viewport.setPointerCapture?.(e.pointerId); } catch (_) {}
}
      this.pointers.set(e.pointerId, {
        x: e.clientX, y: e.clientY,
        startX: e.clientX, startY: e.clientY,
        downTarget: target,
        pointerType: e.pointerType,
      });

      // Switch to pinch on second finger (only meaningful on image slides)
      if (this.pointers.size === 2) {
        if (this.isEditPhotoGallery()) return;
        // Cancel any in-flight swipe and snap track back
        if (this.mode === 'swipe' && this.track) {
          this.track.classList.remove('fab-gallery__track--no-transition');
          this.track.style.transform = `translateX(${-this.activeIndex * 100}%)`;
          this.swipeStart = null;
        }
        const img = this.getActiveImage();
        if (!img) return; // pinch only on images
        const [a, b] = [...this.pointers.values()];
        const s = this.zoomStateOf(img);
        const vpRect = this.viewport.getBoundingClientRect();
        const m = midpoint(a, b);
        this.upgradeImageForZoom(img);
        this.mode = 'pinch';
        this.pinchStart = {
          d: distance(a, b) || 1,
          mxRel: m.x - (vpRect.left + vpRect.width / 2),
          myRel: m.y - (vpRect.top + vpRect.height / 2),
          scale: s.scale,
          tx: s.tx,
          ty: s.ty,
          vpRect,
          fit: this.getFitSize(img, vpRect),
        };
        this.panStart = null;
        return;
      }

      // First pointer down: pan if the active image is zoomed, otherwise swipe.
      if (this.pointers.size === 1) {
        const img = this.getActiveImage();
        const s = this.zoomStateOf(img);
        const vpRect = this.viewport.getBoundingClientRect();

        if (img && s.scale > 1.001 && !this.isEditPhotoGallery()) {
          this.mode = 'pan';
          this.panMoved = false;
          this.panStart = {
            x: e.clientX, y: e.clientY,
            scale: s.scale, tx: s.tx, ty: s.ty,
            vpRect,
            fit: this.getFitSize(img, vpRect),
          };
        } else if (this.slides.length > 1) {
          this.mode = 'swipe';
          this.swipeStart = {
            x: e.clientX, y: e.clientY,
            locked: null,
            vpW: vpRect.width,
          };
          this.track.style.willChange = 'transform';
        } else {
          // Single image, not zoomed — nothing to do on move, but listen for click
          this.mode = null;
        }
      }

      window.addEventListener('pointermove', this.onPointerMove, { passive: false });
      window.addEventListener('pointerup', this.onPointerUp);
      window.addEventListener('pointercancel', this.onPointerCancel);
    }

    onPointerMove(e) {
      const p = this.pointers.get(e.pointerId);
      if (!p) return;
      p.x = e.clientX;
      p.y = e.clientY;

      if (this.mode === 'pinch' && this.pointers.size === 2 && this.pinchStart) {
        const img = this.getActiveImage();
        if (!img) return;
        const [a, b] = [...this.pointers.values()];
        const d = distance(a, b) || 1;
        const m = midpoint(a, b);
        const vc = {
          x: this.pinchStart.vpRect.left + this.pinchStart.vpRect.width / 2,
          y: this.pinchStart.vpRect.top + this.pinchStart.vpRect.height / 2,
        };
        const mxRel = m.x - vc.x;
        const myRel = m.y - vc.y;

        const rawScale = this.pinchStart.scale * (d / this.pinchStart.d);
        const newScale = Math.max(1, Math.min(MAX_PINCH_SCALE, rawScale));

        const k = newScale / this.pinchStart.scale;
        let newTx = mxRel - (this.pinchStart.mxRel - this.pinchStart.tx) * k;
        let newTy = myRel - (this.pinchStart.myRel - this.pinchStart.ty) * k;

        ({ tx: newTx, ty: newTy } = this.clampPan(newTx, newTy, this.pinchStart.fit, this.pinchStart.vpRect, newScale));
        this.applyZoomTransform(img, newScale, newTx, newTy, false);
        e.preventDefault();
        return;
      }

      if (this.mode === 'pan' && this.panStart) {
        const img = this.getActiveImage();
        if (!img) return;
        const dx = e.clientX - this.panStart.x;
        const dy = e.clientY - this.panStart.y;
        if (!this.panMoved && Math.hypot(dx, dy) > 5) this.panMoved = true;
        let tx = this.panStart.tx + dx;
        let ty = this.panStart.ty + dy;
        ({ tx, ty } = this.clampPan(tx, ty, this.panStart.fit, this.panStart.vpRect, this.panStart.scale));
        this.applyZoomTransform(img, this.panStart.scale, tx, ty, false);
        e.preventDefault();
        return;
      }

      if (this.mode === 'swipe' && this.swipeStart) {
        const dx = e.clientX - this.swipeStart.x;
        const dy = e.clientY - this.swipeStart.y;
        if (!this.swipeStart.locked) {
          if (Math.abs(dx) < SWIPE_LOCK_THRESHOLD && Math.abs(dy) < SWIPE_LOCK_THRESHOLD) return;
          this.swipeStart.locked = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        }
        if (this.swipeStart.locked !== 'x') return; // let vertical scroll pass through
        e.preventDefault();
        const offsetPct = -this.activeIndex * 100 + (dx / this.swipeStart.vpW) * 100;
        this.track.classList.add('fab-gallery__track--no-transition');
        this.track.style.transform = `translateX(${offsetPct}%)`;
      }
    }

    onPointerUp(e) {
      const p = this.pointers.get(e.pointerId);
      this.pointers.delete(e.pointerId);

      if (this.mode === 'pinch') {
        if (this.pointers.size < 2) {
          const img = this.getActiveImage();
          const s = this.zoomStateOf(img);
          if (s.scale < PINCH_RESET_BELOW) {
            this.applyZoomTransform(img, 1, 0, 0, true);
            setTimeout(() => {
              if (this.zoomStateOf(img).scale <= 1.001) this.restoreImageAfterZoom(img);
            }, 320);
          }
          // If one finger remains and we're still zoomed, continue as pan
          if (this.pointers.size === 1) {
            const [only] = this.pointers.values();
            const s2 = this.zoomStateOf(img);
            if (s2.scale > 1.001) {
              const vpRect = this.viewport.getBoundingClientRect();
              this.mode = 'pan';
              this.panStart = {
                x: only.x, y: only.y,
                scale: s2.scale, tx: s2.tx, ty: s2.ty,
                vpRect,
                fit: this.getFitSize(img, vpRect),
              };
            } else {
              this.mode = null;
            }
          } else {
            this.mode = null;
            this.pinchStart = null;
            this._removeWindowGestureListeners();
          }
        }
        return;
      }

      if (this.mode === 'pan') {
        if (this.pointers.size === 0) {
          const moved = this.panMoved;
          if (moved) {
            // Real pan committed — suppress the trailing click so the user
            // doesn't accidentally exit zoom by completing the gesture.
            this.realSwipeCommitted = true;
            setTimeout(() => { this.realSwipeCommitted = false; }, 50);
          } else if (p && this._isTapOnImage(p.downTarget)) {
            // Stationary click while zoomed — exit zoom. Synthesise here for
            // the same reason as the swipe-mode tap: Chrome's native click
            // event is unreliable after pointer gestures.
            this.tapHandledByPointerup = true;
            setTimeout(() => { this.tapHandledByPointerup = false; }, 50);
            this.toggleZoomAt(p.startX, p.startY);
          }
          this.mode = null;
          this.panStart = null;
          this.panMoved = false;
          this._removeWindowGestureListeners();
        }
        return;
      }

      if (this.mode === 'swipe') {
        if (this.pointers.size === 0) {
          const dx = (p?.x ?? e.clientX) - this.swipeStart.x;
          const dy = (p?.y ?? e.clientY) - this.swipeStart.y;
          const lockedX = this.swipeStart.locked === 'x';
          this.track.classList.remove('fab-gallery__track--no-transition');

          const threshold = Math.min(SWIPE_DISTANCE_MAX, this.swipeStart.vpW * SWIPE_DISTANCE_RATIO);
          const realSwipe = lockedX && Math.abs(dx) > threshold;

          if (realSwipe) {
            // Suppress the trailing click so we don't toggle zoom or play/pause
            // a video right after a slide change.
            this.realSwipeCommitted = true;
            setTimeout(() => { this.realSwipeCommitted = false; }, 50);
            this.go(dx < 0 ? +1 : -1, 'swipe');
          } else {
            // Snap track back to the active slide. If the gesture started on
            // an image and the pointer didn't really move, treat as a tap to
            // toggle zoom. (For videos and external-video posters, taps fall
            // through to those elements' own click handlers.)
            this.update();

            const tapTravel = Math.hypot(dx, dy);
            if (tapTravel <= TAP_MAX_MOVE_MOUSE && p && this._isTapOnImage(p.downTarget)) {
              this.tapHandledByPointerup = true;
              setTimeout(() => { this.tapHandledByPointerup = false; }, 50);
              this.toggleZoomAt(p.startX, p.startY);
            } else if (lockedX && Math.abs(dx) > SWIPE_LOCK_THRESHOLD) {
              // Some horizontal movement happened but didn't commit — still
              // suppress the trailing click so a half-swipe on a video doesn't
              // toggle play/pause.
              this.realSwipeCommitted = true;
              setTimeout(() => { this.realSwipeCommitted = false; }, 50);
            }
          }

          this.mode = null;
          this.swipeStart = null;
          this._removeWindowGestureListeners();
        }
        return;
      }

      // Mode was null (e.g. single-image gallery, or pointerdown happened on
      // a non-zoomable target). Still need to handle a tap on the image so
      // single-image PDPs can zoom too.
      if (this.pointers.size === 0) {
        if (p) {
          const tapTravel = Math.hypot((p.x - p.startX), (p.y - p.startY));
          if (tapTravel <= TAP_MAX_MOVE_MOUSE && this._isTapOnImage(p.downTarget)) {
            this.tapHandledByPointerup = true;
            setTimeout(() => { this.tapHandledByPointerup = false; }, 50);
            this.toggleZoomAt(p.startX, p.startY);
          }
        }
        this._removeWindowGestureListeners();
      }
    }

    _removeWindowGestureListeners() {
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerCancel);
    }

    /** True if the given pointerdown target is on an active image slide. */
    _isTapOnImage(target) {
      if (!target) return false;
      if (target.closest && (target.closest('button') || target.closest('iframe'))) return false;
      if (target.tagName === 'VIDEO') return false;
      if (target.closest && target.closest('[data-fab-gallery-external-video]')) return false;
      const slide = target.closest && target.closest('[data-fab-gallery-slide]');
      if (!slide || slide.dataset.mediaType !== 'image') return false;
      const slideIdx = parseInt(slide.dataset.mediaIndex, 10);
      if (!Number.isNaN(slideIdx) && slideIdx !== this.activeIndex) return false;
      return true;
    }

    /* ---------------- click handler ----------------
       Defensive backstop. pointerup is the primary path for tap detection
       (Chrome suppresses the native click event after pointer gestures with
       any meaningful drift). Click handlers on video toggles and external-
       video posters check `realSwipeCommitted` themselves so a completed
       swipe gesture doesn't accidentally play/pause the underlying video.
    ------------------------------------------------ */

    onClick(e) {
      if (this.tapHandledByPointerup) return;
      if (this.realSwipeCommitted) return;

      const target = e.target;
      if (!this._isTapOnImage(target)) return;

      this.toggleZoomAt(e.clientX, e.clientY);
    }

    /* ---------------- keyboard ---------------- */

    onKeyDown(e) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.go(-1, 'swipe');
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.go(+1, 'swipe');
      }
    }

    onWindowKeyDown(e) {
      if (e.key === 'Escape' && this.isZoomed()) {
        this.exitZoom(this.getActiveImage());
      }
    }

    /* ---------------- external video ---------------- */

    playExternalVideo(wrap) {
      if (wrap.querySelector('iframe')) return;
      if (!wrap.dataset.originalHtml) wrap.dataset.originalHtml = wrap.innerHTML;

      const host = wrap.dataset.host;
      const id = wrap.dataset.externalId;
      let src = '';
      if (host === 'youtube') {
        src = `https://www.youtube.com/embed/${encodeURIComponent(id)}?autoplay=1&rel=0&playsinline=1`;
      } else if (host === 'vimeo') {
        src = `https://player.vimeo.com/video/${encodeURIComponent(id)}?autoplay=1`;
      }
      if (!src) return;

      const iframe = document.createElement('iframe');
      iframe.src = src;
      iframe.allow = 'autoplay; fullscreen; picture-in-picture; encrypted-media';
      iframe.setAttribute('allowfullscreen', '');
      iframe.className = 'fab-gallery__iframe';
      iframe.setAttribute('frameborder', '0');
      wrap.replaceChildren(iframe);
    }
  }

  /* ---------------- shared utils ---------------- */

  function distance(a, b) {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    return Math.hypot(dx, dy);
  }

  function midpoint(a, b) {
    return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  }

  customElements.define('fab-gallery', FabGallery);
})();

/**
 * fab-pdp-interactions.js
 * PDP engagement events (additive to data-feature exposure tracking).
 * Mirrors size_selector_* naming: snake_case events, product_id as string.
 */
(function () {
  'use strict';

  function capture(eventName, props) {
    try {
      if (window.__fabTrackingHelpers && typeof window.__fabTrackingHelpers.capture === 'function') {
        window.__fabTrackingHelpers.capture(eventName, props || {});
      }
    } catch (e) {}
  }

  function productIdFrom(el) {
    if (!el) return null;
    var root =
      el.closest('[data-product-id]') ||
      el.closest('product-info') ||
      document.querySelector('product-info') ||
      document.querySelector('[data-product-id]');
    if (!root) return null;
    var id =
      root.dataset.productId ||
      root.getAttribute('data-product-id') ||
      (root.querySelector && root.querySelector('[name="product-id"]')?.value);
    return id != null ? String(id) : null;
  }

  function currentSizeKey() {
    var checked =
      document.querySelector('input.fab-size-radio:checked') ||
      document.querySelector('[data-fab-size-key].is-selected') ||
      document.querySelector('.fab-size-pill--selected');
    if (!checked) return null;
    return (
      checked.dataset.fabSizeKey ||
      checked.getAttribute('data-fab-size-key') ||
      checked.value ||
      null
    );
  }

  function frameLabel(input) {
    if (!input) return null;
    var key = input.dataset.frameKey || input.closest('[data-frame-key]')?.dataset?.frameKey;
    if (!key) key = input.dataset.frameValue || input.value;
    if (!key) return null;
    var map = {
      unframed: 'Unframed',
      black: 'Black',
      white: 'White',
      oak: 'Oak',
      brown: 'Brown',
      silver: 'Silver',
      gold: 'Gold'
    };
    var lower = String(key).toLowerCase();
    return map[lower] || key;
  }

  function isUnframed(label) {
    return String(label || '').toLowerCase() === 'unframed';
  }

  /* ---------- Frame selector ---------- */
  function bindFrameSelector() {
    document.addEventListener(
      'change',
      function (event) {
        var input = event.target;
        if (!input || !input.classList || !input.classList.contains('fab-frame-radio')) return;
        // Ignore programmatic checks that aren't from user interaction
        if (!event.isTrusted) return;

        var fieldset = input.closest('fieldset') || input.closest('.fab-frame-layout');
        var previous = fieldset ? fieldset.dataset.fabPrevFrame || null : null;
        var next = frameLabel(input);
        if (!previous) {
          // First user change: previous is whatever was selected before this input
          var others = fieldset
            ? fieldset.querySelectorAll('input.fab-frame-radio')
            : [];
          // dataset may hold last committed frame from a prior change
          previous = fieldset?.dataset?.fabCommittedFrame || null;
        }

        capture('frame_selector_selected', {
          product_id: productIdFrom(input),
          frame: next,
          previous_frame: previous,
          is_framed_upgrade: isUnframed(previous) && !isUnframed(next)
        });

        if (fieldset) {
          fieldset.dataset.fabCommittedFrame = next || '';
          fieldset.dataset.fabPrevFrame = next || '';
        }
      },
      true
    );

    // Seed committed frame from initial checked radio (no event)
    document.querySelectorAll('fieldset.fab-frame-layout, .fab-frame-layout').forEach(function (fieldset) {
      var checked = fieldset.querySelector('input.fab-frame-radio:checked');
      if (checked) {
        var label = frameLabel(checked);
        fieldset.dataset.fabCommittedFrame = label || '';
        fieldset.dataset.fabPrevFrame = label || '';
      }
    });
  }

  /* ---------- Format switch (print ⇄ canvas) ---------- */
  function bindFormatSwitch() {
    document.addEventListener(
      'change',
      function (event) {
        var input = event.target;
        if (!input || input.name !== 'material_type') return;
        if (!event.isTrusted) return;

        var from =
          document.querySelector('.material_selection_item.active input[name="material_type"]')?.value ||
          null;
        // At change time the new input is already checked; derive "from" as the other option
        var to = input.value;
        if (from === to) {
          var all = document.querySelectorAll('input[name="material_type"]');
          all.forEach(function (el) {
            if (el !== input) from = el.value;
          });
        }

        var map = { poster: 'fine_art_print', canvas: 'premium_canvas' };
        capture('format_switched', {
          product_id: productIdFrom(input),
          from_format: map[from] || from,
          to_format: map[to] || to
        });
      },
      true
    );
  }

  /* ---------- Size guide / framing modal ---------- */
  function bindModalOpeners() {
    document.addEventListener(
      'click',
      function (event) {
        if (!event.isTrusted) return;
        var opener = event.target.closest('modal-opener');
        if (!opener) return;
        var modalSel = opener.getAttribute('data-modal') || '';
        var product_id = productIdFrom(opener);

        if (modalSel.indexOf('SizeGuideModal') !== -1) {
          capture('size_guide_opened', {
            product_id: product_id,
            current_size: currentSizeKey()
          });
          return;
        }
        if (modalSel.indexOf('FrameInfoModal') !== -1) {
          var trigger = event.target.closest('.fab-frame-layout, [data-frame-key]')
            ? 'pill_area'
            : 'link';
          capture('framing_modal_opened', {
            product_id: product_id,
            trigger: trigger
          });
        }
      },
      true
    );
  }

  /* ---------- See on wall / AR ---------- */
  function bindSeeOnWall() {
    document.addEventListener(
      'click',
      function (event) {
        if (!event.isTrusted) return;
        var cta = event.target.closest('[data-fab-app-ar-cta]');
        if (!cta) return;
        capture('see_on_wall_clicked', {
          product_id: productIdFrom(cta),
          current_size: currentSizeKey()
        });
      },
      true
    );
  }

  /* ---------- Accordions ---------- */
  function accordionSectionKey(details) {
    if (!details) return 'unknown';
    if (details.dataset.fabAccordionSection) return details.dataset.fabAccordionSection;
    var title = (details.querySelector('.accordion__title')?.textContent || '').trim().toLowerCase();
    var id = (details.id || '').toLowerCase();
    if (id.indexOf('description') !== -1 || /about this art|propos de cette|acerca de esta|über dieses|quest'opera|over dit kunst|om detta/.test(title)) {
      return 'about_this_art';
    }
    if (details.querySelector('#judgeme_product_reviews') || /review|avis|reseña|recensioni|beoordeling|omdöme/.test(title)) {
      return 'reviews';
    }
    if (/deliver|return|shipping|livraison|envío|lieferung|spedizione|verzending|leverans/.test(title)) {
      return 'delivery_returns';
    }
    if (/print|paper|frame|papier|cadre|marco|druck|stampa|afdruk|ram/.test(title)) {
      return 'printing_paper_frames';
    }
    return 'about_this_art';
  }

  function bindAccordions() {
    document.addEventListener(
      'toggle',
      function (event) {
        var details = event.target;
        if (!details || details.tagName !== 'DETAILS') return;
        if (!details.closest('.product__accordion')) return;
        if (!details.open) return; // expand only
        if (!event.isTrusted) return;

        capture('pdp_accordion_opened', {
          product_id: productIdFrom(details),
          section: accordionSectionKey(details)
        });
      },
      true
    );
  }

  /* ---------- Gallery (hooked from fab-product-gallery.js via global) ---------- */
  window.__fabTrackGalleryInteraction = function (interaction, mediaPosition, el) {
    capture('pdp_gallery_interaction', {
      product_id: productIdFrom(el || document.querySelector('fab-product-gallery')),
      interaction: interaction,
      media_position: mediaPosition != null ? Number(mediaPosition) : null
    });
  };

  function init() {
    bindFrameSelector();
    bindFormatSwitch();
    bindModalOpeners();
    bindSeeOnWall();
    bindAccordions();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

class ProductDrawer extends FabDrawer {
  connectedCallback() {
    super.connectedCallback();

    if (!this.id || this.id.indexOf('SizeGuideModal-') !== 0 || this._sizeGuidePreloadBound) return;
    this._sizeGuidePreloadBound = true;

    document
      .querySelectorAll('modal-opener[data-modal="#' + this.id + '"]')
      .forEach((opener) => {
        opener.addEventListener('pointerenter', () => this.loadDeferredSizeGuideImages(), { once: true });
      });
  }

  open(triggeredBy) {
    this.loadDeferredSizeGuideImages();
    super.open(triggeredBy);
  }

  loadDeferredSizeGuideImages() {
    this.querySelectorAll('img[data-size-guide-src]').forEach((image) => {
      image.src = image.dataset.sizeGuideSrc;
      image.removeAttribute('data-size-guide-src');
    });
  }
}

if (!customElements.get('product-drawer')) {
  customElements.define('product-drawer', ProductDrawer);
}

class ProductDrawerOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');
    if (!button) return;

    button.addEventListener('click', () => {
      const drawerId = this.getAttribute('data-drawer');
      if (!drawerId) return;
      const drawer = document.querySelector(drawerId);
      if (drawer && typeof drawer.open === 'function') {
        drawer.open(button);
      }
    });
  }
}

if (!customElements.get('product-drawer-opener')) {
  customElements.define('product-drawer-opener', ProductDrawerOpener);
}

/* ProductInfo + VariantSelects
   - All console logging removed
   - Removes duplicate/unused listeners
   - Keeps behaviour: VariantSelects emits `variant:change`, ProductInfo reacts and updates URL, price, media, inputs
*/

if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
        this.variantSelectsEl = this.querySelector('variant-selects');

        if (this.variantSelectsEl) {
          this.variantSelectsEl.addEventListener('variant:change', (event) =>
            this.onVariantChange(event)
          );
        }
      }

      connectedCallback() {
        if (!this.variantSelectsEl) {
          this.variantSelectsEl = this.querySelector('variant-selects');
          if (this.variantSelectsEl) {
            this.variantSelectsEl.addEventListener('variant:change', (event) =>
              this.onVariantChange(event)
            );
          }
        }

        this.initializeProductSwapUtility();

        // If you rely on Dawn-style pubsub, guard it so this element never hard-crashes
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.onVariantChangeUnsubscriber = subscribe(
            PUB_SUB_EVENTS.optionValueSelectionChange,
            this.handleOptionValueChange.bind(this)
          );

          if (
            !this.dataset.originalSection &&
            this.querySelector('.quantity__rules-cart, .quantity__rules')
          ) {
            this.cartUpdateUnsubscriber = subscribe(
              PUB_SUB_EVENTS.cartUpdate,
              this.fetchQuantityRules.bind(this)
            );
          }
        }

        this.initQuantityHandlers();

        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      disconnectedCallback() {
        try {
          this.onVariantChangeUnsubscriber?.();
        } catch (_) {}

        try {
          this.cartUpdateUnsubscriber?.();
        } catch (_) {}

        try {
          this.abortController?.abort();
        } catch (_) {}
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((el) => el.classList.add('scroll-trigger--cancel'))
        );

        this.postProcessHtmlCallbacks.push(() => {
          window?.Shopify?.PaymentButton?.init?.();
          window?.ProductModel?.loadShopifyXR?.();
        });
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
      }

      onVariantChange(event) {
        const variant = event?.detail;
        if (!variant) return;

        if (this.dataset.updateUrl !== 'false') {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        }

        const productUrl = this.dataset.url || window.location.pathname;
        const sectionId = this.dataset.originalSection || this.dataset.section;
        const sectionUrl = `${productUrl}?variant=${variant.id}&section_id=${sectionId}`;

        fetch(sectionUrl)
          .then((res) => res.text())
          .then((htmlText) => {
            const html = new DOMParser().parseFromString(htmlText, 'text/html');

            const sectionId = this.dataset.section;
            const sourcePriceContainer =
              html.querySelector(`#price-${sectionId} .price__container`) ||
              html.querySelector('.price__container');
            const mainPriceContainer = this.querySelector(`#price-${sectionId} .price__container`);
            const stickyPriceContainer = this.querySelector(`#price-sticky-${sectionId} .price__container`);

            if (sourcePriceContainer) {
              const inner = sourcePriceContainer.innerHTML;
              if (mainPriceContainer) mainPriceContainer.innerHTML = inner;
              if (stickyPriceContainer) stickyPriceContainer.innerHTML = inner;

              const sourcePriceWrapper =
                html.querySelector(`#price-${sectionId} .price`) || html.querySelector('.price');
              const mainPriceWrapper = this.querySelector(`#price-${sectionId} .price`);
              const stickyPriceWrapper = this.querySelector(`#price-sticky-${sectionId} .price`);
              if (sourcePriceWrapper && mainPriceWrapper) {
                mainPriceWrapper.className = sourcePriceWrapper.className;
              }
              if (sourcePriceWrapper && stickyPriceWrapper) {
                stickyPriceWrapper.className = sourcePriceWrapper.className;
              }
            }

            // Sync the add-to-cart button: its inner text now includes the
            // variant price (rendered server-side via Liquid's `money` filter,
            // so it respects the shop's currency / Shopify Markets), and its
            // disabled state can flip when switching to a sold-out variant.
            const sourceSubmit = html.querySelector(`#ProductSubmitButton-${this.dataset.section}`);
            const destinationSubmit = this.querySelector(`#ProductSubmitButton-${this.dataset.section}`);
            const isMidSubmit =
              destinationSubmit?.classList.contains('product-form__submit--adding');
            if (sourceSubmit && destinationSubmit && !isMidSubmit) {
              destinationSubmit.innerHTML = sourceSubmit.innerHTML;
              destinationSubmit.toggleAttribute('disabled', sourceSubmit.hasAttribute('disabled'));
            }

            this.dispatchEvent(
              new CustomEvent('product-info:variant-rendered', {
                detail: variant,
                bubbles: true,
              })
            );
          })
          .catch(() => {});

        // Update media + form variant inputs
        this.updateMediaForVariant(variant);
        this.updateVariantInputs(variant.id);
      }

      updateMediaForVariant(variant) {
        if (!variant?.featured_media?.id) return;

        const mediaGallery =
          this.querySelector(`#MediaGallery-${this.dataset.section}`) ||
          document.getElementById(`MediaGallery-${this.dataset.section}`);
        mediaGallery?.setActiveMedia?.(variant.featured_media.id);
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          if (!input) return;
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      // Dawn-style flow (kept, but guarded by existence checks)
      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!event || !this.contains(event.target)) return;
        if (!selectedOptionValues || !selectedOptionValues.length) return;

        const productUrl = target?.dataset?.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;

        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target?.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (!updateFullPage) {
            HTMLUpdateUtility?.viewTransition?.(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
            return;
          }

          const headTitle = document.querySelector('head title');
          const newHeadTitle = html.querySelector('head title');
          if (headTitle && newHeadTitle) headTitle.innerHTML = newHeadTitle.innerHTML;

          HTMLUpdateUtility?.viewTransition?.(
            document.querySelector('main'),
            html.querySelector('main'),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );
        };
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((res) => res.text())
          .then((text) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(text, 'text/html');
            callback(html);
          })
          .then(() => {
            if (targetId) document.querySelector(`#${targetId}`)?.focus();
          })
          .catch(() => {});
      }

      getSelectedVariant(productInfoNode) {
        if (!productInfoNode) return null;
        const el = productInfoNode.querySelector('variant-selects [data-selected-variant]');
        const raw = el?.textContent || el?.innerHTML;
        if (!raw) return null;

        try {
          return JSON.parse(raw);
        } catch (_) {
          return null;
        }
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];
        if (!shouldFetchFullPage) params.push(`section_id=${this.sectionId}`);
        if (optionValues?.length) params.push(`option_values=${optionValues.join(',')}`);
        return `${url}?${params.join('&')}`;
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl?.(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setQuantityBoundries() {
        if (!this.quantityInput) return;

        const cartQuantity = this.quantityInput.dataset.cartQuantity
          ? parseInt(this.quantityInput.dataset.cartQuantity, 10)
          : 0;

        const minRaw = this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min, 10) : 1;
        const maxRaw = this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max, 10) : null;
        const step = this.quantityInput.step ? parseInt(this.quantityInput.step, 10) : 1;

        let min = minRaw;
        const max = maxRaw === null ? null : maxRaw - cartQuantity;

        if (max !== null) min = Math.min(min, max);
        if (cartQuantity >= minRaw) min = Math.min(min, step);

        this.quantityInput.min = String(min);

        if (max !== null && max > 0) {
          this.quantityInput.max = String(max);
        } else {
          this.quantityInput.removeAttribute('max');
        }

        this.quantityInput.value = String(min);

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
        }
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        const spinner = this.querySelector('.quantity__rules-cart .loading__spinner');
        spinner?.classList?.remove('hidden');

        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((res) => res.text())
          .then((text) => {
            const html = new DOMParser().parseFromString(text, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch(() => {})
          .finally(() => spinner?.classList?.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput || !this.quantityForm) return;

        this.setQuantityBoundries();

        const updatedForm = html.getElementById(`Quantity-Form-${sectionId}`);
        if (!updatedForm) return;

        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];

        for (const selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = updatedForm.querySelector(selector);
          if (!current || !updated) continue;

          if (selector === '.quantity__input') {
            const attrs = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (const attr of attrs) {
              const v = updated.getAttribute(attr);
              if (v !== null) current.setAttribute(attr, v);
              else current.removeAttribute(attr);
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector('product-form');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}

if (!customElements.get('variant-selects')) {
  customElements.define(
    'variant-selects',
    class VariantSelects extends HTMLElement {
      constructor() {
        super();
        this.onChange = this.onChange.bind(this);
        this.variants = [];

        try {
          this.variants = JSON.parse(this.dataset.variants || '[]');
        } catch (_) {
          this.variants = [];
        }
      }

      connectedCallback() {
        this.addEventListener('change', this.onChange);
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onChange);
      }

      onChange() {
        const selectedOptions = Array.from(
          this.querySelectorAll('select, input[type="radio"]:checked')
        ).map((input) => input.value);

        const matchingVariant = this.variants.find((variant) => {
          const variantOptions = (variant.options || []).filter((o) => o !== null && o !== undefined && o !== '');
          return selectedOptions.every((sel, index) => sel === variantOptions[index]);
        });

        if (!matchingVariant) return;

        const selectedVariantEl = this.querySelector('[data-selected-variant]');
        if (selectedVariantEl) {
          selectedVariantEl.textContent = JSON.stringify(matchingVariant);
        }

        this.dispatchEvent(
          new CustomEvent('variant:change', {
            detail: matchingVariant,
            bubbles: true,
          })
        );
      }
    }
  );
}

/* ===========================================================================
   Wishlist sync — keep <wishlist-button> elements inside <product-info> in
   sync with the currently-selected variant. The button observes data-variant-id
   and re-renders automatically (filled / half / empty) when it changes.

   This is a delegated, document-level listener so it works regardless of how
   many <product-info> elements exist on the page (1 normally, possibly more
   in some preview/swatch scenarios).
   =========================================================================== */

if (!window.__fabWishlistVariantSyncInit) {
  window.__fabWishlistVariantSyncInit = true;

  document.addEventListener('variant:change', (event) => {
    const variant = event?.detail;
    // The variant:change event bubbles from <variant-selects>. Find the
    // enclosing <product-info> to scope updates correctly.
    const productInfo = event.target?.closest?.('product-info');
    if (!productInfo) return;

    productInfo.querySelectorAll('wishlist-button').forEach((btn) => {
      if (variant && variant.id) {
        btn.setAttribute('data-variant-id', String(variant.id));
      } else {
        btn.removeAttribute('data-variant-id');
      }
    });
  });
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}


if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        if (this._bound) return;
        this.form = this.querySelector('form');
        if (!this.form) return;
        this._bound = true;

        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        this.submitButton.removeAttribute('aria-disabled');
        this.submitButton.removeAttribute('disabled');
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        // Block double submits while waiting on the cart add request.
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        // Preserve current DOM (label + price etc.) — product-info updates this on variant changes.
        this.originalSubmitInnerHTML = this.submitButton.innerHTML;
        this.enterSubmitAddingState();

        this.sendCartAddRequest();
      }

      sendCartAddRequest() {
        this.syncQuickAddVariantInput();
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              const message = response.description || response.message || window.cartStrings?.error;
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(message);
              this.restoreSubmitButton();
              return;
            }

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            this.error = false;
            // Cart drawer / toast is confirmation — restore immediately (no secondary "Added" state).
            this.restoreSubmitButton();

            const quickAddModal = this.closest('quick-add-modal, fab-quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
              });
            }
          })
          .catch((e) => {
            console.error(e);
            const message = window.cartStrings?.error || e?.message || 'Unable to add this item to your cart.';
            this.handleErrorMessage(message);
            this.restoreSubmitButton();
          });
      }

      syncQuickAddVariantInput() {
        if (!this.closest('fab-quick-add-modal')) return;

        const productInfo = this.closest('product-info');
        const variantSelects = productInfo?.querySelector('variant-selects');
        if (!variantSelects) return;

        let variants = [];
        try {
          variants = JSON.parse(variantSelects.dataset.variants || '[]');
        } catch (_) {
          return;
        }

        const selectedByPosition = {};
        variantSelects
          .querySelectorAll('select[data-option-position], input[type="radio"][data-option-position]:checked')
          .forEach((control) => {
            const position = Number(control.dataset.optionPosition);
            if (position > 0) selectedByPosition[position] = control.value;
          });

        const selectedVariant = variants.find((variant) => {
          const options = Array.isArray(variant.options) ? variant.options : [];
          return (
            options.length > 0 &&
            options.every(
              (value, index) =>
                selectedByPosition[index + 1] !== undefined &&
                String(selectedByPosition[index + 1]) === String(value)
            )
          );
        });

        if (selectedVariant && this.variantIdInput) {
          this.variantIdInput.value = selectedVariant.id;
        }
      }

      enterSubmitAddingState() {
        const labelEl = this.submitButton.querySelector('.product-form__submit-label');
        const priceEl = this.submitButton.querySelector('.product-form__submit-price');
        const sepEl = this.submitButton.querySelector('.product-form__submit-separator');

        this.submitButton.classList.add('product-form__submit--adding');
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.setAttribute('aria-busy', 'true');
        const adding =
          typeof window.variantStrings?.adding === 'string' ? window.variantStrings.adding : 'Adding\u2026';
        if (labelEl) labelEl.textContent = adding;
        if (priceEl) priceEl.hidden = true;
        if (sepEl) sepEl.hidden = true;
      }

      restoreSubmitButton() {
        this.submitButton.classList.remove('product-form__submit--adding');
        this.submitButton.removeAttribute('aria-disabled');
        this.submitButton.removeAttribute('aria-busy');
        if (this.originalSubmitInnerHTML) {
          this.submitButton.innerHTML = this.originalSubmitInnerHTML;
          this.originalSubmitInnerHTML = null;
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        this.submitButton.removeAttribute('disabled');
        this.submitButton.removeAttribute('aria-disabled');
        this.submitButtonText.textContent = window.variantStrings.addToCart;
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}

/**
 * fab-size-key.js
 *
 * Orientation-independent size keys for the collapsed-tabbed size picker.
 * Used to match Shopify option values against the configurable Popular list.
 *
 * Standard sizes → smaller×larger inch pair, e.g. "16x20".
 * A-sizes → "A5" … "A0" (uppercase).
 */
(function (root, factory) {
  'use strict';

  var FabSizeKey = factory();

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = FabSizeKey;
  }
  root.FabSizeKey = FabSizeKey;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  function normX(str) {
    return String(str || '')
      .replace(/\u00d7/g, 'x')
      .replace(/×/g, 'x')
      .replace(/″/g, '"')
      .replace(/"/g, '')
      .trim();
  }

  /**
   * Parse "WxH" from a dimension fragment (cm or in).
   * @returns {{ w: number, h: number } | null}
   */
  function parseDimPair(fragment) {
    if (!fragment) return null;
    var m = normX(fragment).match(/(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)/i);
    if (!m) return null;
    var w = parseFloat(m[1]);
    var h = parseFloat(m[2]);
    if (!w || !h) return null;
    return { w: w, h: h };
  }

  /**
   * Normalise WxH to smaller×larger integer pair string, e.g. "16x20".
   */
  function normalizeDimKey(w, h) {
    var a = Math.round(w);
    var b = Math.round(h);
    if (a <= b) return a + 'x' + b;
    return b + 'x' + a;
  }

  /**
   * Derive a stable popular-matching key from a Shopify Size option value.
   *
   * @param {string} raw — e.g. "40x50cm / 16x20in", "50x40cm / 20x16in", "A4 / A4"
   * @returns {string} — e.g. "16x20", "A4", or "" if unknown
   */
  function normalizePopularKey(raw) {
    if (!raw) return '';

    var parts = String(raw).split('/').map(function (p) {
      return p.trim();
    });
    var cmPart = parts[0] || '';
    var inPart = parts[1] || '';

    var upper = cmPart.toUpperCase();
    var aMatch = upper.match(/^A([0-7])\b/);
    if (aMatch) return 'A' + aMatch[1];

  // Also check inPart / full string for A-sizes like "A4 / A4"
    if (!aMatch && inPart) {
      var inUpper = inPart.toUpperCase();
      var aMatch2 = inUpper.match(/^A([0-7])\b/);
      if (aMatch2) return 'A' + aMatch2[1];
    }

    var inPair = parseDimPair(inPart);
    if (inPair) return normalizeDimKey(inPair.w, inPair.h);

    var cmPair = parseDimPair(cmPart);
    if (cmPair) {
      var cmShort = Math.round(Math.min(cmPair.w, cmPair.h));
      var cmLong = Math.round(Math.max(cmPair.w, cmPair.h));
      if (cmShort === 30 && cmLong === 60) return '12x24';
      if (cmShort === 50 && cmLong === 100) return '20x40';
      if (cmShort === 75 && cmLong === 150) return '30x60';

      // Convert cm to inches for canonical key (matches merchandising ladder).
      var wIn = Math.round((cmPair.w / 2.54) * 10) / 10;
      var hIn = Math.round((cmPair.h / 2.54) * 10) / 10;
      return normalizeDimKey(wIn, hIn);
    }

    return '';
  }

  /**
   * True when a product option value matches a configured popular key.
   */
  function valueMatchesPopularKey(raw, popularKey) {
    if (!raw || !popularKey) return false;
    return normalizePopularKey(raw).toUpperCase() === String(popularKey).trim().toUpperCase();
  }

  /**
   * True for 36×48 in (and metric equivalents such as 90×120 cm).
   */
  function isXxlSizeKey(raw) {
    if (!raw) return false;
    if (normalizePopularKey(raw) === '36x48') return true;

    var s = String(raw)
      .toLowerCase()
      .replace(/\u00d7/g, 'x')
      .replace(/×/g, 'x')
      .trim();

    var cmMatch = s.match(/(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)\s*cm/);
    if (cmMatch) {
      var cw = Math.round(parseFloat(cmMatch[1], 10));
      var ch = Math.round(parseFloat(cmMatch[2], 10));
      if ((cw === 90 && ch === 120) || (cw === 120 && ch === 90)) return true;
    }

    if (/90\s*x\s*120|120\s*x\s*90/.test(s)) return true;
    if (/36\s*x\s*48|48\s*x\s*36/.test(s)) return true;

    return false;
  }

  return {
    normalizePopularKey: normalizePopularKey,
    valueMatchesPopularKey: valueMatchesPopularKey,
    isXxlSizeKey: isXxlSizeKey,
    parseDimPair: parseDimPair,
    normalizeDimKey: normalizeDimKey,
  };
});

(function () {
  function fm(cents) {
    // Try to use Shopify's formatMoney with the theme's money format
    if (window.Shopify && window.Shopify.formatMoney) {
      // Get the money format from theme settings if available
      const moneyFormat = window.theme?.moneyFormat || '${{amount}}';
      return Shopify.formatMoney(cents, moneyFormat);
    }
    
    // Fallback: use the currency from the main product price
    const mainPrice = document.querySelector('.price .price-item');
    if (mainPrice) {
      const format = mainPrice.textContent.replace(/[\d\s.,]/g, '');
      const amount = (cents / 100).toFixed(2);
      if (format && !/free/i.test(format)) return format + amount;
    }
    
    // Ultimate fallback
    return '$' + (cents / 100).toFixed(2);
  }

  function getVariants(vs) {
    try { return JSON.parse(vs.dataset.variants || '[]'); } catch { return []; }
  }

  function getCurrentSizeFromDetail(detail) {
    return detail && Array.isArray(detail.options) ? detail.options[0] : null;
  }

  function getCurrentSizeFromDom(vs) {
    // first select (Size/Taille) value, or selected_or_first_available from script
    const sizeSelect = vs.querySelector('select[name*="Size"], select[name*="size"], select[name*="Taille"], select[name*="taille"]');
    if (sizeSelect) return sizeSelect.value;
    try {
      const selected = JSON.parse(vs.querySelector('[data-selected-variant]')?.textContent || '{}');
      return selected?.options?.[0] || null;
    } catch { return null; }
  }

  function readFrameName(swatchEl) {
    const input = swatchEl.querySelector('input[type=radio]');
    if (input && input.value) return input.value;
    const label = swatchEl.querySelector('label');
    if (label?.title) return label.title;
    return null;
  }

  function ensurePriceEl(swatchEl) {
    let el = swatchEl.querySelector('.swatch-additional-price');
    if (!el) {
      el = document.createElement('div');
      el.className = 'swatch-additional-price';
      const nameEl = swatchEl.querySelector('.swatch-variant-name') || swatchEl.querySelector('label');
      (nameEl || swatchEl).after ? nameEl.after(el) : swatchEl.appendChild(el);
    }
    return el;
  }

  function localeCode() {
    const lang = document.documentElement.lang || '';
    const two = lang.split('-')[0].toLowerCase();
    return two || 'en';
  }

  function canonicalize(name) {
    if (!name) return null;
    const v = String(name).toLowerCase().trim();
    if (['unframed','none','sans','sans cadre','non encadré','sin','sin marco','ohne','ohne rahmen','senza','senza cornice','zonder','zonder lijst','utan','utan ram'].includes(v)) return 'unframed';
    if (['black','noir','schwarz','nero','zwart','svart','negro'].includes(v)) return 'black';
    if (['white','blanc','weiß','weiss','bianco','wit','vit','blanco'].includes(v)) return 'white';
    if (['silver','argent','argenté','silber','argento','zilver','plata','plateado'].includes(v)) return 'silver';
    if (['gold','or','doré','oro','goud','guld','dorado'].includes(v)) return 'gold';
    if (['brown','marron','marrón','marrone','braun','bruin','brun'].includes(v)) return 'brown';
    if (['oak','chêne','roble','eiche','rovere','eiken','eik','ek'].includes(v)) return 'oak';
    return null;
  }

  function localizedLabel(canonical, loc) {
    if (!canonical) return null;
    const map = {
      fr: { unframed: 'Sans', black: 'Noir', white: 'Blanc', oak: 'Chêne', brown: 'Marron', silver: 'Argenté', gold: 'Doré' },
      es: { unframed: 'Sin', black: 'Negro', white: 'Blanco', oak: 'Roble', brown: 'Marrón', silver: 'Plateado', gold: 'Dorado' },
      de: { unframed: 'Ohne', black: 'Schwarz', white: 'Weiß', oak: 'Eiche', brown: 'Braun', silver: 'Silber', gold: 'Gold' },
      it: { unframed: 'Senza', black: 'Nero', white: 'Bianco', oak: 'Rovere', brown: 'Marrone', silver: 'Argento', gold: 'Oro' },
      nl: { unframed: 'Zonder', black: 'Zwart', white: 'Wit', oak: 'Eiken', brown: 'Bruin', silver: 'Zilver', gold: 'Goud' },
      sv: { unframed: 'Utan', black: 'Svart', white: 'Vit', oak: 'Ek', brown: 'Brun', silver: 'Silver', gold: 'Guld' },
      en: { unframed: 'Unframed', black: 'Black', white: 'White', oak: 'Oak', brown: 'Brown', silver: 'Silver', gold: 'Gold' }
    };
    const dict = map[loc] || map.en;
    return dict[canonical] || null;
  }

  function localizeSwatchNames(vs) {
    const loc = localeCode();
    vs.querySelectorAll('fieldset.product-form__input--swatch .swatch-input').forEach(swatchEl => {
      const title = swatchEl.querySelector('label')?.title;
      const canonical = canonicalize(title);
      const target = swatchEl.querySelector('.swatch-variant-name');
      const localized = localizedLabel(canonical, loc);
      if (target && localized) {
        target.textContent = localized;
      }
    });
  }

  function isUnframedStr(s) {
    if (!s) return false;
    const v = String(s).toLowerCase().trim();
    return [
      'unframed','sans','sans cadre','non encadré',
      'sin','sin marco','ohne','ohne rahmen',
      'senza','senza cornice','zonder','zonder lijst',
      'utan','utan ram'
    ].includes(v);
  }

  function getSizeSelect(vs) {
    // Find the size select element with locale support
    return vs.querySelector('select[name*="Size"], select[name*="size"], select[name*="Taille"], select[name*="taille"], select[name*="Größe"], select[name*="grosse"], select[name*="Tamaño"], select[name*="Dimensione"], select[name*="Maat"], select[name*="Storlek"]');
  }

  function getSelectedFrame(vs) {
    // Find the currently selected frame from swatch inputs
    const checkedSwatch = vs.querySelector('fieldset.product-form__input--swatch input[type="radio"]:checked');
    if (checkedSwatch) return checkedSwatch.value;
    return null;
  }

  function extractSizeFromOption(optionText) {
    // Extract the size part from option text like "70x100cm - $149.00" or "70x100cm (Most Popular) - $149.00"
    // Return just the raw size value that matches the variant option
    const dashIndex = optionText.lastIndexOf(' - ');
    if (dashIndex > -1) {
      return optionText.substring(0, dashIndex).trim();
    }
    return optionText.trim();
  }

  function updateSizeDropdownPrices(vs) {
    const variants = getVariants(vs);
    if (!variants.length) return;

    const sizeSelect = getSizeSelect(vs);
    if (!sizeSelect) return;

    const selectedFrame = getSelectedFrame(vs);
    const frameCanon = canonicalize(selectedFrame);

    sizeSelect.querySelectorAll('option').forEach(option => {
      const sizeValue = option.value;
      if (!sizeValue) return;

      const sizeLower = sizeValue.toLowerCase().trim();

      // Find the variant that matches this size and the current frame
      const matchingVariant = variants.find(v => {
        const o0 = String(v.options?.[0] || '').toLowerCase().trim();
        const o1 = String(v.options?.[1] || '').toLowerCase().trim();
        const hasSize = o0 === sizeLower || o1 === sizeLower;
        
        if (!frameCanon || frameCanon === 'unframed') {
          // If no frame or unframed selected, find the unframed variant
          const hasUnframed = isUnframedStr(o0) || isUnframedStr(o1);
          return hasSize && hasUnframed;
        } else {
          // Find variant with this size and the selected frame
          const hasFrame = o0 === frameCanon || o1 === frameCanon;
          return hasSize && hasFrame;
        }
      });

      if (matchingVariant) {
        // Get the display text for this size (preserving labels like "Most Popular")
        const originalText = option.dataset.originalText || option.textContent;
        if (!option.dataset.originalText) {
          // Store the size display text without price
          const displayText = extractSizeFromOption(originalText);
          option.dataset.originalText = displayText;
        }
        
        // Update option text with new price
        option.textContent = `${option.dataset.originalText} - ${fm(matchingVariant.price)}`;
      }
    });
  }

  function recalcFor(vs, explicitSize) {
    const variants = getVariants(vs);
    if (!variants.length) return;

    const size = (explicitSize || getCurrentSizeFromDom(vs) || '').toLowerCase().trim();
    if (!size) return;


    // find unframed variant for this size (regardless of option position)
    const unframed = variants.find(v => {
      const o0 = String(v.options?.[0] || '').toLowerCase().trim();
      const o1 = String(v.options?.[1] || '').toLowerCase().trim();
      const isUnframed = isUnframedStr(o0) || isUnframedStr(o1);
      const hasSize = o0 === size || o1 === size;
      return isUnframed && hasSize;
    });
    


    // More robust selector to find frame swatches in all languages
    const frameSwatches = vs.querySelectorAll('fieldset.product-form__input--swatch .swatch-input, fieldset.product-form__input--couleur .swatch-input, fieldset.product-form__input--color .swatch-input, fieldset.product-form__input--frame .swatch-input');
    

    frameSwatches.forEach(swatchEl => {
      const frameRaw = readFrameName(swatchEl);
      const frameCanon = canonicalize(frameRaw);
    
      // Print-only / unframed: keep the price row in the layout (same card
      // height and width as framed options). Do not use display:none — that
      // collapses the row and shrinks the card in the editorial grid.
      if (!frameCanon || frameCanon === 'unframed') {
        const existingPriceEl = swatchEl.querySelector('.swatch-additional-price');
        if (existingPriceEl) {
          existingPriceEl.style.display = '';
          existingPriceEl.style.visibility = '';
          existingPriceEl.innerHTML = '';
          existingPriceEl.textContent = '\u00A0';
        }
        return;
      }
    
      const priceEl = ensurePriceEl(swatchEl);
      priceEl.style.display = '';

      // find framed variant for this size (order-agnostic)
      const framed = variants.find(v => {
        const o0 = String(v.options?.[0] || '').toLowerCase().trim();
        const o1 = String(v.options?.[1] || '').toLowerCase().trim();
        const frameLC = frameCanon;
        const hasSize = o0 === size || o1 === size;
        const hasFrame = o0 === frameLC || o1 === frameLC;
        return hasSize && hasFrame;
      });

      if (!framed) {

        priceEl.style.visibility = 'hidden';
        priceEl.textContent = '';
        return;
      }



      const add = framed.price - unframed.price;
      if (add > 0) {
        priceEl.style.visibility = 'visible';
        priceEl.innerHTML = `<span class="swatch-additional-price__current">+${fm(add)}</span>`;

      } else {
        priceEl.style.visibility = 'hidden';
        priceEl.textContent = '';

      }
    });

    // ensure labels stay localized - but don't interfere with prices
    localizeSwatchNames(vs);
    
    // Re-apply prices after localization to ensure they don't get overwritten
    setTimeout(() => {
      frameSwatches.forEach(swatchEl => {
        const frameRaw = readFrameName(swatchEl);
        const frameCanon = canonicalize(frameRaw);
        const priceEl = swatchEl.querySelector('.swatch-additional-price');
        
        if (!priceEl) return;
        if (!frameCanon || frameCanon === 'unframed') {
          priceEl.style.display = '';
          priceEl.style.visibility = '';
          priceEl.innerHTML = '';
          priceEl.textContent = '\u00A0';
          return;
        }

        priceEl.style.display = '';

        const framed = variants.find(v => {
          const o0 = String(v.options?.[0] || '').toLowerCase().trim();
          const o1 = String(v.options?.[1] || '').toLowerCase().trim();
          const frameLC = frameCanon;
          const hasSize = o0 === size || o1 === size;
          const hasFrame = o0 === frameLC || o1 === frameLC;
          return hasSize && hasFrame;
        });

        if (framed) {
          const add = framed.price - unframed.price;
          if (add > 0) {
            priceEl.style.visibility = 'visible';
            priceEl.innerHTML = `<span class="swatch-additional-price__current">+${fm(add)}</span>`;
          }
        }
      });
    }, 100);
  }

  function bind(vs) {
    if (!vs.__frameUpdaterBound) {
      // Recalc using DOM on change to avoid relying on option index order
      vs.addEventListener('variant:change', () => {
        recalcFor(vs);
        updateSizeDropdownPrices(vs);
      });
      
      // Also listen for direct swatch changes to update size prices immediately
      const swatchInputs = vs.querySelectorAll('fieldset.product-form__input--swatch input[type="radio"]');
      swatchInputs.forEach(input => {
        input.addEventListener('change', () => {
          // Small delay to ensure the variant:change event has processed
          setTimeout(() => updateSizeDropdownPrices(vs), 50);
        });
      });
      
      vs.__frameUpdaterBound = true;
    }
    recalcFor(vs);
    localizeSwatchNames(vs);
    updateSizeDropdownPrices(vs);
  }

  function initAll() {
    document.querySelectorAll('variant-selects').forEach(bind);
  }

  document.addEventListener('DOMContentLoaded', initAll);
  document.addEventListener('product-info:loaded', initAll);
})();

/**
 * fab-frame-layouts.js (v3)
 *
 * Behaviour for the editorial frame picker layout.
 *
 * Each visible card wraps a real <label for="radio-id"> so clicking
 * triggers the radio natively. This JS listens for the radio's
 *   `change` event to update the selected-class on cards, then lets
 *   `product-form.js` and `frame-price-updater.js` do the rest.
 *
 * Show-more button:
 *   Reveals a separate tail grid so default-visible swatches do not reflow.
 */

(function () {
  'use strict';

  function init(root) {
    const fieldsets = root.querySelectorAll('.fab-frame-layout');
    fieldsets.forEach(initFieldset);
    if (fieldsets.length) {
      bindXxlFrameBySizeListeners();
      refreshAllXxlFrames();
    }
  }


  function initFieldset(fieldset) {
    if (fieldset.dataset.fabInit === '1') return;
    fieldset.dataset.fabInit = '1';

    const layout = fieldset.dataset.layout || 'editorial';

    // Sync selected-class whenever a frame radio changes (labels handle the click natively)
    fieldset.addEventListener('change', function (e) {
      const target = e.target;
      if (!target || target.tagName !== 'INPUT') return;
      if (!target.classList.contains('fab-frame-radio')) return;
      syncSelectedClass(fieldset, target);
    });

    // Editorial layout behaviour
    if (layout === 'editorial') {
      initShowMoreToggle(fieldset);
    }

    initXxlFrameBySize(fieldset);
  }

  var XXL_SIZE_KEY = '36x48';
  var BOX_SIZE_KEY = '30x60';
  var XXL_ALLOWED_FRAME_KEYS = {
    unframed: true,
    black: true,
    white: true,
    oak: true,
  };

  /**
   * Art prints only: at 90×120 / 36×48 (portrait or landscape), only
   * unframed plus black, white, and oak frames are offered.
   * Canvas products are unchanged.
   */
  function getVariantSelectsRoot(fieldset) {
    return fieldset.closest('variant-selects') || fieldset.closest('product-info');
  }

  function normalizeSizeKey(raw) {
    if (!raw) return '';
    if (window.FabSizeKey && typeof window.FabSizeKey.isXxlSizeKey === 'function') {
      if (window.FabSizeKey.isXxlSizeKey(raw)) return XXL_SIZE_KEY;
    }
    if (window.FabSizeKey && typeof window.FabSizeKey.normalizePopularKey === 'function') {
      var popularKey = window.FabSizeKey.normalizePopularKey(raw);
      if (popularKey) return popularKey;
    }
    return '';
  }

  function is36x48Size(raw) {
    if (window.FabSizeKey && typeof window.FabSizeKey.isXxlSizeKey === 'function') {
      return window.FabSizeKey.isXxlSizeKey(raw);
    }
    return normalizeSizeKey(raw) === XXL_SIZE_KEY;
  }

  function is30x60Size(raw) {
    if (!raw) return false;
    if (normalizeSizeKey(raw) === BOX_SIZE_KEY) return true;
    var s = String(raw)
      .toLowerCase()
      .replace(/\u00d7/g, 'x')
      .replace(/×/g, 'x')
      .replace(/\s+/g, '');
    return (
      /(?:30x60|60x30)in\b/.test(s) ||
      /(?:75x150|150x75)cm\b/.test(s)
    );
  }

  function variantIncludes36x48(variant) {
    if (!variant || !Array.isArray(variant.options)) return false;
    return variant.options.some(function (opt) {
      return is36x48Size(opt);
    });
  }

  function variantIncludes30x60(variant) {
    if (!variant || !Array.isArray(variant.options)) return false;
    return variant.options.some(function (opt) {
      return is30x60Size(opt);
    });
  }

  function isXxlAllowedFrameKey(key) {
    return !!XXL_ALLOWED_FRAME_KEYS[String(key || '').toLowerCase()];
  }

  function getSelectedSizeValue(fieldset) {
    var root = getVariantSelectsRoot(fieldset) || document;

    var selectedCell = root.querySelector(
      '[data-fab-size-cell].fab-size__cell--selected, [data-fab-size-cell].fab-size__asize-cell--selected'
    );
    if (selectedCell && selectedCell.dataset.fabSizeValue) {
      return selectedCell.dataset.fabSizeValue;
    }

    var fabSelect = root.querySelector('.fab-size__select-shim');
    if (fabSelect && fabSelect.value) return fabSelect.value;

    var sizeSelect = root.querySelector(
      'select[name*="Size"], select[name*="size"], select[name*="Taille"], select[name*="taille"], select[name*="Größe"], select[name*="grosse"], select[name*="Tamaño"], select[name*="Dimensione"], select[name*="Maat"], select[name*="Storlek"]'
    );
    if (sizeSelect && sizeSelect.value) return sizeSelect.value;

    try {
      var selected = root.querySelector('[data-selected-variant]');
      if (selected) {
        var variant = JSON.parse(selected.textContent || '{}');
        if (variant && Array.isArray(variant.options)) {
          for (var i = 0; i < variant.options.length; i++) {
            if (is36x48Size(variant.options[i]) || is30x60Size(variant.options[i])) {
              return variant.options[i];
            }
          }
          if (variant.options[0]) return variant.options[0];
        }
      }
    } catch (_) {}

    return '';
  }

  function setRestrictedFrames(fieldset, restrict) {
    fieldset.querySelectorAll('[data-frame-card][data-frame-key]').forEach(function (card) {
      var key = (card.getAttribute('data-frame-key') || '').toLowerCase();
      var hide = restrict && !isXxlAllowedFrameKey(key);
      card.classList.toggle('fab-frame-card--size-hidden', hide);
      card.toggleAttribute('hidden', hide);
      if (hide) card.setAttribute('aria-hidden', 'true');
      else card.removeAttribute('aria-hidden');
      var radio = card.querySelector('input.fab-frame-radio');
      if (radio) radio.disabled = hide;
    });
    updateMoreToggleForXxl(fieldset);
  }

  function setBoxFrameLabels(fieldset, useBoxLabel) {
    fieldset.querySelectorAll('[data-frame-card]').forEach(function (card) {
      var key = (card.getAttribute('data-frame-key') || '').toLowerCase();
      if (key === 'unframed') return;
      var benefit = card.querySelector(
        '.fab-frame-pill-benefit[data-frame-standard-label][data-frame-box-label]'
      );
      if (!benefit) return;
      benefit.textContent = useBoxLabel
        ? benefit.dataset.frameBoxLabel
        : benefit.dataset.frameStandardLabel;
    });
  }

  function updateMoreToggleForXxl(fieldset) {
    var tail = fieldset.querySelector('[data-frame-more-tail]');
    var toggle = fieldset.querySelector('[data-frame-more-toggle]');
    if (!tail || !toggle) return;

    var visibleTail = tail.querySelectorAll(
      '[data-frame-card]:not(.fab-frame-card--size-hidden)'
    );
    var hideToggle = visibleTail.length === 0;
    toggle.toggleAttribute('hidden', hideToggle);
    if (hideToggle) {
      toggle.setAttribute('aria-hidden', 'true');
      toggle.setAttribute('aria-expanded', 'false');
      var layout = fieldset.dataset.layout || '';
      var tailCollapsedClassMap = {
        editorial: 'fab-frame-editorial__tail--collapsed',
      };
      var expandedClassMap = {
        editorial: 'fab-frame-editorial__more--expanded',
      };
      var collapsedClass = tailCollapsedClassMap[layout];
      var expandedClass = expandedClassMap[layout];
      if (collapsedClass) tail.classList.add(collapsedClass);
      if (expandedClass) toggle.classList.remove(expandedClass);
      var collapsedText = toggle.querySelector('[data-frame-more-collapsed]');
      var expandedText = toggle.querySelector('[data-frame-more-expanded]');
      var pips = toggle.querySelector('[data-frame-more-pips]');
      if (collapsedText) collapsedText.style.display = '';
      if (expandedText) expandedText.style.display = 'none';
      if (pips) pips.style.display = '';
    } else {
      toggle.removeAttribute('aria-hidden');
    }
  }

  function deselectRestrictedIfNeeded(fieldset) {
    var checked = fieldset.querySelector('input.fab-frame-radio:checked');
    var card = checked && checked.closest('[data-frame-card]');
    if (!card || !card.classList.contains('fab-frame-card--size-hidden')) return;

    var fallback =
      fieldset.querySelector(
        '[data-frame-key="black"]:not(.fab-frame-card--size-hidden) input.fab-frame-radio'
      ) ||
      fieldset.querySelector(
        '[data-frame-key="white"]:not(.fab-frame-card--size-hidden) input.fab-frame-radio'
      ) ||
      fieldset.querySelector(
        '[data-frame-key="oak"]:not(.fab-frame-card--size-hidden) input.fab-frame-radio'
      ) ||
      fieldset.querySelector(
        '[data-frame-card]:not(.fab-frame-card--size-hidden) input.fab-frame-radio'
      );

    if (!fallback || fallback === checked) return;

    fallback.checked = true;
    fallback.dispatchEvent(new Event('change', { bubbles: true }));
    syncSelectedClass(fieldset, fallback);
  }

  function applyXxlFrameBySize(fieldset, variant) {
    if (fieldset.classList.contains('fab-frame-layout--canvas-product')) return;

    var restrictXxl =
      (variant && variantIncludes36x48(variant)) ||
      is36x48Size(getSelectedSizeValue(fieldset));
    var isWideOrTall =
      fieldset.classList.contains('fab-frame-layout--art-wide') ||
      fieldset.classList.contains('fab-frame-layout--art-tall');
    var restrictBox =
      isWideOrTall &&
      ((variant && variantIncludes30x60(variant)) ||
        is30x60Size(getSelectedSizeValue(fieldset)));
    var restrict = restrictXxl || restrictBox;

    setRestrictedFrames(fieldset, restrict);
    setBoxFrameLabels(fieldset, restrictBox);
    if (restrict) deselectRestrictedIfNeeded(fieldset);
  }

  function refreshAllXxlFrames(variant) {
    document.querySelectorAll('.fab-frame-layout').forEach(function (fieldset) {
      applyXxlFrameBySize(fieldset, variant);
    });
  }

  function bindXxlFrameBySizeListeners() {
    if (window.__fabXxlFrameBySizeBound) return;
    window.__fabXxlFrameBySizeBound = true;

    function scheduleRefresh(variant) {
      requestAnimationFrame(function () {
        refreshAllXxlFrames(variant);
      });
    }

    document.addEventListener('variant:change', function (e) {
      scheduleRefresh(e.detail);
    });

    document.addEventListener(
      'change',
      function (e) {
        var target = e.target;
        if (!target) return;
        if (
          (target.classList && target.classList.contains('fab-size__select-shim')) ||
          (target.tagName === 'SELECT' &&
            /size|taille|größe|grosse|tamaño|dimensione|maat|storlek/i.test(target.name || ''))
        ) {
          scheduleRefresh();
        }
      },
      true
    );

    document.addEventListener(
      'click',
      function (e) {
        if (e.target.closest('[data-fab-size-cell]')) scheduleRefresh();
      },
      true
    );
  }

  function initXxlFrameBySize(fieldset) {
    applyXxlFrameBySize(fieldset);
    bindXxlFrameBySizeListeners();
  }

  /**
   * Move the layout's selected class to the card wrapping the newly-checked radio.
   */
  function syncSelectedClass(fieldset, radio) {
    const layout = fieldset.dataset.layout;
    const selectedClassMap = {
      editorial: 'fab-frame-editorial__card--selected',
    };
    const selectedClass = selectedClassMap[layout];
    if (!selectedClass) return;

    fieldset
      .querySelectorAll('[data-frame-card].' + selectedClass)
      .forEach(function (el) { el.classList.remove(selectedClass); });

    const newSelectedCard = radio.closest('[data-frame-card]');
    if (newSelectedCard) newSelectedCard.classList.add(selectedClass);
  }

  /**
   * Editorial: reveal a separate tail grid so the
   * default-visible swatches keep their positions (no CSS grid reflow).
   */
  function initShowMoreToggle(fieldset) {
    const toggle = fieldset.querySelector('[data-frame-more-toggle]');
    const tail = fieldset.querySelector('[data-frame-more-tail]');
    if (!toggle || !tail) return;

    const layout = fieldset.dataset.layout;
    const tailCollapsedClassMap = {
      editorial: 'fab-frame-editorial__tail--collapsed',
    };
    const expandedClassMap = {
      editorial: 'fab-frame-editorial__more--expanded',
    };
    const tailCollapsedClass = tailCollapsedClassMap[layout];
    const expandedClass = expandedClassMap[layout];

    const collapsedText = toggle.querySelector('[data-frame-more-collapsed]');
    const expandedText = toggle.querySelector('[data-frame-more-expanded]');
    const pips = toggle.querySelector('[data-frame-more-pips]');

    toggle.addEventListener('click', function () {
      const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
      const next = !isExpanded;
      toggle.setAttribute('aria-expanded', next ? 'true' : 'false');

      if (tailCollapsedClass) {
        tail.classList.toggle(tailCollapsedClass, !next);
      }

      if (collapsedText) collapsedText.style.display = next ? 'none' : '';
      if (expandedText) expandedText.style.display = next ? '' : 'none';
      if (pips) pips.style.display = next ? 'none' : '';

      if (next) toggle.classList.add(expandedClass);
      else toggle.classList.remove(expandedClass);
    });
  }

  if (document.readyState !== 'loading') {
    init(document);
  } else {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  }

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
  document.addEventListener('product-info:loaded', function (e) {
    init(e.target || document);
  });
})();

/**
 * fab-size-picker.js
 *
 * Drives the new size picker. Two responsibilities:
 *
 *  1. CONTRACT — Translate pill clicks into a real <select>.value change
 *     plus a `change` event, so product-form.js / frame-price-updater.js /
 *     the rest of the variant pipeline keep working unchanged.
 *
 *  2. AESTHETICS — Smooth, Apple/Airbnb-style transitions:
 *       - The room mock expands by measuring its natural height first
 *         (FLIP-ish), animating to that exact number, then snapping to
 *         auto so subsequent layout changes don't cause re-animation.
 *       - CSS placeholder scene swaps cross-fade rather than yank.
 *       - The print width is set as a % of the stage so it matches the
 *         physical ratio print-width (cm) / assumed wall width (cm) for
 *         each scene. The art image is an inner <img> fed from the matching
 *         variant's featured_image (see data-variants on variant-selects).
 *       - The readout pill is positioned from measured print geometry so it
 *         sits just above the print's top edge.
 *
 * Scene mapping is by raw cm-width — see SCENE_FOR_CM.
 *
 * ROOM COMPOSITOR — when a real room library exists (metaobject-backed),
 * the canvas compositor (fab-room-compositor.js) draws a photographed room
 * with the artwork composited in at honest scale. If the compositor isn't
 * loaded, the library is empty, or any image fails, the picker silently
 * falls back to the CSS placeholder scenes below.
 */

(function () {
  'use strict';

  // ============================================================
  // Scene mapping
  // ============================================================
  // Tuned by short edge / width. cm number derived from the raw
  // option value (e.g. "15x20cm" → 15).
  function sceneForCm(cmWidth) {
    if (cmWidth == null || isNaN(cmWidth)) return 'sideboard';
    if (cmWidth <= 28)  return 'desk';
    if (cmWidth <= 45)  return 'sideboard';
    if (cmWidth <= 60)  return 'sofa';
    return 'hero';
  }

  /* =====================================================================
     ROOM LIBRARY — helpers for fab-size-picker (metaobject-backed rooms).
     Compositor consumes this later; pickRoomScene() selects a scene row,
     or collectExactSizeScenes() + carousel when several rows share a size.
     ===================================================================== */

  // Normalise a room string for matching. The custom.room metafield is
  // inconsistently cased across the catalogue ("Living Room" vs
  // "living room") and "Study Room" should behave as "office".
  function normaliseRoom(raw) {
    if (!raw) return '';
    const r = String(raw).toLowerCase().trim();
    if (r === 'study room' || r === 'study') return 'office';
    return r;
  }

  // Parse the room library blob emitted by the Liquid serialiser.
  // Returns an array of scene objects, each with wall_area parsed out of
  // wall_area_raw. Never throws — returns [] on any problem.
  function readRoomLibrary(root) {
    const el =
      (root || document).querySelector('[data-fab-room-library]') ||
      document.querySelector('[data-fab-room-library]');
    if (!el) return [];
    let raw;
    try {
      raw = JSON.parse(el.textContent || '[]');
    } catch (e) {
      return [];
    }
    if (!Array.isArray(raw)) return [];

    return raw
      .map(function (s) {
        let wa = {
          x: 0,
          y: 0,
          width: 100,
          height: 100,
          photoCm: 0,
          wallRgb: null,
          wallLum: null,
        };
        try {
          const parsed =
            typeof s.wall_area_raw === 'string' ? JSON.parse(s.wall_area_raw) : s.wall_area_raw;
          if (parsed && typeof parsed === 'object') {
            wa = {
              x: Number(parsed.x) || 0,
              y: Number(parsed.y) || 0,
              width: Number(parsed.width) || 100,
              height: Number(parsed.height) || 100,
              // photoCm — the room photo's real edge-to-edge width in cm.
              // Set once when the scene is marked. Drives the moulding
              // scale. 0 if absent (old scenes).
              photoCm: Number(parsed.photoCm) || 0,

              // Optional room integration values from wall_area JSON.
              // Example: "wallRgb":[150,136,119],"wallLum":0.54
              wallRgb: Array.isArray(parsed.wallRgb)
                ? parsed.wallRgb.map(function (v) {
                    return Number(v);
                  })
                : null,
              wallLum:
                parsed.wallLum != null && !isNaN(Number(parsed.wallLum))
                  ? Number(parsed.wallLum)
                  : null,
            };
          }
        } catch (e) {
          /* keep default full-bleed box */
        }
        console.log('[room wall area parsed]', wa);
        return {
          id: s.id || '',
          roomType: normaliseRoom(s.room_type),
          // The metaobject field is still keyed `size_class` internally,
          // but now holds an exact size like "50x70" — one scene per size.
          size: normaliseSizeKey(coerceSceneSizeClass(s.size_class)),
          image: s.image || '',
          wallArea: wa,
          lightDirection: String(s.light_direction || 'ambient').toLowerCase().trim(),
          lightingImage: s.lighting_image || '',
        };
      })
      .filter(function (s) {
        // A usable scene must have an image and a resolved size.
        return s.image && s.size;
      });
  }

  // Parse the product's recommended rooms blob. Returns a normalised,
  // de-duplicated array, e.g. ["living room","office"].
  function readProductRooms(root) {
    const el =
      (root || document).querySelector('[data-fab-product-rooms]') ||
      document.querySelector('[data-fab-product-rooms]');
    if (!el) return [];
    let raw;
    try {
      raw = JSON.parse(el.textContent || '[]');
    } catch (e) {
      return [];
    }
    if (!Array.isArray(raw)) return [];
    const seen = {};
    const out = [];
    raw.forEach(function (r) {
      const n = normaliseRoom(r);
      if (n && !seen[n]) {
        seen[n] = true;
        out.push(n);
      }
    });
    return out;
  }

  // A-size short-edge widths in cm (ISO 216).
  const ASIZE_CM = {
    A0: 84.1, A1: 59.4, A2: 42.0, A3: 29.7, A4: 21.0, A5: 14.8,
    A6: 10.5, A7: 7.4
  };

  // Size advice — line1 = verdict, line2 = counter. One variant (short /
  // test / creative) is chosen per page view and used for every size.
  const SIZE_ADVICE = {
    A5: {
      short: {
        line1: 'Best for shelves, ledges and small framed moments.',
        line2: 'Use several together for a gallery wall with presence.',
      },
      test: {
        line1: 'Small, flexible and easy to place.',
        line2: 'Works best layered, paired or mixed with larger prints.',
      },
      creative: {
        line1: 'Ideal for ledges, corners and layered displays.',
        line2: 'Mix a few into a larger gallery wall.',
      },
    },
    A4: {
      short: {
        line1: 'Good for desks, bedsides, shelves and hallway groups.',
        line2: 'Pair two or three for more wall presence.',
      },
      test: {
        line1: 'Easy to use in small rooms, nooks and reading corners.',
        line2: 'Above bigger furniture, use it as part of a set.',
      },
      creative: {
        line1: 'A strong starter size for gallery walls.',
        line2: 'Mix with A3 or A2 pieces for a more collected look.',
      },
    },
    A3: {
      short: {
        line1: 'Good for hallways, sideboards and smaller walls.',
        line2: 'Use as a pair above a console or in a tidy grid.',
      },
      test: {
        line1: 'Large enough to stand alone in a nook or hallway.',
        line2: 'Above wider furniture, add a second print or size up.',
      },
      creative: {
        line1: 'Works well in pairs, grids and gallery walls.',
        line2: 'Try two above twin beds or three down a hallway.',
      },
    },
    A2: {
      short: {
        line1: 'A useful mid-large size for bedrooms and medium walls.',
        line2: 'For sofas or beds, consider A1 or a pair.',
      },
      test: {
        line1: 'Strong enough for an entry bench, console or dresser.',
        line2: 'If the wall is wide, give it company.',
      },
      creative: {
        line1: 'Good as a single anchor or a matched pair.',
        line2: 'Hang two side by side for a clean hotel-style layout.',
      },
    },
    A1: {
      short: {
        line1: 'Strong above beds, sofas and main furniture.',
        line2: 'On extra-wide walls, use two together or go A0.',
      },
      test: {
        line1: 'Big enough to feel intentional on a main wall.',
        line2: 'For narrow corners or shelves, consider sizing down.',
      },
      creative: {
        line1: 'Works well as a bold centrepiece.',
        line2: 'Use two for a calm, symmetrical wide-wall layout.',
      },
    },
    A0: {
      short: {
        line1: 'Best as a main piece on a large wall.',
        line2: 'Leave space around it, or pair only on very wide walls.',
      },
      test: {
        line1: 'A serious statement size with real wall presence.',
        line2: 'In tighter rooms, A1 will feel easier.',
      },
      creative: {
        line1: 'Use one bold piece instead of a busy gallery wall.',
        line2: 'Or hang a matched pair across a very wide room.',
      },
    },
    '8x6': {
      short: {
        line1: 'Best for shelves, desks and picture ledges.',
        line2: 'Use several together for a gallery wall with real presence.',
      },
      test: {
        line1: 'Small, flexible and easy to layer.',
        line2: 'For a sofa or bed, use it as part of a set.',
      },
      creative: {
        line1: 'Ideal for ledges, corners and layered displays.',
        line2: 'Mix with larger prints to build a fuller gallery wall.',
      },
    },
    '14x11': {
      short: {
        line1: 'Good for bedside spaces, shelves and narrow walls.',
        line2: 'Pair two to fill more width.',
      },
      test: {
        line1: 'Easy above a desk, nightstand or small console.',
        line2: 'For a sofa, use it as part of a pair or gallery wall.',
      },
      creative: {
        line1: 'Strong in pairs for stairs, hallways or bedsides.',
        line2: 'Also works neatly inside a larger gallery wall.',
      },
    },
    '16x12': {
      short: {
        line1: 'Good for smaller walls, desks and grouped layouts.',
        line2: 'Above wider furniture, use a pair or size up.',
      },
      test: {
        line1: 'A useful size for sideboards and reading corners.',
        line2: 'If the wall is wide, give it company.',
      },
      creative: {
        line1: 'Works well as a matched pair.',
        line2: 'Try two over a console or three down a hallway.',
      },
    },
    '20x16': {
      short: {
        line1: 'A versatile mid-size for desks, consoles and smaller walls.',
        line2: 'For a sofa or bed, consider sizing up.',
      },
      test: {
        line1: 'Big enough to stand alone in tighter rooms.',
        line2: 'Pair it with a second print for more width.',
      },
      creative: {
        line1: 'Good beside a window, bookcase or low dresser.',
        line2: 'Use two together for a clean, balanced set.',
      },
    },
    '24x18': {
      short: {
        line1: 'Good above desks, consoles and smaller sofas.',
        line2: 'Also works well as a pair on a wider wall.',
      },
      test: {
        line1: 'Large enough to read clearly across the room.',
        line2: 'Above a bed, check the wall width before choosing.',
      },
      creative: {
        line1: 'A solid anchor piece for a small living room.',
        line2: 'Try two side by side if the wall feels long.',
      },
    },
    '28x20': {
      short: {
        line1: 'Good for bedrooms and living rooms without taking over.',
        line2: 'For a main piece above a bed or sofa, consider sizing up.',
      },
      test: {
        line1: 'Works as a confident single piece on medium walls.',
        line2: 'Pair two together if you want a wider look.',
      },
      creative: {
        line1: 'Strong over a dresser, loveseat or entry bench.',
        line2: 'Also works well in a clean two-print set.',
      },
    },
    '32x24': {
      short: {
        line1: 'A strong focal piece above sofas and beds.',
        line2: 'On very wide walls, use two together or go larger.',
      },
      test: {
        line1: 'Big enough to hold its own above main furniture.',
        line2: 'For narrow walls or shelves, consider sizing down.',
      },
      creative: {
        line1: 'Works well as a centrepiece.',
        line2: 'Hang two for a balanced wide-wall layout.',
      },
    },
    '40x28': {
      short: {
        line1: 'Best above beds, sofas and wide walls.',
        line2: 'In tighter spaces, consider sizing down.',
      },
      test: {
        line1: 'A strong single piece when you want real wall presence.',
        line2: 'For extra-wide walls, pair it with a second print.',
      },
      creative: {
        line1: 'Made for a main wall or large headboard.',
        line2: 'Build a calm two-print layout across a wide room.',
      },
    },
    '90x120': {
      short: {
        line1: 'A proper centrepiece for sofas, beds and large walls.',
        line2: 'Best with breathing room around it, not squeezed into a tight space.',
      },
      test: {
        line1: 'Large enough to change the whole room.',
        line2: 'Use as one strong piece instead of a busy gallery wall.',
      },
      creative: {
        line1: 'Big, confident and gallery-like.',
        line2: 'Anchor it with furniture below, or build smaller prints around it.',
      },
    },
    '100x150': {
      short: {
        line1: 'A true statement piece for big walls and spacious rooms.',
        line2: 'Best above large sofas, beds or sideboards with room to breathe.',
      },
      test: {
        line1: 'Made for real wall presence.',
        line2: 'In smaller rooms, 120×90cm will usually feel easier.',
      },
      creative: {
        line1: 'Use one piece to hold the whole wall.',
        line2: 'For very wide rooms, pair with smaller prints rather than crowding it.',
      },
    },
  };

  // Standard print sizes (cm WxH, max×min) → inch-style advice keys above.
  const CM_TO_STD_ADVICE = {
    '20x15': '8x6',
    '35x28': '14x11',
    '40x30': '16x12',
    '50x40': '20x16',
    '60x45': '24x18',
    '70x50': '28x20',
    '80x60': '32x24',
    '100x70': '40x28',
    '120x90': '90x120',
    '150x100': '100x150',
  };

  const SIZE_ADVICE_VARIANTS = ['short', 'test', 'creative'];

  function pickAdviceVariant() {
    return SIZE_ADVICE_VARIANTS[Math.floor(Math.random() * SIZE_ADVICE_VARIANTS.length)];
  }

  /** Inch advice key: larger×smaller (e.g. 6×8in → "8x6"). */
  function inchAdviceKey(inPart) {
    if (!inPart) return null;
    const m = String(inPart)
      .replace(/\u00d7/g, 'x')
      .replace(/×/g, 'x')
      .match(/(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)/i);
    if (!m) return null;
    const a = parseFloat(m[1]);
    const b = parseFloat(m[2]);
    if (!(a > 0) || !(b > 0)) return null;
    return Math.round(Math.max(a, b)) + 'x' + Math.round(Math.min(a, b));
  }

  function cmDimsAdviceKey(cmW, cmH) {
    if (cmW == null || cmH == null) return null;
    const hi = Math.round(Math.max(cmW, cmH));
    const lo = Math.round(Math.min(cmW, cmH));
    return CM_TO_STD_ADVICE[hi + 'x' + lo] || null;
  }

  /** Map a cm short-edge to the closest A-size key that has advice copy. */
  function closestAdviceAsize(cm) {
    const keys = Object.keys(SIZE_ADVICE).filter(function (k) {
      return k.charAt(0) === 'A';
    });
    let best = null;
    let bestDiff = Infinity;
    keys.forEach(function (k) {
      const ref = ASIZE_CM[k];
      if (ref == null) return;
      const d = Math.abs(ref - cm);
      if (d < bestDiff) {
        bestDiff = d;
        best = k;
      }
    });
    return best;
  }

  function stdAdviceKey(parsed, raw) {
    if (!parsed || parsed.kind !== 'std') return null;
    const parts = String(raw || '').split('/').map(function (p) {
      return p.trim();
    });
    const fromIn = inchAdviceKey(parts[1] || '');
    if (fromIn && SIZE_ADVICE[fromIn]) return fromIn;
    const fromCm = cmDimsAdviceKey(parsed.cm, parsed.cmH);
    if (fromCm && SIZE_ADVICE[fromCm]) return fromCm;
    if (parsed.cm != null) return closestAdviceAsize(parsed.cm);
    return null;
  }

  function adviceKeyForCell(cell) {
    if (!cell) return null;
    const stamped = cell.dataset.fabAsizeName;
    if (stamped && SIZE_ADVICE[stamped]) return stamped;
    const parsed = cell.__fabParsed;
    if (parsed && parsed.kind === 'asize' && parsed.name && SIZE_ADVICE[parsed.name]) {
      return parsed.name;
    }
    if (parsed && parsed.kind === 'std') {
      return stdAdviceKey(parsed, cell.dataset.fabSizeValue);
    }
    return null;
  }

  // Placeholder when a variant has no featured image (<img src>).
  const FALLBACK_PRINT_DATA_URI =
    'data:image/svg+xml;charset=utf-8,' +
    encodeURIComponent(
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 40"><defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#365a3b"/><stop offset="1" stop-color="#264428"/></linearGradient></defs><rect width="30" height="40" fill="url(#g)"/><circle cx="8" cy="12" r="3.5" fill="#e8c563"/><circle cx="20" cy="10" r="2.8" fill="#d97474"/><circle cx="13" cy="20" r="2.4" fill="#eea0a0"/><circle cx="22" cy="22" r="3" fill="#c068a8"/><circle cx="6" cy="28" r="2.2" fill="#f5d987"/><circle cx="17" cy="32" r="2.6" fill="#e08aab"/><circle cx="24" cy="34" r="1.8" fill="#f0c660"/><circle cx="9" cy="35" r="1.9" fill="#d97474"/></svg>'
    );

  /** Match frame-price-updater.js — variant.price is in cents. */
  function formatMoneyCents(cents) {
    if (window.Shopify && window.Shopify.formatMoney) {
      const moneyFormat =
        (window.theme && window.theme.moneyFormat) || '${{amount}}';
      return Shopify.formatMoney(cents, moneyFormat);
    }
    const mainPrice = document.querySelector('.price .price-item');
    if (mainPrice) {
      const format = mainPrice.textContent.replace(/[\d\s.,]/g, '');
      const amount = (cents / 100).toFixed(2);
      return format + amount;
    }
    return '$' + (cents / 100).toFixed(2);
  }

  // Wall-width assumption per scene (cm) — used for the caption
  // "wall 2.8m wide" line.
  const WALL_CM_BY_SCENE = {
    desk:      140,
    sideboard: 280,
    sofa:      320,
    hero:      360,
  };

  // Vertical baseline (where the BOTTOM of the print sits in the scene)
  // as a percentage from the top of the stage.
  const BASELINE_PCT_BY_SCENE = {
    desk:      62,
    sideboard: 48,
    sofa:      62,
    hero:      80,
  };

  // Scene labels (only English for now; production should add locale variants)
  const SCENE_LABEL = {
    desk:      'Desk scene',
    sideboard: 'Sideboard scene',
    sofa:      'Sofa scene',
    hero:      'Full wall scene',
  };

  // Scene HTML — sit inside the .fab-size__scene layer.
  // Kept as simple CSS shapes until production room photos exist.
  function sceneHTML(scene) {
    switch (scene) {
      case 'desk':
        return `
          <div class="fab-scene-desk-surface"></div>
          <div class="fab-scene-desk-mug"></div>
          <div class="fab-scene-desk-book"></div>
        `;
      case 'sideboard':
        return `
          <div class="fab-scene-sideboard-lamp"></div>
          <div class="fab-scene-sideboard-top"></div>
          <div class="fab-scene-sideboard-vase"></div>
        `;
      case 'sofa':
        return `
          <div class="fab-scene-sofa"></div>
          <div class="fab-scene-sofa-cushion fab-scene-sofa-cushion--left"></div>
          <div class="fab-scene-sofa-cushion fab-scene-sofa-cushion--right"></div>
        `;
      case 'hero':
        return `
          <div class="fab-scene-hero-chair"></div>
          <div class="fab-scene-hero-plant"></div>
        `;
    }
    return '';
  }

  // ============================================================
  // Parsing the option value
  // ============================================================
  // Values look like "15x20cm / 6x8in" or "A4 / A4".
  function parseSizeValue(raw) {
    if (!raw) return null;
    const parts = String(raw).split('/').map(p => p.trim());
    const cmPart = parts[0] || '';
    const inPart = parts[1] || '';

    // A-size check: starts with A followed by a digit
    const upper = cmPart.toUpperCase();
    const aMatch = upper.match(/^A\d+/);
    if (aMatch) {
      const aName = aMatch[0];
      const cm = ASIZE_CM[aName] || null;
      return {
        kind: 'asize',
        cm: cm,
        // ISO 216 portrait: long edge = short edge × √2
        ratio: Math.SQRT2,
        name: aName,
        raw: raw,
      };
    }

    // Standard "15x20cm" — parse w and h (accept × / Unicode multiply as x)
    const cmNorm = String(cmPart).replace(/\u00d7/g, 'x').replace(/×/g, 'x');
    const m = cmNorm.match(/(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)/i);
    if (m) {
      const w = parseFloat(m[1]);
      const h = parseFloat(m[2]);
      return {
        kind: 'std',
        cm: w,
        cmH: h,
        ratio: w && h ? w / h : null,
        raw: raw,
      };
    }

    return { kind: 'unknown', cm: null, ratio: null, raw: raw };
  }

  // Normalised size key for matching a variant size against a room_scene's
  // Size field. Includes orientation, because portrait and landscape are
  // separate room_scene entries (a landscape print is used differently).
  // Standard sizes only — A-sizes have no room scenes.
  //   ("50 x 70cm / 20 x 28in", "portrait")  → "50x70-portrait"
  //   ("15x20cm / 6x8\"",        "landscape") → "15x20-landscape"
  // The room_scene Size field stores exactly this form.
  // Returns '' for A-sizes / unknowns (→ no room scene, CSS fallback).
  function sizeKey(parsed, orientation) {
    if (!parsed) return '';
    const ori = orientation === 'landscape' ? 'landscape' : 'portrait';
    if (parsed.kind === 'asize' && parsed.name) {
      // A-size key, e.g. "a3-portrait" — matches the room_scene size_class.
      return parsed.name.toLowerCase() + '-' + ori;
    }
    if (parsed.kind !== 'std') return '';
    if (parsed.cm == null || parsed.cmH == null) return '';
    // Drop any decimals — catalogue sizes are whole cm.
    return Math.round(parsed.cm) + 'x' + Math.round(parsed.cmH) + '-' + ori;
  }

  // Normalise a room_scene Size field value the same way, so admin entry
  // is forgiving: "50 x 70 Portrait", "50X70-portrait" → "50x70-portrait".
  // If no orientation is present, defaults to portrait.
  // Also accepts "50×70" (Unicode multiply) and similar.
  function normaliseSizeKey(raw) {
    if (!raw) return '';
    const s = String(raw)
      .toLowerCase()
      .replace(/\u00d7/g, 'x')
      .replace(/×/g, 'x');
    // A-size key, e.g. "a3-portrait" / "a3 landscape" / bare "a3".
    const am = s.match(/\ba([0-7])\b/);
    if (am) {
      const ori = s.indexOf('landscape') !== -1 ? 'landscape' : 'portrait';
      return 'a' + am[1] + '-' + ori;
    }
    const m = s.match(/(\d+)\s*x\s*(\d+)/);
    if (!m) return '';
    const ori = s.indexOf('landscape') !== -1 ? 'landscape' : 'portrait';
    return m[1] + 'x' + m[2] + '-' + ori;
  }

  /**
   * Shopify JSON may hand size_class as a string, number, or (rarely) list.
   * Coerce to a single string for normaliseSizeKey.
   */
  function coerceSceneSizeClass(raw) {
    if (raw == null || raw === '') return '';
    if (typeof raw === 'string') return raw;
    if (typeof raw === 'number' && !isNaN(raw)) return String(raw);
    if (Array.isArray(raw)) {
      for (let i = 0; i < raw.length; i++) {
        const c = coerceSceneSizeClass(raw[i]);
        if (c) return c;
      }
      return '';
    }
    if (typeof raw === 'object') {
      if (raw.handle != null) return String(raw.handle);
      if (raw.value != null) return coerceSceneSizeClass(raw.value);
    }
    return '';
  }

  /**
   * True when a library scene's normalised size refers to the same print as
   * `wantKey` (already normalised). Portrait: exact WxH + orientation.
   * Landscape: also allows swapped WxH (admin often enters 70×50 vs 50×70).
   */
  function dimsMatchForPrintCarousel(wantKey, sceneSizeNorm) {
    if (!wantKey || !sceneSizeNorm) return false;
    if (wantKey === sceneSizeNorm) return true;
    const mw = wantKey.match(/^(\d+)\s*x\s*(\d+)/);
    const ms = sceneSizeNorm.match(/^(\d+)\s*x\s*(\d+)/);
    if (!mw || !ms) return false;
    const w1 = parseInt(mw[1], 10);
    const h1 = parseInt(mw[2], 10);
    const w2 = parseInt(ms[1], 10);
    const h2 = parseInt(ms[2], 10);
    const o1 =
      wantKey.indexOf('landscape') !== -1 ? 'landscape' : 'portrait';
    const o2 =
      sceneSizeNorm.indexOf('landscape') !== -1 ? 'landscape' : 'portrait';
    if (o1 !== o2) return false;
    if (w1 === w2 && h1 === h2) return true;
    if (o1 === 'landscape' && w1 === h2 && h1 === w2) return true;
    return false;
  }

  // Convert cm dimensions to a printable display, locale-aware.
  function dimDisplay(parsed, country) {
    if (parsed.kind === 'asize') return parsed.name;
    if (parsed.kind !== 'std') return parsed.raw;
    if (country === 'us') {
      const wIn = Math.round(parsed.cm / 2.54 * 10) / 10;
      const hIn = Math.round(parsed.cmH / 2.54 * 10) / 10;
      return `${wIn}″ × ${hIn}″`;
    }
    return `${parsed.cm} × ${parsed.cmH} cm`;
  }

  function parseFabBoolean(value, defaultValue) {
    if (typeof value === 'boolean') return value;
    if (value == null || value === '') return defaultValue;

    const s = String(value).trim().toLowerCase();

    if (s === 'false' || s === '0' || s === 'no' || s === 'off') return false;
    if (s === 'true' || s === '1' || s === 'yes' || s === 'on') return true;

    return defaultValue;
  }

  // ============================================================
  // Picker — one instance per .fab-size-picker root
  // ============================================================
  class FabSizePicker {
    constructor(root) {
      this.root = root;
      this.select = root.querySelector('.fab-size__select-shim');
      this.toggle = root.querySelector('[data-fab-preview-toggle]');
      this.roomWrap = root.querySelector('[data-fab-room-wrap]');
      this.sceneLayer = root.querySelector('[data-fab-scene-layer]');
      this.print = root.querySelector('[data-fab-print]');
      this.printImg = this.print && this.print.querySelector('[data-fab-print-img]');
      if (this.print && !this.printImg) {
        const img = document.createElement('img');
        img.className = 'fab-size__print-img';
        img.setAttribute('data-fab-print-img', '');
        img.alt = '';
        img.decoding = 'async';
        img.loading = 'eager';
        this.print.appendChild(img);
        this.printImg = img;
      }
      // Readout pill (the "70 × 100 cm" dimension label) is intentionally
      // disabled — the room mock shows the print at honest scale, the
      // floating label is redundant clutter. Forced null so every
      // `if (this.readout)` guard below skips it. To bring it back, change
      // this to: root.querySelector('[data-fab-readout]').
      this.readout = null;
      // Caption ("Full wall scene · wall 3.6m wide") is disabled — the
      // measurement arrows now show the wall dimensions far more clearly.
      // Forced null so every `if (this.caption…)` guard skips it.
      this.captionScene = null;
      this.captionScale = null;
      this.adviceVerdict = root.querySelector('[data-fab-advice-verdict]');
      this.adviceCounter = root.querySelector('[data-fab-advice-counter]');
      this.adviceVariant = pickAdviceVariant();
      this.cells = Array.from(root.querySelectorAll('[data-fab-size-cell]'));
      this.asizeToggle = root.querySelector('[data-fab-asize-toggle]');
      this.asizeBlock = root.querySelector('[data-fab-asize-block]');
      this.variantSelectsEl = root.closest('variant-selects');

      this.country = (root.dataset.fabCountry || '').toLowerCase();
      this.locale = (root.dataset.fabLocale || '').toLowerCase();
      // The product's artwork orientation, from custom.orientation, stamped
      // on the root by the Liquid snippet. Portrait unless explicitly
      // landscape. Used to pick the right room_scene (portrait vs landscape
      // are separate entries).
      this.orientation =
        (root.dataset.fabOrientation || '').toLowerCase() === 'landscape'
          ? 'landscape'
          : 'portrait';
      // Full-bleed: custom.fullbleed boolean, stamped on the root. Default
      // true (image goes edge to edge). When the attribute is exactly
      // 'false', the print has a white paper border.
      this.fullBleed = parseFabBoolean(root.dataset.fabFullbleed, true);
      // Canvas products render with a gallery-wrap + floater-frame model
      // instead of the flat paper moulding. The Liquid snippet stamps
      // product.type on the root as data-fab-product-type.
      this.isCanvas =
        (root.dataset.fabProductType || '').toLowerCase().indexOf('canvas') !== -1;
      console.log('[Fab size picker fullBleed]', {
        raw: root.dataset.fabFullbleed,
        parsed: this.fullBleed,
        isCanvas: this.isCanvas,
        productType: root.dataset.fabProductType,
      });

      this.expanded = false;
      // True after the customer taps "Hide room"; size pills won't reopen until "See in room".
      this.roomDismissedByUser = false;
      this.asizeExpanded = false;
      this.currentScene = null;
      this.currentParsed = null;

      // Room library + this product's recommended rooms.
      this.roomLibrary = readRoomLibrary(root);
      this.productRooms = readProductRooms(root);
      this.currentRoomScene = null;

      // Multiple room_scene rows for the same print size → arrows + dots + swipe.
      // `_roomCarouselBuiltSig` tracks whether controls match the scene list.
      this._roomCarouselUi = null;
      this._roomCarouselScenes = null;
      this._roomCarouselIndex = 0;
      this._roomCarouselBuiltSig = '';
      this._carouselPtrStartX = null;
      this._onCarouselPtrDown = null;
      this._onCarouselPtrUp = null;

      // Canvas compositor. Created lazily on first room expand so we don't
      // pay for a canvas on PDPs where the room mock is never opened.
      // compositorActive is true only while a real composite is showing.
      this.compositor = null;
      this.compositorActive = false;

      this.init();
    }

    applyFullBleedMode() {
      // For now, fix art prints only. Canvas handling can come later.
      const nonFullBleedArtPrint = !this.isCanvas && this.fullBleed === false;

      this.root.classList.toggle(
        'fab-size-picker--non-full-bleed',
        nonFullBleedArtPrint
      );

      if (this.print) {
        this.print.classList.toggle(
          'fab-size__print--non-full-bleed',
          nonFullBleedArtPrint
        );
      }

      if (this.printImg) {
        this.printImg.classList.toggle(
          'fab-size__print-img--non-full-bleed',
          nonFullBleedArtPrint
        );
      }
    }

    init() {
      // Decorate every cell with parsed metadata and the scene it belongs
      // to (used for the CSS placeholder fallback room).
      this.cells.forEach(cell => {
        const raw = cell.dataset.fabSizeValue;
        const parsed = parseSizeValue(raw);
        cell.__fabParsed = parsed;
        if (parsed && parsed.cm != null) {
          cell.dataset.fabScene = sceneForCm(parsed.cm);
        }
        cell.addEventListener('click', (e) => this.onCellClick(cell, e));
      });

 // Preview toggle
if (this.toggle && this.roomWrap) {
  this.toggle.addEventListener('click', (e) => {
    e.preventDefault();
    this.onToggleClick();
  });
}

// A-sizes toggle
if (this.asizeToggle && this.asizeBlock) {
  this.asizeToggle.addEventListener('click', (e) => {
    e.preventDefault();
    this.toggleAsizes();
  });
}

      this.applyFullBleedMode();

      // Initial: paint the room mock for the currently-selected size
      // (so when the user expands later, it's pre-correct)
      const initialCell = this.cells.find(c => c.classList.contains('fab-size__cell--selected') || c.classList.contains('fab-size__asize-cell--selected')) || this.cells[0];
      if (initialCell && initialCell.__fabParsed) {
        this.applySize(initialCell.__fabParsed, /*animate*/ false);
      }
      if (initialCell) this.updateAdvice(initialCell);

      // If the variant-selects fires a change for another option (e.g. frame),
      // we don't need to do anything — the underlying select still holds the
      // right size value. But if something external sets select.value, mirror.
      if (this.select) {
        this.select.addEventListener('change', () => this.syncFromSelect());
      }
      // Reposition the readout if the stage resizes (e.g. after expand, on
      // viewport change). Without this, the readout would sit at the wrong
      // pixel offset because it was computed against the old stage height.
      //
      // Debounced: the expand animation resizes the stage every frame for
      // ~460ms. Without debouncing that fires applySize() — and a fresh
      // compositor render — dozens of times. We coalesce to one call once
      // the size settles.
      if (typeof ResizeObserver !== 'undefined') {
        const stage = this.print && this.print.parentElement;
        if (stage) {
          this._resizeObserver = new ResizeObserver(() => {
            window.clearTimeout(this._resizeTimer);
            this._resizeTimer = window.setTimeout(() => {
              if (this.currentParsed) {
                // animate=false; we don't want a transition on resize.
                this.applySize(this.currentParsed, false);
              }
            }, 120);
          });
          this._resizeObserver.observe(stage);
        }
      }

      if (this.variantSelectsEl) {
        const schedulePillPrices = () => {
          window.clearTimeout(this._pillPriceTimer);
          this._pillPriceTimer = window.setTimeout(() => this.updatePillPrices(), 50);
        };
        this.variantSelectsEl.addEventListener('change', schedulePillPrices);

        this.variantSelectsEl.addEventListener('variant:change', e => {
          this.syncPrintArt(e.detail);
          schedulePillPrices();
          const value = this.select && this.select.value;
          const cell = value && this.cells.find(c => c.dataset.fabSizeValue === value);
          if (cell && cell.__fabParsed) {
            this.setSelectedCell(cell);
            this.applySize(cell.__fabParsed, false);
          } else if (this.currentParsed) {
            this.applySize(this.currentParsed, false);
          }
          this._syncAsizePanelToSelection();
        });
      }

      this.updatePillPrices();
      this._syncAsizePanelToSelection();

      // If there is no usable room library, the compositor will fall back
      // to the CSS placeholder scenes. Nothing else to do at init.
      if (!this.roomLibrary.length) {
        // No real rooms yet — placeholder scenes remain in use.
      }
    }

    syncFromSelect() {
      const value = this.select.value;
      const cell = this.cells.find(c => c.dataset.fabSizeValue === value);
      if (cell && cell.__fabParsed) {
        this.setSelectedCell(cell);
        this.applySize(cell.__fabParsed, true);
      }
      this._syncAsizePanelToSelection();
    }

    setSelectedCell(targetCell) {
      this.cells.forEach(c => {
        const isAsizeCell = c.classList.contains('fab-size__asize-cell');
        const selected = c === targetCell;
        if (isAsizeCell) {
          c.classList.toggle('fab-size__asize-cell--selected', selected);
        } else {
          c.classList.toggle('fab-size__cell--selected', selected);
        }
        c.setAttribute('aria-checked', selected ? 'true' : 'false');
      });
      this.updateAdvice(targetCell);
    }

    updateAdvice(cell) {
      const key = adviceKeyForCell(cell);
      const copy =
        key && SIZE_ADVICE[key] ? SIZE_ADVICE[key][this.adviceVariant] : null;
      if (this.adviceVerdict) {
        this.adviceVerdict.textContent = copy ? copy.line1 : '';
      }
      if (this.adviceCounter) {
        this.adviceCounter.textContent = copy ? copy.line2 : '';
      }
    }

    onCellClick(cell) {
      const value = cell.dataset.fabSizeValue;
      const parsed = cell.__fabParsed;
      if (!value || !parsed) return;

      // Update the hidden select FIRST, dispatch change to wake the
      // variant pipeline. product-form.js will fire variant:change,
      // frame-price-updater.js will recompute frame deltas, etc.
      if (this.select.value !== value) {
        this.select.value = value;
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
      }

      this.setSelectedCell(cell);
      this.applySize(parsed, true);

      const isCollapsedMode =
        (this.root.dataset.fabSizeMode || '') === 'collapsed-tabbed';
      if (!isCollapsedMode && !this.expanded && !this.roomDismissedByUser) {
        this.expand();
      }
    }

    onToggleClick() {
      if (this.expanded) {
        this.roomDismissedByUser = true;
        this.collapse();
      } else {
        this.roomDismissedByUser = false;
        this.expand();
      }
    }

    /**
     * Given a size+orientation key (e.g. "50x70-portrait"), returns the
     * best room scene for THIS product, or null if the library can't
     * serve one (caller then uses the CSS placeholder scene).
     *
     * One room_scene exists per size per orientation. Matching is exact;
     * the fallback degrades by nearest size BUT NEVER crosses orientation
     * — a portrait print never falls back to a landscape room.
     */
    pickRoomScene(sizeKeyWanted) {
      const lib = this.roomLibrary;
      if (!lib || !lib.length) return null;

      const want = normaliseSizeKey(sizeKeyWanted);
      if (!want) return null;
      const wantOri = want.indexOf('landscape') !== -1 ? 'landscape' : 'portrait';
      const rooms = this.productRooms || [];

      // Stable index for deterministic tie-breaking. The product id is
      // stamped on the picker root by the Liquid serialiser.
      const pid = parseInt(this.root.dataset.fabProductId, 10) || 0;
      const pickStable = function (candidates) {
        if (!candidates.length) return null;
        return candidates[pid % candidates.length];
      };

      // Orientation guard — used by every fallback so we never hand a
      // landscape room to a portrait print or vice versa.
      const sameOri = function (s) {
        return (s.size.indexOf('landscape') !== -1 ? 'landscape' : 'portrait')
               === wantOri;
      };

      // "Nearest size" fallback — by total cm area, same orientation only.
      const keyArea = function (k) {
        const m = String(k).match(/(\d+)x(\d+)/);
        return m ? parseInt(m[1], 10) * parseInt(m[2], 10) : 0;
      };
      const wantArea = keyArea(want);
      const nearestBySize = function (candidates) {
        const pool = candidates.filter(sameOri);
        if (!pool.length) return null;
        if (!wantArea) return pickStable(pool);
        let best = null, bestDist = Infinity;
        pool.forEach(function (s) {
          const d = Math.abs(keyArea(s.size) - wantArea);
          if (d < bestDist) { bestDist = d; best = s; }
        });
        return best;
      };

      // 1. Exact room + exact size, product rooms in declared order.
      for (let i = 0; i < rooms.length; i++) {
        const exact = lib.filter(function (s) {
          return s.roomType === rooms[i] && s.size === want;
        });
        if (exact.length) return pickStable(exact);
      }

      // 2. Exact size, any room.
      const sizeOnly = lib.filter(function (s) { return s.size === want; });
      if (sizeOnly.length) return pickStable(sizeOnly);

      // 3. Preferred room, nearest size (same orientation).
      for (let j = 0; j < rooms.length; j++) {
        const roomOnly = lib.filter(function (s) {
          return s.roomType === rooms[j];
        });
        if (roomOnly.length) {
          const hit = nearestBySize(roomOnly);
          if (hit) return hit;
        }
      }

      // 4. Nearest size, any room (same orientation).
      return nearestBySize(lib);
    }

    /**
     * All library scenes whose normalised size key matches `sizeKeyWanted`
     * exactly (same orientation). Used to decide whether to show a carousel.
     */
    collectExactSizeScenes(sizeKeyWanted) {
      const lib = this.roomLibrary;
      if (!lib || !lib.length) return [];
      const want = normaliseSizeKey(sizeKeyWanted);
      if (!want) return [];
      const wantOri =
        want.indexOf('landscape') !== -1 ? 'landscape' : 'portrait';
      const sameOri = function (s) {
        return (
          (s.size.indexOf('landscape') !== -1 ? 'landscape' : 'portrait') ===
          wantOri
        );
      };
      const pool = lib.filter(function (s) {
        return sameOri(s) && dimsMatchForPrintCarousel(want, s.size);
      });
      if (pool.length <= 1) return pool;
      const seen = {};
      const out = [];
      for (let i = 0; i < pool.length; i++) {
        const s = pool[i];
        const k = (s.id ? String(s.id) : '') + '|' + String(s.image || '');
        if (seen[k]) continue;
        seen[k] = true;
        out.push(s);
      }
      return out.length > 1 ? out : pool.slice(0, 1);
    }

    /**
     * Order scenes for carousel: product `custom.room` order first (the
     * first listed room that appears in the pool wins as slide 0), then
     * any other rooms in stable library order.
     */
    orderScenesForCarousel(pool) {
      if (!pool || pool.length <= 1) return pool ? pool.slice() : [];
      const rooms = this.productRooms || [];
      const decorated = pool.map((s, i) => {
        let rank = 999;
        const rt = s.roomType || '';
        for (let r = 0; r < rooms.length; r++) {
          if (rt === rooms[r]) {
            rank = r;
            break;
          }
        }
        return { s, rank, i };
      });
      decorated.sort(function (a, b) {
        return a.rank - b.rank || a.i - b.i;
      });
      return decorated.map(function (d) {
        return d.s;
      });
    }

    _carouselSigFromScenes(scenes) {
      if (!scenes || !scenes.length) return '';
      return scenes
        .map(function (s) {
          return (s.id || '') + '|' + (s.image || '');
        })
        .join(';;');
    }

    _destroyRoomCarousel() {
      const stage = this.print && this.print.parentElement;
      if (stage && this._onCarouselPtrDown && this._onCarouselPtrUp) {
        stage.removeEventListener('pointerdown', this._onCarouselPtrDown);
        stage.removeEventListener('pointerup', this._onCarouselPtrUp);
        stage.removeEventListener('pointercancel', this._onCarouselPtrUp);
      }
      this._onCarouselPtrDown = null;
      this._onCarouselPtrUp = null;
      this._carouselPtrStartX = null;
      if (this._roomCarouselUi && this._roomCarouselUi.parentNode) {
        this._roomCarouselUi.parentNode.removeChild(this._roomCarouselUi);
      }
      this._roomCarouselUi = null;
      this._roomCarouselBuiltSig = '';
    }

    _roomCarouselDotAriaLabel(i, n) {
      const tpl =
        this.root.getAttribute('data-fab-room-dot-aria') ||
        'Room view {current} of {total}';
      return tpl.replace('{current}', String(i + 1)).replace('{total}', String(n));
    }

    _roomCarouselArrowSvgPrev() {
      return (
        '<svg viewBox="0 0 512 512" aria-hidden="true">' +
        '<path fill="none" stroke="currentColor" stroke-linecap="round" ' +
        'stroke-linejoin="round" stroke-width="48" d="M328 112L184 256l144 144"/>' +
        '</svg>'
      );
    }

    _roomCarouselArrowSvgNext() {
      return (
        '<svg viewBox="0 0 512 512" aria-hidden="true">' +
        '<path fill="none" stroke="currentColor" stroke-linecap="round" ' +
        'stroke-linejoin="round" stroke-width="48" d="M184 112l144 144-144 144"/>' +
        '</svg>'
      );
    }

    _syncRoomCarouselChrome() {
      const n = this._roomCarouselScenes
        ? this._roomCarouselScenes.length
        : 0;
      const idx = Math.max(
        0,
        Math.min(this._roomCarouselIndex, Math.max(0, n - 1))
      );
      this._roomCarouselIndex = idx;
      if (!this._roomCarouselUi) return;
      const prevBtn = this._roomCarouselUi.querySelector(
        '[data-fab-room-carousel-prev]'
      );
      const nextBtn = this._roomCarouselUi.querySelector(
        '[data-fab-room-carousel-next]'
      );
      if (prevBtn) {
        const hidePrev = idx === 0 || n <= 1;
        prevBtn.toggleAttribute('hidden', hidePrev);
        prevBtn.setAttribute('aria-hidden', hidePrev ? 'true' : 'false');
      }
      if (nextBtn) {
        const hideNext = n <= 1 || idx >= n - 1;
        nextBtn.toggleAttribute('hidden', hideNext);
        nextBtn.setAttribute('aria-hidden', hideNext ? 'true' : 'false');
      }
      const buttons = this._roomCarouselUi.querySelectorAll(
        '.fab-size__room-carousel-dot'
      );
      buttons.forEach((btn, i) => {
        const active = i === idx;
        btn.classList.toggle('fab-size__room-carousel-dot--active', active);
        btn.setAttribute('aria-current', active ? 'true' : 'false');
        btn.setAttribute('aria-selected', active ? 'true' : 'false');
        btn.setAttribute('aria-label', this._roomCarouselDotAriaLabel(i, n));
      });
    }

    _setRoomCarouselIndex(stage, nextIdx) {
      if (!this._roomCarouselScenes || !this._roomCarouselScenes.length) return;
      const n = this._roomCarouselScenes.length;
      const i = Math.max(0, Math.min(nextIdx, n - 1));
      if (i === this._roomCarouselIndex) return;
      this._roomCarouselIndex = i;
      this._syncRoomCarouselChrome();
      const scene = this._roomCarouselScenes[i];
      this.currentRoomScene = scene;
      this._compositorRenderPromise(scene, stage);
    }

    _ensureRoomCarousel(stage, scenes) {
      const self = this;
      if (!scenes || scenes.length <= 1) {
        this._destroyRoomCarousel();
        return;
      }
      const sig = this._carouselSigFromScenes(scenes);
      if (this._roomCarouselUi && this._roomCarouselBuiltSig === sig) {
        this._syncRoomCarouselChrome();
        return;
      }
      this._destroyRoomCarousel();

      const ui = document.createElement('div');
      ui.className = 'fab-size__room-carousel-ui';

      const prevBtn = document.createElement('button');
      prevBtn.type = 'button';
      prevBtn.className = 'fab-gallery__arrow fab-gallery__arrow--prev';
      prevBtn.setAttribute('data-fab-room-carousel-control', '');
      prevBtn.setAttribute('data-fab-room-carousel-prev', '');
      prevBtn.setAttribute(
        'aria-label',
        this.root.getAttribute('data-fab-prev-slide-label') || 'Previous'
      );
      prevBtn.innerHTML = this._roomCarouselArrowSvgPrev();
      prevBtn.addEventListener('click', function (e) {
        e.preventDefault();
        self._setRoomCarouselIndex(stage, self._roomCarouselIndex - 1);
      });

      const nextBtn = document.createElement('button');
      nextBtn.type = 'button';
      nextBtn.className = 'fab-gallery__arrow fab-gallery__arrow--next';
      nextBtn.setAttribute('data-fab-room-carousel-control', '');
      nextBtn.setAttribute('data-fab-room-carousel-next', '');
      nextBtn.setAttribute(
        'aria-label',
        this.root.getAttribute('data-fab-next-slide-label') || 'Next'
      );
      nextBtn.innerHTML = this._roomCarouselArrowSvgNext();
      nextBtn.addEventListener('click', function (e) {
        e.preventDefault();
        self._setRoomCarouselIndex(stage, self._roomCarouselIndex + 1);
      });

      const dotsWrap = document.createElement('div');
      dotsWrap.className = 'fab-size__room-carousel-dots';
      dotsWrap.setAttribute('role', 'tablist');
      dotsWrap.setAttribute(
        'aria-label',
        this.root.getAttribute('data-fab-room-dots-label') || 'Room photos'
      );

      const n = scenes.length;
      for (let i = 0; i < n; i++) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'fab-size__room-carousel-dot';
        btn.setAttribute('data-fab-room-carousel-control', '');
        btn.setAttribute('role', 'tab');
        btn.setAttribute('aria-selected', 'false');
        btn.setAttribute('aria-label', this._roomCarouselDotAriaLabel(i, n));
        const sceneIndex = i;
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          self._setRoomCarouselIndex(stage, sceneIndex);
        });
        dotsWrap.appendChild(btn);
      }

      ui.appendChild(prevBtn);
      ui.appendChild(nextBtn);
      ui.appendChild(dotsWrap);
      stage.appendChild(ui);
      this._roomCarouselUi = ui;
      this._roomCarouselBuiltSig = sig;
      this._syncRoomCarouselChrome();

      this._onCarouselPtrDown = function (e) {
        if (!self._roomCarouselScenes || self._roomCarouselScenes.length <= 1) {
          return;
        }
        if (
          e.target.closest &&
          e.target.closest('[data-fab-room-carousel-control]')
        ) {
          return;
        }
        if (typeof e.button === 'number' && e.button !== 0) return;
        self._carouselPtrStartX = e.clientX;
      };
      this._onCarouselPtrUp = function (e) {
        if (self._carouselPtrStartX == null) return;
        const start = self._carouselPtrStartX;
        self._carouselPtrStartX = null;
        if (!self._roomCarouselScenes || self._roomCarouselScenes.length <= 1) {
          return;
        }
        const dx = e.clientX - start;
        if (dx > 48) {
          self._setRoomCarouselIndex(stage, self._roomCarouselIndex - 1);
        } else if (dx < -48) {
          self._setRoomCarouselIndex(stage, self._roomCarouselIndex + 1);
        }
      };
      stage.addEventListener('pointerdown', this._onCarouselPtrDown);
      stage.addEventListener('pointerup', this._onCarouselPtrUp);
      stage.addEventListener('pointercancel', this._onCarouselPtrUp);
    }

    _compositorRenderPromise(scene, stage) {
      if (!scene) {
        return Promise.resolve(false);
      }
      if (!this.compositor) {
        this.compositor = new window.FabRoomCompositor(stage);
      }
      return this.compositor
        .render({
          scene: scene,
          artUrl: this._unframedArtUrl(),
          frameColor: this.getSelectedFrameColor(),
          country: this.country,
          fullBleed: this.fullBleed,
          isCanvas: this.isCanvas,
        })
        .then(result => {
          if (result === 'stale') return result;
          if (result) {
            this.compositorActive = true;
            this.compositor.show();
            if (this.sceneLayer) this.sceneLayer.style.opacity = '0';
            if (this.print) this.print.style.opacity = '0';
            this._positionReadoutFromCompositor();
          } else {
            this._useCssScene();
          }
          return result;
        })
        .catch(() => {
          this._useCssScene();
          return false;
        });
    }

    getVariantsArray() {
      if (!this.variantSelectsEl) return [];
      try {
        return JSON.parse(this.variantSelectsEl.dataset.variants || '[]');
      } catch (_) {
        return [];
      }
    }

    getSelectedOptionValues() {
      if (!this.variantSelectsEl) return [];
      const controls = Array.from(
        this.variantSelectsEl.querySelectorAll(
          'select[data-option-position], input[type="radio"][data-option-position]:checked'
        )
      );
      controls.sort((a, b) => {
        const pa = parseInt(a.getAttribute('data-option-position'), 10) || 0;
        const pb = parseInt(b.getAttribute('data-option-position'), 10) || 0;
        return pa - pb;
      });
      return controls.map(el => el.value);
    }

    findMatchingVariant() {
      const variants = this.getVariantsArray();
      const selectedOptions = this.getSelectedOptionValues();
      if (!variants.length || !selectedOptions.length) return null;
      return (
        variants.find(variant => {
          const variantOptions = (variant.options || []).filter(
            o => o !== null && o !== undefined && o !== ''
          );
          return selectedOptions.every((sel, index) => sel === variantOptions[index]);
        }) || null
      );
    }

    /**
     * Variant for a hypothetical size value while keeping every other
     * option (frame, etc.) at the customer's current selection.
     */
    findVariantForSizeValue(sizeValue) {
      if (!this.select || sizeValue == null || sizeValue === '') return null;
      const variants = this.getVariantsArray();
      const selected = this.getSelectedOptionValues();
      if (!variants.length || !selected.length) return null;
      const sizePos = parseInt(this.select.getAttribute('data-option-position'), 10);
      if (!(sizePos >= 1)) return null;
      const sizeIdx = sizePos - 1;
      if (sizeIdx < 0 || sizeIdx >= selected.length) return null;
      const target = selected.map((v, i) => (i === sizeIdx ? String(sizeValue) : v));
      return (
        variants.find(variant => {
          const vo = (variant.options || []).filter(
            o => o !== null && o !== undefined && o !== ''
          );
          if (vo.length !== target.length) return false;
          return target.every((t, i) => vo[i] === t);
        }) || null
      );
    }

    /** Refresh .fab-size__price / .fab-size__asize-price when frame (or other options) change. */
    updatePillPrices() {
      if (!this.select || !this.variantSelectsEl) return;
      const selected = this.getSelectedOptionValues();
      if (!selected.length) return;
      this.cells.forEach(cell => {
        const sizeVal = cell.dataset.fabSizeValue;
        if (!sizeVal) return;
        const priceEl = cell.querySelector('.fab-size__price, .fab-size__asize-price');
        if (!priceEl) return;
        const v = this.findVariantForSizeValue(sizeVal);
        if (v && v.price != null) {
          priceEl.textContent = formatMoneyCents(v.price);
        }
      });

      const collapsed = this.root.__fabSizePickerCollapsed;
      if (collapsed && typeof collapsed.syncSummaryFromSelect === 'function') {
        collapsed.syncSummaryFromSelect();
      }
    }

    /** Shopify often returns protocol-relative URLs; normalize for CSS + fetch. */
    normalizeImageUrl(raw) {
      if (!raw || typeof raw !== 'string') return '';
      const s = raw.trim();
      if (s.indexOf('//') === 0) return 'https:' + s;
      return s;
    }

    variantFeaturedImageUrl(variant) {
      if (!variant) return '';
      if (typeof variant.featured_image === 'string' && variant.featured_image) {
        return this.normalizeImageUrl(variant.featured_image);
      }
      if (variant.featured_image && typeof variant.featured_image.src === 'string') {
        return this.normalizeImageUrl(variant.featured_image.src);
      }
      const fm = variant.featured_media && variant.featured_media.preview_image;
      if (fm && fm.src) {
        return this.normalizeImageUrl(fm.src);
      }
      return '';
    }

    syncPrintArt(variantHint) {
      if (!this.print || !this.printImg) return;
      let url = '';
      if (variantHint) {
        url = this.variantFeaturedImageUrl(variantHint);
      }
      if (!url) {
        url = this.variantFeaturedImageUrl(this.findMatchingVariant());
      }
      if (!url && this.variantSelectsEl) {
        try {
          const sel = this.variantSelectsEl.querySelector('[data-selected-variant]');
          if (sel && sel.textContent) {
            url = this.variantFeaturedImageUrl(JSON.parse(sel.textContent));
          }
        } catch (_) {}
      }
      this.print.style.backgroundImage = 'none';
      this.printImg.src = url || FALLBACK_PRINT_DATA_URI;
      this.printImg.alt = '';
    }

    // ----------------------------------------------------------
    // Compositor wiring
    // ----------------------------------------------------------

    // Read the live frame swatch and canonicalise to a lowercase colour
    // key ('black'|'white'|'oak'|'brown'|'silver'|'gold'|'unframed').
    // The frame picker uses radio inputs with class "fab-frame-radio"
    // (see product-variant-picker-new.liquid).
    getSelectedFrameColor() {
      const vs = this.variantSelectsEl;
      if (!vs) return 'unframed';
      const checked =
        vs.querySelector('input.fab-frame-radio:checked') ||
        vs.querySelector('fieldset.product-form__input--swatch input[type="radio"]:checked');
      let raw = checked ? checked.value : '';
      if (!raw) {
        try {
          const sel = vs.querySelector('[data-selected-variant]');
          if (sel) {
            const v = JSON.parse(sel.textContent || '{}');
            const opts = (v.options || []).map(o => String(o).toLowerCase().trim());
            const FRAMES = ['black', 'white', 'oak', 'brown', 'silver', 'gold'];
            raw = opts.find(o => FRAMES.indexOf(o) !== -1) || '';
          }
        } catch (e) {}
      }
      const c = String(raw).toLowerCase().trim();
      const MAP = {
        'unframed': 'unframed', 'none': 'unframed', 'sans': 'unframed', 'sans cadre': 'unframed',
        'non encadré': 'unframed', 'ohne': 'unframed', 'ohne rahmen': 'unframed',
        'senza': 'unframed', 'senza cornice': 'unframed', 'zonder': 'unframed',
        'zonder lijst': 'unframed', 'utan': 'unframed', 'utan ram': 'unframed',
        'sin': 'unframed', 'sin marco': 'unframed',
        'black': 'black', 'noir': 'black', 'schwarz': 'black', 'nero': 'black',
        'zwart': 'black', 'svart': 'black', 'negro': 'black',
        'white': 'white', 'blanc': 'white', 'weiß': 'white', 'weiss': 'white',
        'bianco': 'white', 'wit': 'white', 'vit': 'white', 'blanco': 'white',
        'oak': 'oak', 'chêne': 'oak', 'roble': 'oak', 'eiche': 'oak',
        'rovere': 'oak', 'eiken': 'oak', 'eik': 'oak', 'ek': 'oak',
        'brown': 'brown', 'marron': 'brown', 'marrón': 'brown', 'marrone': 'brown',
        'braun': 'brown', 'bruin': 'brown', 'brun': 'brown', 'walnut': 'brown',
        'silver': 'silver', 'argent': 'silver', 'argenté': 'silver', 'silber': 'silver',
        'argento': 'silver', 'zilver': 'silver', 'plata': 'silver', 'plateado': 'silver',
        'gold': 'gold', 'or': 'gold', 'doré': 'gold', 'oro': 'gold',
        'goud': 'gold', 'guld': 'gold', 'dorado': 'gold'
      };
      return MAP[c] || 'unframed';
    }

    // The bare, unframed artwork URL for the compositor to frame live.
    // It must NOT be a pre-framed mock.
    //
    // On this catalogue, variants carry NO images (variant.image is null
    // for every variant), so variant lookups all come back empty. The
    // bare artwork is the PRODUCT's featured image (first media item) —
    // the clean painting, not the framed `il_fullxfull` mocks. The Liquid
    // snippet stamps that URL onto the root as data-fab-art-url.
    //
    // Order: data-fab-art-url → an unframed variant image (future-proof,
    // if variants ever get images) → matching variant → any variant.
    _unframedArtUrl() {
      // 1. The product featured image, stamped by the Liquid snippet.
      const stamped = this.root.dataset.fabArtUrl;
      if (stamped) {
        const u = this.normalizeImageUrl(stamped);
        if (u) return u;
      }

      // 2. An unframed variant's own image (only if variants have images).
      const variants = this.getVariantsArray();
      const UNFRAMED = ['unframed', 'none', 'sans', 'ohne', 'senza', 'zonder', 'utan', 'sin'];
      for (let i = 0; i < variants.length; i++) {
        const opts = (variants[i].options || []).map(o => String(o).toLowerCase().trim());
        if (opts.some(o => UNFRAMED.indexOf(o) !== -1)) {
          const u = this.variantFeaturedImageUrl(variants[i]);
          if (u) return u;
        }
      }

      // 3. The matching variant, then any variant.
      const m = this.variantFeaturedImageUrl(this.findMatchingVariant());
      if (m) return m;
      for (let j = 0; j < variants.length; j++) {
        const u = this.variantFeaturedImageUrl(variants[j]);
        if (u) return u;
      }
      return '';
    }

    // Drive the canvas compositor. Decides between a real composited room
    // and the CSS placeholder scene. Safe to call whether or not the room
    // mock is currently expanded. `sizeKeyWanted` is the normalised size
    // key for the current selection (e.g. "50x70-portrait"), or '' for A-sizes.
    //
    // When several room_scene metaobjects share that exact size, all of
    // them are offered as a carousel (recommended `custom.room` order
    // first); otherwise behaviour matches pickRoomScene() fallbacks.
    renderRoom(parsed, sizeKeyWanted) {
      if (!parsed) return;
      const stage = this.print && this.print.parentElement;
      if (!stage) return;

      // No compositor, no library, or an A-size (no room scenes exist for
      // A-sizes) → CSS placeholder scene.
      if (typeof window.FabRoomCompositor === 'undefined' ||
          !this.roomLibrary.length ||
          !sizeKeyWanted) {
        this._useCssScene();
        return;
      }

      const exact = this.collectExactSizeScenes(sizeKeyWanted);
      let scene = null;
      let carouselScenes = null;

      if (exact.length > 1) {
        carouselScenes = this.orderScenesForCarousel(exact);
        const sig = this._carouselSigFromScenes(carouselScenes);
        if (this._roomCarouselBuiltSig !== sig) {
          this._roomCarouselIndex = 0;
        }
        this._roomCarouselScenes = carouselScenes;
        this._roomCarouselIndex = Math.min(
          this._roomCarouselIndex,
          carouselScenes.length - 1
        );
        this._ensureRoomCarousel(stage, carouselScenes);
        scene = carouselScenes[this._roomCarouselIndex];
      } else {
        this._destroyRoomCarousel();
        this._roomCarouselScenes = null;
        if (exact.length === 1) {
          scene = exact[0];
        } else {
          scene = this.pickRoomScene(sizeKeyWanted);
        }
      }

      if (!scene) {
        this._useCssScene();
        return;
      }

      this.currentRoomScene = scene;
      this._compositorRenderPromise(scene, stage);
    }

    // Fall back to the CSS placeholder scene: clear the compositor canvas
    // and restore the scene layer + <img> print to visible.
    _useCssScene() {
      this._destroyRoomCarousel();
      this._roomCarouselScenes = null;
      this.compositorActive = false;
      if (this.compositor) this.compositor.clear();
      if (this.sceneLayer) this.sceneLayer.style.opacity = '';
      if (this.print) this.print.style.opacity = '';
    }

    // Position the readout pill above the compositor's drawn piece. The
    // piece rect is in CSS px relative to the stage; the readout is a
    // stage child, so the coords are used directly.
    _positionReadoutFromCompositor() {
      if (!this.readout || !this.compositor) return;
      const rect = this.compositor.getPieceRect();
      if (!rect) return;
      this.readout.style.left = (rect.x + rect.w / 2) + 'px';
      this.readout.style.transform = 'translateX(-50%)';
      this.readout.style.top = Math.max(4, rect.y - 28) + 'px';
    }

    // ----------------------------------------------------------
    // Applying a size to the room mock
    // ----------------------------------------------------------
    applySize(parsed, animate) {
      if (!parsed) return;

      const cm = parsed.cm || 40;
      const cmH =
        parsed.cmH != null
          ? parsed.cmH
          : parsed.kind === 'asize' && parsed.ratio
            ? cm * parsed.ratio
            : cm * 1.25;
      const targetScene = sceneForCm(cm);
      const wallCm = WALL_CM_BY_SCENE[targetScene] || 280;
      const widthFrac = Math.min(cm / wallCm, 0.92);
      const widthPct = widthFrac * 100;

      if (this.print) {
        this.print.style.setProperty('--fab-print-width-pct', widthPct.toFixed(2) + '%');
        this.print.style.aspectRatio = `${cm} / ${cmH}`;
        this.print.style.setProperty('--fab-print-baseline', BASELINE_PCT_BY_SCENE[targetScene]);
      }
      this.syncPrintArt();

      // Readout positioning. When the compositor is active it owns the
      // readout position (see _positionReadoutFromCompositor) — the CSS
      // scene geometry below would fight it, so skip it in that case.
      if (this.readout) {
        this.readout.textContent = dimDisplay(parsed, this.country);
        if (!this.compositorActive) {
          const stage = this.print && this.print.parentElement;
          if (stage) {
            const stageH = stage.clientHeight;
            if (stageH > 0) {
              const baseWpx = stage.clientWidth * widthFrac;
              const baseHpx = baseWpx * (cmH / cm);
              const printHpx = baseHpx;
              const printBottomPx = stageH * (BASELINE_PCT_BY_SCENE[targetScene] / 100);
              const printTopPx = printBottomPx - printHpx;
              this.readout.style.top = Math.max(4, printTopPx - 28) + 'px';
            }
          }
        }
      }

      // Scene swap if needed
      if (this.sceneLayer) {
        if (targetScene !== this.currentScene) {
          this.swapScene(targetScene, animate);
        } else if (this.currentScene == null) {
          this.sceneLayer.innerHTML = sceneHTML(targetScene);
          this.currentScene = targetScene;
          this.updateCaption(targetScene);
        } else {
          this.updateCaption(targetScene);
        }
      }

      // Drive the canvas compositor (falls back to the CSS scene
      // internally). Matched on the EXACT size+orientation key. Several
      // room_scene rows for the same key show a carousel; A-sizes yield ''
      // → CSS fallback.
      this.renderRoom(parsed, sizeKey(parsed, this.orientation));

      this.currentParsed = parsed;
    }

    swapScene(nextScene, animate) {
      if (!this.sceneLayer) return;
      if (!animate) {
        this.sceneLayer.innerHTML = sceneHTML(nextScene);
        this.currentScene = nextScene;
        this.updateCaption(nextScene);
        return;
      }
      // Fade out → swap → fade in
      this.sceneLayer.classList.add('is-fading');
      // Wait for the fade transition (about 280ms in CSS)
      const onFadeOut = () => {
        this.sceneLayer.removeEventListener('transitionend', onFadeOut);
        this.sceneLayer.innerHTML = sceneHTML(nextScene);
        this.currentScene = nextScene;
        this.updateCaption(nextScene);
        // Next frame, fade back in
        requestAnimationFrame(() => {
          this.sceneLayer.classList.remove('is-fading');
        });
      };
      this.sceneLayer.addEventListener('transitionend', onFadeOut, { once: true });
      // Safety: if for any reason transitionend doesn't fire, swap anyway.
      setTimeout(() => {
        if (this.sceneLayer.classList.contains('is-fading')) {
          onFadeOut();
        }
      }, 360);
    }

    updateCaption(scene) {
      if (this.captionScene) this.captionScene.textContent = SCENE_LABEL[scene] || '';
      if (this.captionScale) {
        const wallCm = WALL_CM_BY_SCENE[scene] || 280;
        if (this.country === 'us') {
          const ft = (wallCm / 30.48).toFixed(1);
          this.captionScale.textContent = `wall ${ft}ft wide`;
        } else {
          const m = (wallCm / 100).toFixed(1);
          this.captionScale.textContent = `wall ${m}m wide`;
        }
      }
    }

    // ----------------------------------------------------------
    // Room expand/collapse
    // ----------------------------------------------------------
    _syncPreviewToggleLabel() {
      if (!this.toggle) return;
      const span = this.toggle.querySelector('span');
      if (!span) return;
      const show = this.toggle.dataset.fabPreviewShow;
      const hide = this.toggle.dataset.fabPreviewHide;
      if (this.expanded) {
        if (hide != null && hide !== '') span.textContent = hide;
      } else if (show != null && show !== '') {
        span.textContent = show;
      }
    }

    expand() {
      const wrap = this.roomWrap;
      if (!wrap || this.expanded) return;

      window.clearTimeout(this._roomTimer);

      if (this.sceneLayer && !this.sceneLayer.innerHTML.trim() && this.currentScene) {
        this.sceneLayer.innerHTML = sceneHTML(this.currentScene);
      }

      // Make sure no old CSS-var or inline state is fighting us.
      wrap.style.removeProperty('--fab-room-h');
      wrap.style.overflow = 'hidden';
      wrap.style.display = 'block';

      // Measure open height.
      wrap.style.transition = 'none';
      wrap.classList.add('is-expanded');
      wrap.setAttribute('aria-hidden', 'false');
      wrap.style.height = 'auto';

      const target = wrap.scrollHeight;

      // Start collapsed.
      wrap.style.height = '0px';
      void wrap.offsetHeight;

      // Animate open.
      wrap.style.transition = '';
      this.expanded = true;

      if (this.toggle) {
        this.toggle.setAttribute('aria-expanded', 'true');
        this._syncPreviewToggleLabel();
      }

      requestAnimationFrame(() => {
        wrap.style.height = target + 'px';
      });

      this._roomTimer = window.setTimeout(() => {
        if (!this.expanded) return;

        wrap.style.height = 'auto';

        if (this.currentParsed) {
          this.applySize(this.currentParsed, false);
        }
      }, 460);
    }

    collapse() {
      const wrap = this.roomWrap;
      if (!wrap || !this.expanded) return;

      window.clearTimeout(this._roomTimer);

      wrap.style.removeProperty('--fab-room-h');
      wrap.style.overflow = 'hidden';
      wrap.style.display = 'block';

      // Pin current open height.
      wrap.style.transition = 'none';
      wrap.style.height = wrap.scrollHeight + 'px';
      void wrap.offsetHeight;

      // Animate closed.
      wrap.style.transition = '';
      wrap.classList.remove('is-expanded');
      wrap.setAttribute('aria-hidden', 'true');

      this.expanded = false;

      if (this.toggle) {
        this.toggle.setAttribute('aria-expanded', 'false');
        this._syncPreviewToggleLabel();
      }

      requestAnimationFrame(() => {
        wrap.style.height = '0px';
      });

      this._roomTimer = window.setTimeout(() => {
        if (this.expanded) return;

        wrap.style.height = '';
        wrap.style.display = '';
      }, 460);
    }

    // ----------------------------------------------------------
    // A-sizes toggle
    // ----------------------------------------------------------
    _syncAsizePanelToSelection() {
      if (!this.asizeBlock || !this.asizeToggle) return;
      const selectedCell = this.cells.find(
        c =>
          c.classList.contains('fab-size__asize-cell--selected') ||
          c.classList.contains('fab-size__cell--selected')
      );
      if (!selectedCell) return;
      const isAsize = selectedCell.classList.contains('fab-size__asize-cell');
      if (isAsize) {
        if (!this.asizeExpanded) this.expandAsizes();
      } else if (this.asizeExpanded) {
        this.collapseAsizes();
      }
    }

    toggleAsizes() {
      if (!this.asizeBlock) return;

      if (this.asizeExpanded) {
        this.collapseAsizes();
      } else {
        this.expandAsizes();
      }
    }

    expandAsizes() {
      const block = this.asizeBlock;
      if (!block || this.asizeExpanded) return;

      window.clearTimeout(this._asizeTimer);

      // Critical: remove hidden before measuring, and force display block.
      block.hidden = false;
      block.style.display = 'block';
      block.style.removeProperty('--fab-asize-h');
      block.style.overflow = 'hidden';

      // Measure expanded height.
      block.style.transition = 'none';
      block.classList.add('is-expanded');
      block.style.height = 'auto';

      const target = block.scrollHeight;

      // Start collapsed.
      block.style.height = '0px';
      void block.offsetHeight;

      // Animate open.
      block.style.transition = '';
      this.asizeExpanded = true;

      if (this.asizeToggle) {
        this.asizeToggle.setAttribute('aria-expanded', 'true');
        this.asizeToggle.textContent = this.asizeToggle.dataset.hideLabel || 'Hide A-sizes';
      }

      requestAnimationFrame(() => {
        block.style.height = target + 'px';
      });

      this._asizeTimer = window.setTimeout(() => {
        if (!this.asizeExpanded) return;

        block.style.height = 'auto';
      }, 400);
    }

    collapseAsizes() {
      const block = this.asizeBlock;
      if (!block || !this.asizeExpanded) return;

      window.clearTimeout(this._asizeTimer);

      block.style.display = 'block';
      block.style.removeProperty('--fab-asize-h');
      block.style.overflow = 'hidden';

      // Pin current open height.
      block.style.transition = 'none';
      block.style.height = block.scrollHeight + 'px';
      void block.offsetHeight;

      // Animate closed.
      block.style.transition = '';
      block.classList.remove('is-expanded');

      this.asizeExpanded = false;

      if (this.asizeToggle) {
        this.asizeToggle.setAttribute('aria-expanded', 'false');
        this.asizeToggle.textContent = this.asizeToggle.dataset.showLabel || 'Show A-sizes (A0–A5)';
      }

      requestAnimationFrame(() => {
        block.style.height = '0px';
      });

      this._asizeTimer = window.setTimeout(() => {
        if (this.asizeExpanded) return;

        block.hidden = true;
        block.style.height = '';
        block.style.display = '';
      }, 400);
    }
  }

  // ============================================================
  // Bootstrapping
  // ============================================================
  function initAll(scope) {
    const roots = (scope || document).querySelectorAll('[data-fab-size-picker]');
    roots.forEach(root => {
      if (root.__fabSizePicker) return;
      root.__fabSizePicker = new FabSizePicker(root);
    });
    if (typeof window.__fabInitCollapsedSizePickers === 'function') {
      window.__fabInitCollapsedSizePickers(scope || document);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document));
  } else {
    initAll(document);
  }
  // Shopify Section Rendering API support — rebind after section re-render
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
  // Theme product-info loaded (Dawn-style)
  document.addEventListener('product-info:loaded', () => initAll(document));
})();

/**
 * fab-size-picker-collapsed.js
 *
 * Collapsed + tabbed size selector experiment. Works alongside fab-size-picker.js
 * which handles variant contract, room mock, and pill metadata.
 */
(function () {
  'use strict';

  function capture(eventName, props) {
    try {
      if (window.__fabTrackingHelpers && typeof window.__fabTrackingHelpers.capture === 'function') {
        window.__fabTrackingHelpers.capture(eventName, props || {});
      }
    } catch (e) { /* noop */ }
  }

  function parseFabBoolean(value, defaultValue) {
    if (typeof value === 'boolean') return value;
    if (value == null || value === '') return defaultValue;
    var s = String(value).trim().toLowerCase();
    if (s === 'false' || s === '0' || s === 'no' || s === 'off') return false;
    if (s === 'true' || s === '1' || s === 'yes' || s === 'on') return true;
    return defaultValue;
  }

  function sizeKeyForCell(cell) {
    if (!cell) return '';
    if (cell.dataset.fabSizeKey) return cell.dataset.fabSizeKey;
    if (window.FabSizeKey && cell.dataset.fabSizeValue) {
      return window.FabSizeKey.normalizePopularKey(cell.dataset.fabSizeValue);
    }
    return '';
  }

  class FabSizePickerCollapsed {
    /**
     * @param {HTMLElement} root
     * @param {import('./fab-size-picker.js')} picker — FabSizePicker instance
     */
    constructor(root, picker) {
      this.root = root;
      this.picker = picker;
      this.trigger = root.querySelector('[data-fab-collapsed-trigger]');
      this.panel = root.querySelector('[data-fab-collapsed-panel]');
      this.summaryEl = root.querySelector('[data-fab-collapsed-summary]');
      this.tabs = Array.from(root.querySelectorAll('[data-fab-size-tab]'));
      this.tabPanels = Array.from(root.querySelectorAll('[data-fab-size-tabpanel]'));
      this.collapseOnSelect = parseFabBoolean(root.dataset.fabCollapseOnSelect, true);
      this.defaultTab = root.dataset.fabDefaultTab === 'all' ? 'all' : 'popular';
      this.expanded = false;
      this.activeTab = this.tabs.length ? this.defaultTab : 'all';
      this._onKeydown = this.onKeydown.bind(this);
      this._exposureSent = false;

      this.init();
    }

    init() {
      if (!this.trigger || !this.panel) return;

      this.trigger.addEventListener('click', () => this.togglePanel());

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.activateTab(tab.dataset.fabSizeTab, true));
        tab.addEventListener('keydown', (e) => this.onTabKeydown(e, tab));
      });

      this.root.querySelectorAll('[data-fab-size-cell]').forEach((cell) => {
        cell.addEventListener('click', () => this.onPillSelected(cell));
      });

      if (this.picker && this.picker.select) {
        this.picker.select.addEventListener('change', () => this.syncSummaryFromSelect());
      }

      if (this.picker && this.picker.variantSelectsEl) {
        this.picker.variantSelectsEl.addEventListener('variant:change', () => {
          window.setTimeout(() => this.syncSummaryFromSelect(), 60);
        });
      }

      this.sendExposure();
      this.syncSummaryFromSelect();
    }

    sendExposure() {
      if (this._exposureSent) return;
      this._exposureSent = true;
      capture('size_selector_experiment_exposure', {
        variant: 'collapsed-tabbed',
        ui_variant: 'collapsed-tabbed',
        product_id: this.root.dataset.fabProductId || null,
      });
    }

    togglePanel(forceOpen) {
      var open = typeof forceOpen === 'boolean' ? forceOpen : !this.expanded;
      this.expanded = open;
      this.trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      this.panel.hidden = !open;

      if (open) {
        capture('size_selector_expanded', {
          variant: 'collapsed-tabbed',
          product_id: this.root.dataset.fabProductId || null,
        });
        var activeTabEl = this.tabs.find(function (t) {
          return t.dataset.fabSizeTab === this.activeTab;
        }, this);
        (activeTabEl || this.tabs[0])?.focus();
        document.addEventListener('keydown', this._onKeydown);
      } else {
        document.removeEventListener('keydown', this._onKeydown);
      }
    }

    collapsePanel() {
      if (!this.expanded) return;
      this.togglePanel(false);
      this.trigger.focus();
    }

    onKeydown(e) {
      if (!this.expanded) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        this.togglePanel(false);
        this.trigger.focus();
      }
    }

    activateTab(tabName, fromUser) {
      if (!tabName || this.tabs.length === 0) return;
      if (fromUser && tabName !== this.activeTab) {
        capture('size_selector_tab_changed', {
          tab: tabName,
          variant: 'collapsed-tabbed',
          product_id: this.root.dataset.fabProductId || null,
        });
      }
      this.activeTab = tabName;

      this.tabs.forEach(function (tab) {
        var isActive = tab.dataset.fabSizeTab === tabName;
        tab.classList.toggle('fab-size__tab--active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.tabIndex = isActive ? 0 : -1;
      });

      this.tabPanels.forEach(function (panel) {
        var isActive = panel.dataset.fabSizeTabpanel === tabName;
        panel.classList.toggle('fab-size__tabpanel--active', isActive);
        panel.hidden = !isActive;
      });
    }

    onTabKeydown(e, tab) {
      if (this.tabs.length < 2) return;
      var idx = this.tabs.indexOf(tab);
      if (idx < 0) return;

      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        e.preventDefault();
        var next = e.key === 'ArrowRight'
          ? (idx + 1) % this.tabs.length
          : (idx - 1 + this.tabs.length) % this.tabs.length;
        this.activateTab(this.tabs[next].dataset.fabSizeTab, true);
        this.tabs[next].focus();
      } else if (e.key === 'Home') {
        e.preventDefault();
        this.activateTab(this.tabs[0].dataset.fabSizeTab, true);
        this.tabs[0].focus();
      } else if (e.key === 'End') {
        e.preventDefault();
        this.activateTab(this.tabs[this.tabs.length - 1].dataset.fabSizeTab, true);
        this.tabs[this.tabs.length - 1].focus();
      }
    }

    onPillSelected(cell) {
      var tab = this.findTabForCell(cell);
      capture('size_selector_size_selected', {
        sizeKey: sizeKeyForCell(cell),
        tab: tab,
        fromCollapsed: true,
        panelOpen: this.expanded,
        variant: 'collapsed-tabbed',
        product_id: this.root.dataset.fabProductId || null,
      });

      window.requestAnimationFrame(() => {
        this.syncSummaryFromCell(cell);
        if (this.collapseOnSelect && this.expanded) {
          this.collapsePanel();
        }
      });
    }

    findTabForCell(cell) {
      var panel = cell.closest('[data-fab-size-tabpanel]');
      return panel ? panel.dataset.fabSizeTabpanel || 'all' : 'all';
    }

    syncSummaryFromSelect() {
      if (!this.picker || !this.picker.select || !this.summaryEl) return;
      var value = this.picker.select.value;
      var cells = this.root.querySelectorAll('[data-fab-size-cell]');
      var cell = null;
      for (var i = 0; i < cells.length; i++) {
        if (cells[i].dataset.fabSizeValue === value) {
          cell = cells[i];
          break;
        }
      }
      if (cell) {
        this.syncSummaryFromCell(cell);
      }
    }

    syncSummaryFromCell(cell) {
      if (!this.summaryEl || !cell) return;
      var dim = cell.dataset.fabDisplayDim || cell.querySelector('.fab-size__dim, .fab-size__asize-name')?.textContent?.trim() || '';
      var price = cell.querySelector('.fab-size__price, .fab-size__asize-price')?.textContent?.trim() || '';
      this.summaryEl.textContent = dim && price ? dim + ' · ' + price : dim || price;
    }
  }

  function initCollapsed(scope) {
    var roots = (scope || document).querySelectorAll(
      '[data-fab-size-picker][data-fab-size-mode="collapsed-tabbed"]'
    );
    roots.forEach(function (root) {
      if (root.__fabSizePickerCollapsed) return;
      var picker = root.__fabSizePicker;
      if (!picker) return;
      root.__fabSizePickerCollapsed = new FabSizePickerCollapsed(root, picker);
    });
  }

  window.__fabInitCollapsedSizePickers = initCollapsed;

  function boot() {
    initCollapsed(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) {
    initCollapsed(e.target);
  });
  document.addEventListener('product-info:loaded', boot);
})();

(function () {
  var DESKTOP_MQ = '(min-width: 750px)';

  function parseStickySummaryConfig(bar) {
    var el = bar.querySelector('[data-sticky-summary-config]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent.trim());
    } catch (e) {
      return null;
    }
  }

  function formatSizeSegment(raw, country) {
    var parts = String(raw || '').split('/');
    var cmPart = (parts[0] || '').trim();
    var inPart = (parts[1] || '').trim();
    if (country === 'us') {
      if (inPart) return inPart;
      return cmPart || '';
    }
    if (cmPart) return cmPart;
    return inPart || '';
  }

  function canonicalFrameKey(raw) {
    var v = String(raw || '').toLowerCase().trim();
    var unframed = [
      'unframed',
      'none',
      'sans',
      'sans cadre',
      'non encadré',
      'sin',
      'sin marco',
      'ohne',
      'ohne rahmen',
      'senza',
      'senza cornice',
      'zonder',
      'zonder lijst',
      'utan',
      'utan ram',
    ];
    if (unframed.indexOf(v) >= 0) return 'unframed';
    var blacks = ['black', 'noir', 'negro', 'schwarz', 'nero', 'zwart', 'svart'];
    if (blacks.indexOf(v) >= 0) return 'black';
    var whites = ['white', 'blanc', 'blanco', 'weiß', 'weiss', 'bianco', 'wit', 'vit'];
    if (whites.indexOf(v) >= 0) return 'white';
    var silvers = ['silver', 'argent', 'argenté', 'silber', 'argento', 'zilver', 'plata', 'plateado'];
    if (silvers.indexOf(v) >= 0) return 'silver';
    var golds = ['gold', 'or', 'doré', 'oro', 'goud', 'guld', 'dorado'];
    if (golds.indexOf(v) >= 0) return 'gold';
    var browns = ['brown', 'marron', 'marrón', 'marrone', 'braun', 'bruin', 'brun', 'walnut'];
    if (browns.indexOf(v) >= 0) return 'brown';
    var oaks = ['oak', 'chêne', 'chene', 'roble', 'eiche', 'rovere', 'eiken', 'eik', 'ek'];
    if (oaks.indexOf(v) >= 0) return 'oak';
    return null;
  }

  function formatFrameSegment(raw, config) {
    var key = canonicalFrameKey(raw);
    if (key === 'unframed') return config.printOnly || '';
    var labels = config.colorLabels || {};
    var rawTrim = String(raw || '').trim();
    var color = key && labels[key] != null ? labels[key] : rawTrim;
    return (color + ' ' + (config.frameWord || 'Frame')).trim();
  }

  function formatStickyVariantSummary(variant, config) {
    if (!variant || !variant.options) return '';
    if (!config || !config.optionKinds || !config.optionKinds.length) {
      return variant.options
        .filter(function (o) {
          return o != null && o !== '';
        })
        .join(', ');
    }
    var kinds = config.optionKinds;
    var country = config.country || '';
    var parts = [];
    for (var i = 0; i < kinds.length; i++) {
      var raw = variant.options[i];
      if (raw == null || raw === '') continue;
      var kind = kinds[i];
      var seg = '';
      if (kind === 'size') seg = formatSizeSegment(raw, country);
      else if (kind === 'frame') seg = formatFrameSegment(raw, config);
      else seg = String(raw).trim();
      if (seg) parts.push(seg);
    }
    return parts.join(', ');
  }

  function variantImageUrl(variant, fallback) {
    if (!variant) return fallback || '';
    var fi = variant.featured_image;
    if (fi) {
      var fisrc = fi.src || fi.url;
      if (fisrc) {
        return fisrc.indexOf('//') === 0 ? 'https:' + fisrc : fisrc;
      }
    }
    var fm = variant.featured_media && variant.featured_media.preview_image;
    if (fm && fm.src) {
      return fm.src.indexOf('//') === 0 ? 'https:' + fm.src : fm.src;
    }
    return fallback || '';
  }

  function hasCustomPrintUpload(productInfo) {
    if (!productInfo) return false;
    var uploadInput = productInfo.querySelector('[data-fab-cp-prop-upload-id]');
    return !!(uploadInput && uploadInput.value && !uploadInput.disabled);
  }

  function syncStickySubmit(stickyBtn, mainBtn) {
    if (!stickyBtn || !mainBtn) return;
    stickyBtn.toggleAttribute('disabled', mainBtn.disabled);
    var mainLabel = mainBtn.querySelector('.product-form__submit-label');
    var stickyLabel = stickyBtn.querySelector('[data-sticky-cart-submit-label]');
    if (mainLabel && stickyLabel) {
      stickyLabel.textContent = mainLabel.textContent.trim();
    }
    stickyBtn.classList.toggle(
      'product-form__submit--adding',
      mainBtn.classList.contains('product-form__submit--adding')
    );
    if (mainBtn.getAttribute('aria-busy') === 'true') {
      stickyBtn.setAttribute('aria-busy', 'true');
    } else {
      stickyBtn.removeAttribute('aria-busy');
    }
    if (mainBtn.getAttribute('aria-disabled') === 'true') {
      stickyBtn.setAttribute('aria-disabled', 'true');
    } else {
      stickyBtn.removeAttribute('aria-disabled');
    }
  }

  function init(bar) {
    var sectionId = bar.dataset.sectionId;
    if (!sectionId) return;

    var mq = window.matchMedia(DESKTOP_MQ);
    var productInfo = bar.closest('product-info');
    var infoEl = document.getElementById('ProductInfo-' + sectionId);
    var mainSubmit =
      (infoEl && infoEl.querySelector('#ProductSubmitButton-' + sectionId)) ||
      document.getElementById('ProductSubmitButton-' + sectionId);
    var buyButtons = mainSubmit && mainSubmit.closest('.product-form__buttons');
    var visibilityTarget = buyButtons || mainSubmit;
    var stickySubmit = bar.querySelector('[data-sticky-cart-submit]');
    var summaryEl = bar.querySelector('[data-sticky-variant-summary]');
    var imgEl = bar.querySelector('.product-sticky-cart-bar__img');
    var fallbackSrc = bar.dataset.stickyImgFallback || '';

    if (!mainSubmit || !stickySubmit || !visibilityTarget) {
      bar.remove();
      return;
    }

    /**
     * Show only after the buy row has scrolled above the viewport — not when it is
     * still below the fold (a plain "not intersecting" check fires too early).
     */
    function shouldShowStickyBar() {
      var rect = visibilityTarget.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) return false;

      var viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      if (rect.top >= viewportHeight) return false;
      return rect.bottom <= 0;
    }

    var summaryConfig = parseStickySummaryConfig(bar);

    var resizeObs;
    var buyAreaObserver;

    function setBarVisible(show) {
      if (show) {
        bar.classList.add('is-visible');
        bar.removeAttribute('hidden');
        bar.setAttribute('aria-hidden', 'false');
      } else {
        bar.classList.remove('is-visible');
        bar.setAttribute('hidden', '');
        bar.setAttribute('aria-hidden', 'true');
      }
    }

    function updateVisibility() {
      if (!mq.matches) {
        setBarVisible(false);
        return;
      }
      setBarVisible(shouldShowStickyBar());
    }

    function onVariant(ev) {
      var variant = ev.detail;
      if (!variant || !productInfo || !productInfo.contains(ev.target)) return;

      if (summaryEl) {
        var label = formatStickyVariantSummary(variant, summaryConfig);
        summaryEl.textContent = label;
        summaryEl.hidden = !label;
      }

      var url = variantImageUrl(variant, fallbackSrc);
      var thumbImg = bar.querySelector('.product-sticky-cart-bar__thumb-wrap .product-sticky-cart-bar__img');
      if (thumbImg && thumbImg.tagName === 'IMG' && !hasCustomPrintUpload(productInfo) && url) {
        thumbImg.src = url;
      }
      var mockupBase = bar.querySelector('.fab-cp-cart-mockup__base');
      if (mockupBase && url) mockupBase.src = url;

      requestAnimationFrame(function () {
        syncStickySubmit(stickySubmit, mainSubmit);
        updateVisibility();
      });
    }

    stickySubmit.addEventListener('click', function () {
      if (
        mainSubmit.disabled ||
        mainSubmit.getAttribute('aria-disabled') === 'true' ||
        mainSubmit.getAttribute('aria-busy') === 'true'
      ) {
        return;
      }
      mainSubmit.click();
    });

    var mo = new MutationObserver(function () {
      syncStickySubmit(stickySubmit, mainSubmit);
    });
    mo.observe(mainSubmit, {
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled', 'aria-busy', 'class'],
      subtree: true,
      childList: true,
      characterData: true,
    });

    productInfo.addEventListener('variant:change', onVariant);

    window.addEventListener('scroll', updateVisibility, { passive: true });
    window.addEventListener('resize', updateVisibility);
    if (mq.addEventListener) {
      mq.addEventListener('change', updateVisibility);
    } else if (mq.addListener) {
      mq.addListener(updateVisibility);
    }

    syncStickySubmit(stickySubmit, mainSubmit);
    updateVisibility();

    if (typeof IntersectionObserver !== 'undefined') {
      buyAreaObserver = new IntersectionObserver(
        function () {
          updateVisibility();
        },
        { root: null, threshold: [0, 1] }
      );
      buyAreaObserver.observe(visibilityTarget);
    }

    if (typeof ResizeObserver !== 'undefined') {
      resizeObs = new ResizeObserver(function () {
        updateVisibility();
      });
      resizeObs.observe(visibilityTarget);
      resizeObs.observe(bar);
    }
  }

  document.querySelectorAll('[data-product-sticky-cart-bar]').forEach(init);
})();

if (!customElements.get('pickup-availability')) {
  customElements.define(
    'pickup-availability',
    class PickupAvailability extends HTMLElement {
      constructor() {
        super();

        if (!this.hasAttribute('available')) return;

        this.errorHtml = this.querySelector('template').content.firstElementChild.cloneNode(true);
        this.onClickRefreshList = this.onClickRefreshList.bind(this);
        this.fetchAvailability(this.dataset.variantId);
      }

      fetchAvailability(variantId) {
        if (!variantId) return;

        let rootUrl = this.dataset.rootUrl;
        if (!rootUrl.endsWith('/')) {
          rootUrl = rootUrl + '/';
        }
        const variantSectionUrl = `${rootUrl}variants/${variantId}/?section_id=pickup-availability`;

        fetch(variantSectionUrl)
          .then((response) => response.text())
          .then((text) => {
            const sectionInnerHTML = new DOMParser()
              .parseFromString(text, 'text/html')
              .querySelector('.shopify-section');
            this.renderPreview(sectionInnerHTML);
          })
          .catch((e) => {
            const button = this.querySelector('button');
            if (button) button.removeEventListener('click', this.onClickRefreshList);
            this.renderError();
          });
      }

      onClickRefreshList() {
        this.fetchAvailability(this.dataset.variantId);
      }

      update(variant) {
        if (variant?.available) {
          this.fetchAvailability(variant.id);
        } else {
          this.removeAttribute('available');
          this.innerHTML = '';
        }
      }

      renderError() {
        this.innerHTML = '';
        this.appendChild(this.errorHtml);

        this.querySelector('button').addEventListener('click', this.onClickRefreshList);
      }

      renderPreview(sectionInnerHTML) {
        const drawer = document.querySelector('pickup-availability-drawer');
        if (drawer) drawer.remove();
        if (!sectionInnerHTML.querySelector('pickup-availability-preview')) {
          this.innerHTML = '';
          this.removeAttribute('available');
          return;
        }

        this.innerHTML = sectionInnerHTML.querySelector('pickup-availability-preview').outerHTML;
        this.setAttribute('available', '');

        document.body.appendChild(sectionInnerHTML.querySelector('pickup-availability-drawer'));
        const colorClassesToApply = this.dataset.productPageColorScheme.split(' ');
        colorClassesToApply.forEach((colorClass) => {
          document.querySelector('pickup-availability-drawer').classList.add(colorClass);
        });

        const button = this.querySelector('button');
        if (button)
          button.addEventListener('click', (evt) => {
            document.querySelector('pickup-availability-drawer').show(evt.target);
          });
      }
    }
  );
}

if (!customElements.get('pickup-availability-drawer')) {
  customElements.define(
    'pickup-availability-drawer',
    class PickupAvailabilityDrawer extends HTMLElement {
      constructor() {
        super();

        this.onBodyClick = this.handleBodyClick.bind(this);

        this.querySelector('button').addEventListener('click', () => {
          this.hide();
        });

        this.addEventListener('keyup', (event) => {
          if (event.code.toUpperCase() === 'ESCAPE') this.hide();
        });
      }

      handleBodyClick(evt) {
        const target = evt.target;
        if (
          target != this &&
          !target.closest('pickup-availability-drawer') &&
          target.id != 'ShowPickupAvailabilityDrawer'
        ) {
          this.hide();
        }
      }

      hide() {
        this.removeAttribute('open');
        document.body.removeEventListener('click', this.onBodyClick);
        document.body.classList.remove('overflow-hidden');
        removeTrapFocus(this.focusElement);
      }

      show(focusElement) {
        this.focusElement = focusElement;
        this.setAttribute('open', '');
        document.body.addEventListener('click', this.onBodyClick);
        document.body.classList.add('overflow-hidden');
        trapFocus(this);
      }
    }
  );
}
